import { useState, useEffect, useCallback } from 'react'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Badge } from '@/components/ui/badge'
import { Download } from 'lucide-react'
import { ScaryLoader } from './ScaryLoader'
import { BreachTimeline } from './BreachTimeline'
import { BreachAnalytics } from './BreachAnalytics'
import { D3LinkAnalysisGraph } from './D3LinkAnalysisGraph'
import { motion, AnimatePresence } from 'framer-motion'
import { apiClient, Graph } from '@/lib/api'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import DOMPurify from 'dompurify'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { CallerIdData } from '@/pages/BreachINT'
import { AIInvestigationBrief } from './breach/AIInvestigationBrief'
import { IntelligenceBrief, type IntelligenceBriefData } from './breach/IntelligenceBrief'
import { IdentityProfileCard } from './breach/IdentityProfileCard'
import { SourceIntelPanel } from './breach/SourceIntelPanel'
import { SelectionToolbar } from './breach/SelectionToolbar'
import { EnrichmentActions } from './breach/EnrichmentActions'
import { AIEnrichmentPanel } from './breach/AIEnrichmentPanel'
import { BTRAPanel } from './breach/BTRAPanel'
import { BTRAInvestigationPanel } from './breach/BTRAInvestigationPanel'
import { CorrelationEnginePanel } from './breach/CorrelationEnginePanel'
import { BreachPivotEnrichment } from './breach/BreachPivotEnrichment'

interface ModernOSINTDashboardProps {
  graph: any
  query: string
  onRerunSearch?: (query: string, forceRefresh: boolean) => Promise<void>
  /** Caller ID & Facebook data when query is a phone number (phone-to-social mapping) */
  callerIdData?: CallerIdData | null
}

interface SycekEnrichmentData {
  enrichment_brand: string
  enrichment_provider: string
  enriched_email: string
  name: string
  linkedin_url?: string
  links?: {
    linkedin?: string
    twitter?: string
    facebook?: string
    instagram?: string
    github?: string
    [key: string]: any
  }
  location?: string
  current_title?: string
  current_employer?: string
  current_employer_domain?: string
  current_employer_website?: string
  current_employer_linkedin_url?: string
  job_history?: any[]
  education?: any[]
  skills?: string[]
  birth_year?: number
  city?: string
  region?: string
  country?: string
  country_code?: string
  region_latitude?: number
  region_longitude?: number
  emails?: any[]
  phones?: any[]
  recommended_email?: string
  recommended_personal_email?: string
  recommended_professional_email?: string
  current_work_email?: string
  current_personal_email?: string
}

interface GmailOSINTData {
  email: string
  profile_picture?: string
  cover_picture?: string
  gaia_id?: string
  last_profile_edit?: string
  user_types?: string[]
  entity_type?: string
  customer_id?: string
  enterprise_user?: boolean
  activated_services?: string[]
  maps_profile?: string
  play_games?: string
  calendar?: string
  errors?: string[]
}

interface PIIData {
  personalInfo: {
    fullName?: string
    firstName?: string
    lastName?: string
    nickName?: string
    dob?: string
    gender?: string
    age?: string
    nationality?: string
  }
  contactDetails: {
    email?: string
    phone?: string
    alternativeEmails?: string
    phoneNumbers?: string
  }
  socialMedia: {
    linkedin?: string
    facebook?: string
    twitter?: string
    instagram?: string
    vk?: string
    socialProfiles?: string
  }
  governmentIDs: {
    ssn?: string
    tin?: string
    passport?: string
    driverLicense?: string
    nationalID?: string
  }
  photos: Array<{
    url: string
    source: string
    type: string
  }>
  addresses: {
    street?: string
    city?: string
    state?: string
    country?: string
    postCode?: string
    latitude?: string
    longitude?: string
    fullAddress?: string
  }
  financial: {
    password?: string
    paymentMethod?: string
    balance?: string
    creditCard?: string
    bankAccount?: string
    financialData?: string
  }
  breachSources: Array<{
    source: string
    date: string
    records: string
    dataTypes: string
    email?: string
    password?: string
    fullName?: string
    phone?: string
    address?: string
    city?: string
    birthDate?: string
    ipAddress?: string
    gender?: string
    nationality?: string
    jobTitle?: string
    company?: string
    website?: string
    socialMedia?: string
    avatar?: string
    allRecords?: any[]
    breachDescription?: string
    numOfResults?: number
    matchRelevance?: number | null
  }>
  webResults: Array<{
    id: string
    title: string
    url: string
    snippet?: string
    extracted_snippets?: string[]
    props?: any
  }>
  gmailOSINT?: GmailOSINTData
  sycekEnrichment?: SycekEnrichmentData[]
  stealerLogs?: Array<{
    stealer_family: string
    date_compromised: string
    computer_name: string
    operating_system: string
    malware_path: string
    ip: string
    antiviruses: string[]
    total_corporate_services: number
    total_user_services: number
    top_passwords: string[]
    top_logins: string[]
    source: string
    infection_count?: number
    users_affected?: number
    employees_affected?: number
    domain?: string
  }>
  mapLocations?: Array<{
    lat: number
    lng: number
    address?: string
    city?: string
    country?: string
    source: string
    label: string
  }>
  leakixData?: {
    domain: string
    services_count: number
    leaks_count: number
    services: any[]
    leaks: any[]
  } | null
  occrpAlephData?: {
    total_results: number
    results: Array<{
      id: string
      schema: string
      name: string
      collection_label: string
      collection_id: string
      country?: string[]
      address?: string[]
      highlight?: string[]
      ui_link?: string
      api_link?: string
      score?: number
      properties?: any
    }>
  } | null
  sanctionsData?: {
    is_sanctioned: boolean
    highest_score: number
    total_hits: number
    hits: Array<{
      entity_id?: string
      caption: string
      score: number
      datasets?: string[]
      match_type?: string
    }>
    list_coverage?: string[]
  } | null
}

// High-value data source keywords for alerts
const HIGH_VALUE_SOURCES = [
  { keywords: ['wikileaks', 'wiki leaks', 'state department', 'wikileaks state department'], label: 'WikiLeaks', severity: 'critical', icon: '🚨' },
  { keywords: ['panama papers', 'panama', 'offshore', 'icij panama', 'icij panama papers'], label: 'Panama Papers', severity: 'critical', icon: '🚨' },
  { keywords: ['fbi', 'federal bureau', 'federal bureau of investigation'], label: 'FBI Records', severity: 'critical', icon: '🚨' },
  { keywords: ['paradise papers', 'paradise', 'icij paradise'], label: 'Paradise Papers', severity: 'critical', icon: '🚨' },
  { keywords: ['pandora papers', 'pandora', 'icij pandora'], label: 'Pandora Papers', severity: 'critical', icon: '🚨' },
  { keywords: ['offshore leaks', 'offshore', 'icij offshore'], label: 'Offshore Leaks', severity: 'high', icon: '⚠️' },
  { keywords: ['sanctions', 'sanctioned', 'sanctions list'], label: 'Sanctions Lists', severity: 'high', icon: '⚠️' },
  { keywords: ['court', 'judicial', 'lawsuit', 'court archive'], label: 'Court Archives', severity: 'high', icon: '⚠️' },
  { keywords: ['procurement', 'government contract', 'procurement record'], label: 'Procurement Records', severity: 'medium', icon: '📋' },
  { keywords: ['financial', 'bank', 'transaction', 'financial record'], label: 'Financial Records', severity: 'high', icon: '💰' },
  { keywords: ['sec', 'securities and exchange', 'corpwatch'], label: 'SEC Filings', severity: 'high', icon: '📄' },
  { keywords: ['un', 'ohchr', 'human rights', 'united nations'], label: 'UN Reports', severity: 'high', icon: '🌐' }
]

export function ModernOSINTDashboard({ graph, query, onRerunSearch, callerIdData }: ModernOSINTDashboardProps) {
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [highValueAlerts, setHighValueAlerts] = useState<Array<{source: string, severity: string, icon: string, collection: string}>>([])
  const [showAlerts, setShowAlerts] = useState(true)
  const [profileData, setProfileData] = useState<PIIData | null>(null)
  const [showRecentSearches, setShowRecentSearches] = useState(false)
  const [selectedNode, setSelectedNode] = useState<any>(null)
  const [aiInsights, setAiInsights] = useState<any>(null)
  const [loadingInsights, setLoadingInsights] = useState(false)
  const [exportingPDF, setExportingPDF] = useState(false)
  const [rerunningSearch, setRerunningSearch] = useState(false)
  
  // New state for data selection and combining
  const [selectedItems, setSelectedItems] = useState<{
    breaches: Set<string>
    stealerLogs: Set<string>
    leakixServices: Set<string>
    leakixLeaks: Set<string>
    webResults: Set<string>
  }>({
    breaches: new Set(),
    stealerLogs: new Set(),
    leakixServices: new Set(),
    leakixLeaks: new Set(),
    webResults: new Set()
  })
  const [combinedSearches, setCombinedSearches] = useState<any[]>([])
  const [showCombineDialog, setShowCombineDialog] = useState(false)
  const [showSelectionMode, setShowSelectionMode] = useState(false)
  // When on, every selectable list (breaches, web results, stealer logs,
  // LeakIX services/leaks) renders ONLY the checked items instead of the
  // full set — this is what actually lets an analyst remove unwanted data
  // from view, not just tag it for later. Off by default so selection mode
  // still starts by showing everything to pick from.
  const [hideUnselected, setHideUnselected] = useState(false)
  const [mergedGraph, setMergedGraph] = useState<Graph | null>(null)
  const [showRecordSelectionModal, setShowRecordSelectionModal] = useState(false)
  const [selectedRecordIds, setSelectedRecordIds] = useState<Set<string>>(new Set())

  // Breach pivot enrichment state (INVESTIGATE tab)
  const [enrichResult, setEnrichResult] = useState<any | null>(null)
  const [enrichLoading, setEnrichLoading] = useState(false)
  const [enrichError, setEnrichError] = useState<string | null>(null)
  const [enrichTargets, setEnrichTargets] = useState<{ phones: string[]; emails: string[]; usernames: string[] }>({ phones: [], emails: [], usernames: [] })
  const [allRecords, setAllRecords] = useState<Array<{id: string, breachSource: string, breachIndex: number, recordIndex: number, record: any, keyInfo: any}>>([])
  // isDesktop tracks live via matchMedia (not just a mount-time innerWidth
  // check) — previously these two only ever read window.innerWidth once in
  // the useState initializer, so a window narrower than 1024px on first load
  // froze both sidebars collapsed to a 32px button strip forever, with no
  // way to recover even after resizing wider. The sync effect below re-snaps
  // both sidebars to the size-class default whenever that boundary is
  // crossed, while still leaving manual toggles (the buttons below) alone
  // within the same size class.
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(isDesktop)
  const [rightSidebarOpen, setRightSidebarOpen] = useState(isDesktop)

  useEffect(() => {
    setLeftSidebarOpen(isDesktop)
    setRightSidebarOpen(isDesktop)
  }, [isDesktop])

  // Fetch AI insights manually (not auto-fetch)
  const fetchAIInsights = async (useSelectedItems: boolean = false) => {
    if (!profileData) return
    
    setLoadingInsights(true)
    try {
      let payload: any = {
        query: query,
        breachSources: profileData.breachSources,
        personalInfo: profileData.personalInfo,
        contactDetails: profileData.contactDetails,
        addresses: profileData.addresses,
        webResults: profileData.webResults || [],
        stealerLogs: profileData.stealerLogs || [],
        gmailOSINT: profileData.gmailOSINT || null,
        leakixData: profileData.leakixData || null
      }
      
      // If using selected items, include selection
      if (useSelectedItems) {
        payload.selectedItems = {
          breaches: Array.from(selectedItems.breaches),
          stealerLogs: Array.from(selectedItems.stealerLogs),
          leakixServices: Array.from(selectedItems.leakixServices),
          leakixLeaks: Array.from(selectedItems.leakixLeaks),
          webResults: Array.from(selectedItems.webResults)
        }
      }
      
      const insights = await apiClient.getBreachAIInsights(payload)
      setAiInsights(insights)
    } catch (error: any) {
      console.error('Failed to fetch AI insights:', error)
      setAiInsights({
        insights: `**OSINT Intelligence Analysis for ${query}**\n\n**Error:** AI analysis temporarily unavailable. ${error?.message || 'Failed to fetch insights'}\n\n**Note:** Using fallback analysis.`,
        risk_level: 'UNKNOWN',
        breach_count: profileData.breachSources.length,
        model: 'error',
        error: error?.message || 'Unknown error'
      })
    } finally {
      setLoadingInsights(false)
    }
  }
  
  // Extract all individual records from breach sources for selection modal
  useEffect(() => {
    if (profileData && profileData.breachSources.length > 0) {
      const records: Array<{id: string, breachSource: string, breachIndex: number, recordIndex: number, record: any, keyInfo: any}> = []
      
      profileData.breachSources.forEach((breach: any, breachIndex: number) => {
        if (breach.allRecords && Array.isArray(breach.allRecords) && breach.allRecords.length > 0) {
          breach.allRecords.forEach((record: any, recordIndex: number) => {
            // Extract key identifying information
            const keyInfo = {
              email: record.Email || record.email || record.EmailAddress || '',
              fullName: record.FullName || record.fullName || record.Name || record.name || '',
              dob: record.DOB || record.dob || record.BirthDate || record.birthDate || record.Bday || record.bday || '',
              phone: record.Phone || record.phone || record.PhoneNumber || record.phoneNumber || '',
              address: record.Address || record.address || '',
              city: record.City || record.city || '',
              state: record.State || record.state || '',
              country: record.Country || record.country || '',
              ip: record.IP || record.ip || record.IPAddress || record.ipAddress || ''
            }
            
            const recordId = `breach_${breachIndex}_record_${recordIndex}`
            records.push({
              id: recordId,
              breachSource: breach.source || 'Unknown',
              breachIndex,
              recordIndex,
              record,
              keyInfo
            })
          })
        } else {
          // If no allRecords, create a single record from breach data
          const keyInfo = {
            email: breach.email || query || '',
            fullName: breach.name || '',
            dob: breach.dob || '',
            phone: breach.phone || '',
            address: breach.address || '',
            city: breach.city || '',
            state: breach.state || '',
            country: breach.country || '',
            ip: ''
          }
          
          const recordId = `breach_${breachIndex}_record_0`
          records.push({
            id: recordId,
            breachSource: breach.source || 'Unknown',
            breachIndex,
            recordIndex: 0,
            record: breach,
            keyInfo
          })
        }
      })
      
      setAllRecords(records)
      
      // Show modal if there are multiple records and they appear to be different people
      if (records.length > 1) {
        // Check if records have different identifying info (different people)
        const uniqueEmails = new Set(records.map(r => r.keyInfo.email).filter(e => e))
        const uniqueNames = new Set(records.map(r => r.keyInfo.fullName).filter(n => n && n.length > 3))
        const uniqueDOBs = new Set(records.map(r => r.keyInfo.dob).filter(d => d))
        
        // Show modal if:
        // 1. Multiple unique emails found, OR
        // 2. Multiple unique names found (and they're different), OR
        // 3. Multiple unique DOBs found, OR
        // 4. Combination of different identifiers suggests different people
        const hasMultiplePeople = 
          uniqueEmails.size > 1 || 
          uniqueNames.size > 1 || 
          uniqueDOBs.size > 1 ||
          (records.length > 3 && (uniqueEmails.size > 0 || uniqueNames.size > 0))
        
        if (hasMultiplePeople) {
          setShowRecordSelectionModal(true)
        }
      }
    }
  }, [profileData, query])
  
  // Auto-fetch insights only if no selection mode (backward compatibility)
  useEffect(() => {
    if (!showSelectionMode && profileData && profileData.breachSources.length > 0 && !showRecordSelectionModal) {
      fetchAIInsights(false)
    }
  }, [profileData, query, showSelectionMode, showRecordSelectionModal])


  // Breach pivot enrichment handler — extracts discovered identifiers and pivots on each
  const handleRunEnrichment = useCallback(async () => {
    // Extract unique phones, alt-emails, usernames from breach records
    const phones: string[]    = []
    const altEmails: string[] = []
    const usernames: string[] = []
    const queryLower = query.toLowerCase().trim()

    if (profileData) {
      const SKIP = new Set(['source', 'date', 'records', 'datatypes', 'numofresults'])
      profileData.breachSources.forEach((breach: any) => {
        const records: any[] = breach.allRecords?.length ? breach.allRecords : [breach]
        records.forEach((rec: any) => {
          Object.entries(rec).forEach(([k, v]) => {
            if (!v) return
            const vs  = String(v).trim()
            const kl  = k.toLowerCase().replace(/[^a-z]/g, '')
            if (SKIP.has(kl) || vs.length < 3) return

            if (kl.includes('phone') || kl.includes('mobile') || kl === 'tel') {
              const d = vs.replace(/\D/g, '')
              if (d.length >= 7 && !phones.includes(d) && d !== queryLower.replace(/\D/g, ''))
                phones.push(d)
            } else if ((kl.includes('email') || kl.includes('mail')) && vs.includes('@')) {
              const el = vs.toLowerCase()
              if (el !== queryLower && !altEmails.includes(el)) altEmails.push(el)
            } else if (kl.includes('username') || kl.includes('nickname') || kl === 'login' || kl === 'handle') {
              if (vs.toLowerCase() !== queryLower && !usernames.includes(vs)) usernames.push(vs)
            }
          })
        })
      })
    }

    const targets = {
      phones:    phones.slice(0, 5),
      emails:    altEmails.slice(0, 5),
      usernames: usernames.slice(0, 3),
    }
    setEnrichTargets(targets)
    setEnrichLoading(true)
    setEnrichError(null)
    try {
      const result = await apiClient.pivotEnrich({ seed: query.trim(), ...targets })
      setEnrichResult(result)
    } catch (e) {
      setEnrichError(e instanceof Error ? e.message : 'Pivot enrichment failed')
    } finally {
      setEnrichLoading(false)
    }
  }, [query, profileData])

  // Export PDF function
  const handleExportPDF = async () => {
    setExportingPDF(true)
    try {
      // The network graph used to be captured here as a browser screenshot
      // (html2canvas / dom-to-image-more of the live D3 force graph, forcing
      // a tab switch + ~2s of waits) — that's gone. The backend now renders
      // the graph itself as a static SVG straight from this same node/edge
      // data (services/breachint/report_generator.py's network_graph_svg),
      // which is both instant and reliable — no more blank-graph exports
      // from a percentage-sized <svg>, a transform-attribute zoom, or a
      // race against the force simulation settling.
      const effectiveGraph = mergedGraph || graph

      // Filter data based on selected items if in selection mode.
      // Previously a category with zero explicitly-checked items fell back to
      // "include everything" — meaning unchecking every item in a category
      // (the natural way to exclude it) had no effect, and there was no way
      // to leave a whole category out of the report. Now: selection mode off
      // = export everything (unchanged); selection mode on = export exactly
      // what's checked, including an empty category if nothing there is checked.
      const getFilteredBreachSources = () => {
        if (!profileData?.breachSources) return []
        if (!showSelectionMode) return profileData.breachSources
        return profileData.breachSources.filter((_: any, index: number) =>
          selectedItems.breaches.has(String(index))
        )
      }

      const getFilteredStealerLogs = () => {
        if (!profileData?.stealerLogs) return []
        if (!showSelectionMode) return profileData.stealerLogs
        return profileData.stealerLogs.filter((_: any, index: number) =>
          selectedItems.stealerLogs.has(String(index))
        )
      }

      const getFilteredWebResults = () => {
        if (!profileData?.webResults) return []
        if (!showSelectionMode) return profileData.webResults
        return profileData.webResults.filter((_: any, index: number) =>
          selectedItems.webResults.has(String(index))
        )
      }

      const getFilteredLeakixData = () => {
        if (!profileData?.leakixData) return null
        if (!showSelectionMode) return profileData.leakixData

        const filteredData = { ...profileData.leakixData }
        filteredData.services = (filteredData.services || []).filter((_: any, index: number) =>
          selectedItems.leakixServices.has(String(index))
        )
        filteredData.services_count = filteredData.services.length
        filteredData.leaks = (filteredData.leaks || []).filter((_: any, index: number) =>
          selectedItems.leakixLeaks.has(String(index))
        )
        filteredData.leaks_count = filteredData.leaks.length
        return filteredData
      }

      const filteredBreachSources = getFilteredBreachSources()
      const filteredStealerLogs = getFilteredStealerLogs()
      const filteredWebResults = getFilteredWebResults()
      const filteredLeakixData = getFilteredLeakixData()

      // Selection mode on but nothing checked anywhere = an accidental
      // near-empty report waiting to happen — confirm rather than silently export it.
      if (showSelectionMode) {
        const nothingSelected =
          filteredBreachSources.length === 0 &&
          filteredStealerLogs.length === 0 &&
          filteredWebResults.length === 0 &&
          (filteredLeakixData?.services?.length ?? 0) === 0 &&
          (filteredLeakixData?.leaks?.length ?? 0) === 0
        if (nothingSelected) {
          const proceed = window.confirm(
            'You have "Select items for report" turned on but nothing is checked, so this report would be empty.\n\n' +
            'Click Cancel to go back and select what to include, or OK to export an empty report anyway.'
          )
          if (!proceed) {
            return // `finally` below still resets exportingPDF
          }
        }
      }
      
      // Extract timeline and analytics data for PDF (using filtered data)
      const extractTimelineData = () => {
        const events: any[] = []
        filteredBreachSources.forEach((breach: any) => {
          const breachName = breach.source || 'Unknown Breach'
          const breachDate = breach.date || breach.breach_date || 'Unknown Date'
          const recordsAffected = parseInt(breach.records) || breach.numOfResults || breach.allRecords?.length || 1
          const dataTypes = breach.dataTypes ? breach.dataTypes.split(', ') : ['Email, Password, Personal Data']
          
          let severity: string = 'low'
          if (recordsAffected > 10000000) severity = 'critical'
          else if (recordsAffected > 1000000) severity = 'high'
          else if (recordsAffected > 100000) severity = 'medium'
          
          events.push({
            date: breachDate,
            breachName: breachName,
            description: breach.description || breach.InfoLeak || `Data breach affecting ${recordsAffected.toLocaleString()} records`,
            recordsAffected: recordsAffected,
            dataTypes: dataTypes,
            severity: severity
          })
        })
        return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      }
      
      const extractAnalyticsData = () => {
        if (!filteredBreachSources || filteredBreachSources.length === 0) return null
        
        const totalBreaches = filteredBreachSources.length
        const totalRecords = filteredBreachSources.reduce((sum: number, b: any) => 
          sum + (parseInt(b.records) || b.numOfResults || b.allRecords?.length || 1), 0)
        
        const attributeStats: Record<string, number> = {}
        filteredBreachSources.forEach((breach: any) => {
          if (breach.allRecords) {
            breach.allRecords.forEach((record: any) => {
              Object.keys(record).forEach(key => {
                if (record[key] && key !== 'source' && key !== 'date') {
                  attributeStats[key] = (attributeStats[key] || 0) + 1
                }
              })
            })
          }
        })
        
        return {
          totalBreaches,
          totalRecords,
          attributeStats,
          riskLevel: aiInsights?.risk_level || 'UNKNOWN'
        }
      }
      
      // Prepare comprehensive report data. IMPORTANT: profileData here must
      // stay the RAW, unfiltered arrays (not filteredBreachSources etc.) —
      // the backend's own "server-side selection safety net" in
      // /breach/export-pdf re-filters by the ORIGINAL array indices carried
      // in selectedItems below (mirroring /breach/ai-insights's contract,
      // see fetchAIInsights above, which sends the same shape). Sending the
      // already-filtered arrays here made the backend re-enumerate an
      // already-shortened array against original indices — collapsing
      // almost any real partial selection to an empty export. The frontend
      // no longer needs to pre-filter for the export itself since the
      // backend is the single source of truth for what actually gets
      // written into the PDF; filteredBreachSources etc. are still used
      // above for the on-screen preview/analytics only.
      const reportData = {
        query: query,
        profileData: {
          ...profileData,
          photos: profileData?.photos || [],
          personalInfo: profileData?.personalInfo || {},
          contactDetails: profileData?.contactDetails || {},
          breachSources: profileData?.breachSources || [],
          stealerLogs: profileData?.stealerLogs || [],
          webResults: profileData?.webResults || [],
          leakixData: profileData?.leakixData || null,
          occrpAlephData: profileData?.occrpAlephData || null,
          sanctionsData: profileData?.sanctionsData || null,
          sycekEnrichment: profileData?.sycekEnrichment || [],
          highValueAlerts: highValueAlerts
        },
        selectedItems: showSelectionMode ? {
          breaches: Array.from(selectedItems.breaches),
          stealerLogs: Array.from(selectedItems.stealerLogs),
          leakixServices: Array.from(selectedItems.leakixServices),
          leakixLeaks: Array.from(selectedItems.leakixLeaks),
          webResults: Array.from(selectedItems.webResults)
        } : undefined,
        aiInsights: aiInsights || null,
        graphData: effectiveGraph || null,
        timelineData: extractTimelineData(),
        analyticsData: extractAnalyticsData(),
        timestamp: new Date().toISOString(),
        breachCount: filteredBreachSources.length,
        riskLevel: aiInsights?.risk_level || 'UNKNOWN'
      }
      
      console.log('Exporting PDF with comprehensive data:', {
        query: reportData.query,
        breachCount: reportData.breachCount,
        photoCount: reportData.profileData.photos?.length || 0,
        hasGraphData: !!reportData.graphData,
        graphNodeCount: reportData.graphData?.nodes?.length || 0,
        breachSourcesSample: reportData.profileData.breachSources?.slice(0, 2),
        hasAIInsights: !!reportData.aiInsights,
        aiInsightsKeys: reportData.aiInsights ? Object.keys(reportData.aiInsights) : [],
        aiInsightsPreview: reportData.aiInsights?.insights?.substring(0, 100),
      })
      
      // Call backend to generate PDF
      const blob = await apiClient.exportReportPDF(reportData)
      
      if (!blob || blob.size === 0) {
        throw new Error('Received empty PDF blob from server')
      }
      
      // Download PDF - use both direct download and fallback for browser compatibility
      const url = window.URL.createObjectURL(blob)
      const filename = `sycek-osint-report-${query.replace(/[@.]/g, '-')}-${Date.now()}.pdf`
      try {
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch (downloadErr) {
        // Fallback: open in new tab if programmatic download blocked (e.g. after async ops)
        console.warn('Direct download failed, opening in new tab:', downloadErr)
        window.open(url, '_blank', 'noopener,noreferrer')
      }
      // Revoke after a delay to allow download/tab to use the blob
      setTimeout(() => window.URL.revokeObjectURL(url), 5000)
      
      console.log('PDF exported successfully')
    } catch (error: any) {
      console.error('Failed to export PDF:', error)
      const errorMessage = error?.message || 'Unknown error occurred'
      alert(`Failed to export PDF: ${errorMessage}\n\nPlease ensure:\n1. You have breach data loaded\n2. ReportLab is installed on the server\n3. Your browser allows file downloads`)
    } finally {
      setExportingPDF(false)
    }
  }

  // Generate interactive graph data from breach attributes (unused - replaced with D3LinkAnalysisGraph)
  // @ts-ignore - unused function
  const _generateNetworkGraph = (_profileData: PIIData) => {
    const nodes: any[] = []
    const links: any[] = []
    
    // Color palette for diverse node colors
    const nodeColors = [
      '#ff6b6b', // Red
      '#4ecdc4', // Teal
      '#45b7d1', // Blue
      '#ffa07a', // Light Salmon
      '#98d8c8', // Mint
      '#f7dc6f', // Yellow
      '#bb8fce', // Purple
      '#85c1e2', // Sky Blue
      '#f8b739', // Orange
      '#52be80', // Green
      '#ec7063', // Coral
      '#5dade2', // Light Blue
      '#f1948a', // Pink
      '#7dcea0', // Light Green
      '#f39c12', // Dark Orange
      '#e74c3c', // Bright Red
    ]
    
    // Central target node - bright white/cyan
    nodes.push({
      id: 'target',
      name: query,
      type: 'target',
      val: 10,
      color: '#ffffff',
      group: 0
    })
    
    // Breach source nodes - varied colors
    _profileData.breachSources?.forEach((breach, index) => {
      const nodeId = `breach-${index}`
      const colorIndex = (index % nodeColors.length)
      
      nodes.push({
        id: nodeId,
        name: breach.source,
        type: 'breach',
        val: 6,
        color: nodeColors[colorIndex],
        group: 1 + (index % 4), // Vary groups for color diversity
        breach: breach
      })
      
      // Link to target
      links.push({
        source: 'target',
        target: nodeId,
        value: 2,
        color: nodeColors[colorIndex]
      })
    })
    
    // Attribute nodes (PII data) - vibrant colors - include ALL attributes found
    const allAttributes = new Set<string>()
    
    // Collect all attributes from breach sources
    _profileData.breachSources?.forEach((breach) => {
      Object.keys(breach).forEach(key => {
        if (key !== 'source' && key !== 'date' && key !== 'records' && key !== 'dataTypes' && breach[key as keyof typeof breach]) {
          allAttributes.add(key)
        }
      })
    })
    
    // Also check personalInfo, contactDetails, etc.
    if (_profileData.personalInfo?.fullName) allAttributes.add('fullName')
    if (_profileData.contactDetails?.email) allAttributes.add('email')
    if (_profileData.contactDetails?.phone) allAttributes.add('phone')
    if (_profileData.addresses?.city || _profileData.addresses?.city) allAttributes.add('address')
    if (_profileData.financial?.password) allAttributes.add('password')
    
    const attributeColors: Record<string, string> = {
      'email': '#5dade2',
      'password': '#e74c3c',
      'fullName': '#52be80',
      'phone': '#bb8fce',
      'address': '#f39c12',
      'city': '#85c1e2',
      'birthDate': '#ec7063',
      'ipAddress': '#7dcea0',
      'phoneNumber': '#bb8fce',
      'firstName': '#52be80',
      'lastName': '#52be80'
    }
    
    Array.from(allAttributes).forEach((attrKey, index) => {
      const nodeId = `attr-${attrKey}`
      const color = attributeColors[attrKey] || `#${Math.floor(Math.random()*16777215).toString(16)}`
      
      nodes.push({
        id: nodeId,
        name: attrKey,
        type: 'attribute',
        val: 3,
        color: color,
        group: 10 + (index % 10),
        attribute: attrKey
      })
      
      // Link to breaches that have this attribute
      _profileData.breachSources?.forEach((breach, breachIndex) => {
        const value = breach[attrKey as keyof typeof breach]
        if (value && value !== '' && value !== 'Unknown' && value !== 'N/A') {
          links.push({
            source: `breach-${breachIndex}`,
            target: nodeId,
            value: 1,
            color: color
          })
        }
      })
    })
    
    return { nodes, links }
  }

  // Get recent searches from localStorage
  const getRecentSearches = () => {
    try {
      const recent = localStorage.getItem('sycek_recent_searches')
      return recent ? JSON.parse(recent) : []
    } catch {
      return []
    }
  }

  // Save current search to recent searches
  const saveToRecentSearches = (query: string, results: any) => {
    try {
      const recent = getRecentSearches()
      const newSearch = {
        query,
        timestamp: new Date().toISOString(),
        resultsCount: results?.NumOfResults || 0,
        id: Date.now()
      }
      
      // Remove if already exists
      const filtered = recent.filter((search: any) => search.query !== query)
      // Add to beginning and limit to 10
      const updated = [newSearch, ...filtered].slice(0, 10)
      
      localStorage.setItem('sycek_recent_searches', JSON.stringify(updated))
    } catch (error) {
      console.error('Failed to save recent search:', error)
    }
  }

  // Navigate back to search page
  const goBackToSearch = () => {
    // This will be handled by the parent component
    window.history.back()
  }

  // Load a previous search
  const loadPreviousSearch = (searchQuery: string) => {
    // This will trigger a new search with the previous query
    window.location.href = `/app/breach?query=${encodeURIComponent(searchQuery)}`
  }

  // Merge multiple graphs together
  const mergeGraphs = (baseGraph: Graph, additionalGraphs: Graph[]): Graph => {
    const mergedGraph: Graph = {
      nodes: [...(baseGraph.nodes || [])],
      edges: [...(baseGraph.edges || [])],
      facets: { ...(baseGraph.facets || {}) },
      metadata: { ...(baseGraph.metadata || {}) }
    }

    // Track node IDs to avoid duplicates
    const nodeIds = new Set(baseGraph.nodes?.map(n => n.id) || [])
    const edgeIds = new Set(baseGraph.edges?.map(e => e.id || `${e.source}-${e.target}-${e.relation}`) || [])

    // Merge nodes from additional graphs
    additionalGraphs.forEach((additionalGraph) => {
      if (additionalGraph.nodes) {
        additionalGraph.nodes.forEach((node) => {
          if (!nodeIds.has(node.id)) {
            mergedGraph.nodes.push(node)
            nodeIds.add(node.id)
          } else {
            // Merge properties if node already exists
            const existingNode = mergedGraph.nodes.find(n => n.id === node.id)
            if (existingNode) {
              existingNode.props = { ...existingNode.props, ...node.props }
              // Merge other properties
              if (node.confidence && (!existingNode.confidence || node.confidence > existingNode.confidence)) {
                existingNode.confidence = node.confidence
              }
            }
          }
        })
      }

      // Merge edges
      if (additionalGraph.edges) {
        additionalGraph.edges.forEach((edge) => {
          const edgeId = edge.id || `${edge.source}-${edge.target}-${edge.relation}`
          if (!edgeIds.has(edgeId)) {
            mergedGraph.edges.push(edge)
            edgeIds.add(edgeId)
          }
        })
      }

      // Merge facets
      if (additionalGraph.facets) {
        Object.keys(additionalGraph.facets).forEach((facetKey) => {
          if (!mergedGraph.facets[facetKey]) {
            mergedGraph.facets[facetKey] = {}
          }
          Object.keys(additionalGraph.facets[facetKey]).forEach((facetValue) => {
            mergedGraph.facets[facetKey][facetValue] = 
              (mergedGraph.facets[facetKey][facetValue] || 0) + 
              (additionalGraph.facets[facetKey][facetValue] || 0)
          })
        })
      }

      // Merge metadata - combine original intelligence results
      if (additionalGraph.metadata) {
        // Merge original_intelligence_result if it exists
        if (additionalGraph.metadata.original_intelligence_result) {
          const additionalList = additionalGraph.metadata.original_intelligence_result.List || {}
          if (mergedGraph.metadata.original_intelligence_result) {
            mergedGraph.metadata.original_intelligence_result.List = {
              ...mergedGraph.metadata.original_intelligence_result.List,
              ...additionalList
            }
            mergedGraph.metadata.original_intelligence_result.NumOfResults = 
              Object.keys(mergedGraph.metadata.original_intelligence_result.List).length
          } else {
            mergedGraph.metadata.original_intelligence_result = {
              ...additionalGraph.metadata.original_intelligence_result,
              List: { ...additionalList }
            }
          }
        }

        // Merge original_list if it exists
        if (additionalGraph.metadata.original_list) {
          const additionalList = additionalGraph.metadata.original_list
          if (mergedGraph.metadata.original_list) {
            mergedGraph.metadata.original_list = {
              ...mergedGraph.metadata.original_list,
              ...additionalList
            }
          } else {
            mergedGraph.metadata.original_list = { ...additionalList }
          }
          mergedGraph.metadata.original_num_results = Object.keys(mergedGraph.metadata.original_list).length
        }

        // Merge other metadata fields
        Object.keys(additionalGraph.metadata).forEach((key) => {
          if (key !== 'original_intelligence_result' && key !== 'original_list' && key !== 'original_num_results') {
            if (!mergedGraph.metadata[key]) {
              mergedGraph.metadata[key] = additionalGraph.metadata[key]
            }
          }
        })
      }
    })

    return mergedGraph
  }

  // Merge profile data from multiple searches
  const mergeProfileData = (baseProfile: PIIData, additionalProfiles: PIIData[]): PIIData => {
    const merged: PIIData = {
      personalInfo: { ...baseProfile.personalInfo },
      contactDetails: { ...baseProfile.contactDetails },
      socialMedia: { ...baseProfile.socialMedia },
      governmentIDs: { ...baseProfile.governmentIDs },
      photos: [...(baseProfile.photos || [])],
      addresses: { ...baseProfile.addresses },
      financial: { ...baseProfile.financial },
      breachSources: [...(baseProfile.breachSources || [])],
      webResults: [...(baseProfile.webResults || [])],
      stealerLogs: [...(baseProfile.stealerLogs || [])],
      mapLocations: [...(baseProfile.mapLocations || [])],
      leakixData: baseProfile.leakixData || null,
      occrpAlephData: baseProfile.occrpAlephData || null,
      sanctionsData: baseProfile.sanctionsData || null,
      gmailOSINT: baseProfile.gmailOSINT || undefined
    }

    // Track unique items to avoid duplicates
    const breachSourceKeys = new Set(baseProfile.breachSources?.map(b => `${b.source}-${b.date}`) || [])
    const webResultIds = new Set(baseProfile.webResults?.map(w => w.id) || [])
    const stealerLogKeys = new Set(baseProfile.stealerLogs?.map(s => `${s.stealer_family}-${s.date_compromised}-${s.computer_name}`) || [])
    const photoUrls = new Set(baseProfile.photos?.map(p => p.url) || [])

    additionalProfiles.forEach((additionalProfile) => {
      // Merge personal info
      const personalInfo = additionalProfile.personalInfo || {}
      if (personalInfo.fullName && !merged.personalInfo.fullName) merged.personalInfo.fullName = personalInfo.fullName
      if (personalInfo.firstName && !merged.personalInfo.firstName) merged.personalInfo.firstName = personalInfo.firstName
      if (personalInfo.lastName && !merged.personalInfo.lastName) merged.personalInfo.lastName = personalInfo.lastName
      if (personalInfo.nickName && !merged.personalInfo.nickName) merged.personalInfo.nickName = personalInfo.nickName
      if (personalInfo.dob && !merged.personalInfo.dob) merged.personalInfo.dob = personalInfo.dob
      if (personalInfo.gender && !merged.personalInfo.gender) merged.personalInfo.gender = personalInfo.gender
      if (personalInfo.age && !merged.personalInfo.age) merged.personalInfo.age = personalInfo.age
      if (personalInfo.nationality && !merged.personalInfo.nationality) merged.personalInfo.nationality = personalInfo.nationality

      // Merge contact details
      const contactDetails = additionalProfile.contactDetails || {}
      if (contactDetails.email && !merged.contactDetails.email) merged.contactDetails.email = contactDetails.email
      if (contactDetails.phone && !merged.contactDetails.phone) merged.contactDetails.phone = contactDetails.phone
      if (contactDetails.alternativeEmails) {
        const existing = merged.contactDetails.alternativeEmails ? merged.contactDetails.alternativeEmails.split(',').map(e => e.trim()) : []
        const additional = contactDetails.alternativeEmails.split(',').map(e => e.trim())
        merged.contactDetails.alternativeEmails = [...new Set([...existing, ...additional])].join(', ')
      }
      if (contactDetails.phoneNumbers) {
        const existing = merged.contactDetails.phoneNumbers ? merged.contactDetails.phoneNumbers.split(',').map(p => p.trim()) : []
        const additional = contactDetails.phoneNumbers.split(',').map(p => p.trim())
        merged.contactDetails.phoneNumbers = [...new Set([...existing, ...additional])].join(', ')
      }

      // Merge social media
      const socialMedia = additionalProfile.socialMedia || {}
      if (socialMedia.linkedin && !merged.socialMedia.linkedin) merged.socialMedia.linkedin = socialMedia.linkedin
      if (socialMedia.facebook && !merged.socialMedia.facebook) merged.socialMedia.facebook = socialMedia.facebook
      if (socialMedia.twitter && !merged.socialMedia.twitter) merged.socialMedia.twitter = socialMedia.twitter
      if (socialMedia.instagram && !merged.socialMedia.instagram) merged.socialMedia.instagram = socialMedia.instagram
      if (socialMedia.vk && !merged.socialMedia.vk) merged.socialMedia.vk = socialMedia.vk
      if (socialMedia.socialProfiles && !merged.socialMedia.socialProfiles) merged.socialMedia.socialProfiles = socialMedia.socialProfiles

      // Merge government IDs
      const governmentIDs = additionalProfile.governmentIDs || {}
      if (governmentIDs.ssn && !merged.governmentIDs.ssn) merged.governmentIDs.ssn = governmentIDs.ssn
      if (governmentIDs.tin && !merged.governmentIDs.tin) merged.governmentIDs.tin = governmentIDs.tin
      if (governmentIDs.passport && !merged.governmentIDs.passport) merged.governmentIDs.passport = governmentIDs.passport
      if (governmentIDs.driverLicense && !merged.governmentIDs.driverLicense) merged.governmentIDs.driverLicense = governmentIDs.driverLicense
      if (governmentIDs.nationalID && !merged.governmentIDs.nationalID) merged.governmentIDs.nationalID = governmentIDs.nationalID

      // Merge addresses
      const addresses = additionalProfile.addresses || {}
      if (addresses.street && !merged.addresses.street) merged.addresses.street = addresses.street
      if (addresses.city && !merged.addresses.city) merged.addresses.city = addresses.city
      if (addresses.state && !merged.addresses.state) merged.addresses.state = addresses.state
      if (addresses.country && !merged.addresses.country) merged.addresses.country = addresses.country
      if (addresses.postCode && !merged.addresses.postCode) merged.addresses.postCode = addresses.postCode
      if (addresses.latitude && !merged.addresses.latitude) merged.addresses.latitude = addresses.latitude
      if (addresses.longitude && !merged.addresses.longitude) merged.addresses.longitude = addresses.longitude
      if (addresses.fullAddress && !merged.addresses.fullAddress) merged.addresses.fullAddress = addresses.fullAddress

      // Merge financial
      const financial = additionalProfile.financial || {}
      if (financial.password && !merged.financial.password) merged.financial.password = financial.password
      if (financial.paymentMethod && !merged.financial.paymentMethod) merged.financial.paymentMethod = financial.paymentMethod
      if (financial.balance && !merged.financial.balance) merged.financial.balance = financial.balance
      if (financial.creditCard && !merged.financial.creditCard) merged.financial.creditCard = financial.creditCard
      if (financial.bankAccount && !merged.financial.bankAccount) merged.financial.bankAccount = financial.bankAccount
      if (financial.financialData && !merged.financial.financialData) merged.financial.financialData = financial.financialData

      // Merge breach sources (avoid duplicates)
      if (additionalProfile.breachSources) {
        additionalProfile.breachSources.forEach((breach) => {
          const key = `${breach.source}-${breach.date}`
          if (!breachSourceKeys.has(key)) {
            merged.breachSources.push(breach)
            breachSourceKeys.add(key)
          } else {
            // Merge allRecords if breach already exists
            const existingBreach = merged.breachSources.find(b => `${b.source}-${b.date}` === key)
            if (existingBreach && breach.allRecords) {
              if (!existingBreach.allRecords) {
                existingBreach.allRecords = []
              }
              const existingRecordKeys = new Set(
                existingBreach.allRecords.map((r: any) => JSON.stringify(r))
              )
              breach.allRecords.forEach((record: any) => {
                const recordKey = JSON.stringify(record)
                if (!existingRecordKeys.has(recordKey) && existingBreach.allRecords) {
                  existingBreach.allRecords.push(record)
                  existingRecordKeys.add(recordKey)
                }
              })
            }
          }
        })
      }

      // Merge web results (avoid duplicates)
      if (additionalProfile.webResults) {
        additionalProfile.webResults.forEach((webResult) => {
          if (!webResultIds.has(webResult.id)) {
            merged.webResults.push(webResult)
            webResultIds.add(webResult.id)
          }
        })
      }

      // Merge stealer logs (avoid duplicates)
      if (additionalProfile.stealerLogs) {
        if (!merged.stealerLogs) {
          merged.stealerLogs = []
        }
        additionalProfile.stealerLogs.forEach((stealerLog) => {
          const key = `${stealerLog.stealer_family}-${stealerLog.date_compromised}-${stealerLog.computer_name}`
          if (!stealerLogKeys.has(key)) {
            merged.stealerLogs!.push(stealerLog)
            stealerLogKeys.add(key)
          }
        })
      }

      // Merge photos (avoid duplicates)
      if (additionalProfile.photos) {
        additionalProfile.photos.forEach((photo) => {
          if (!photoUrls.has(photo.url)) {
            merged.photos.push(photo)
            photoUrls.add(photo.url)
          }
        })
      }

      // Merge map locations
      if (additionalProfile.mapLocations) {
        if (!merged.mapLocations) {
          merged.mapLocations = []
        }
        const existingLocationKeys = new Set(
          merged.mapLocations.map(l => `${l.lat}-${l.lng}`)
        )
        additionalProfile.mapLocations.forEach((location) => {
          const key = `${location.lat}-${location.lng}`
          if (!existingLocationKeys.has(key)) {
            merged.mapLocations!.push(location)
            existingLocationKeys.add(key)
          }
        })
      }

      // Merge leakix data
      if (additionalProfile.leakixData && !merged.leakixData) {
        merged.leakixData = additionalProfile.leakixData
      } else if (additionalProfile.leakixData && merged.leakixData) {
        // Merge leakix services and leaks
        if (!merged.leakixData.services) {
          merged.leakixData.services = []
        }
        if (!merged.leakixData.leaks) {
          merged.leakixData.leaks = []
        }
        const existingServiceKeys = new Set(merged.leakixData.services.map((s: any) => s.id || s.name))
        const existingLeakKeys = new Set(merged.leakixData.leaks.map((l: any) => l.id || l.name))
        
        additionalProfile.leakixData.services?.forEach((service: any) => {
          const key = service.id || service.name
          if (!existingServiceKeys.has(key)) {
            merged.leakixData!.services!.push(service)
            existingServiceKeys.add(key)
          }
        })
        
        additionalProfile.leakixData.leaks?.forEach((leak: any) => {
          const key = leak.id || leak.name
          if (!existingLeakKeys.has(key)) {
            merged.leakixData!.leaks!.push(leak)
            existingLeakKeys.add(key)
          }
        })
        
        merged.leakixData.services_count = merged.leakixData.services.length
        merged.leakixData.leaks_count = merged.leakixData.leaks.length
      }

      // Merge OCCRP Aleph data (prefer highest total_results)
      if (additionalProfile.occrpAlephData && !merged.occrpAlephData) {
        merged.occrpAlephData = additionalProfile.occrpAlephData
      } else if (additionalProfile.occrpAlephData && merged.occrpAlephData) {
        if ((additionalProfile.occrpAlephData.total_results || 0) > (merged.occrpAlephData.total_results || 0)) {
          merged.occrpAlephData = additionalProfile.occrpAlephData
        }
      }

      // Merge sanctions data (prefer sanctioned hit over clean)
      if (additionalProfile.sanctionsData && !merged.sanctionsData) {
        merged.sanctionsData = additionalProfile.sanctionsData
      } else if (additionalProfile.sanctionsData && merged.sanctionsData) {
        if (additionalProfile.sanctionsData.is_sanctioned && !merged.sanctionsData.is_sanctioned) {
          merged.sanctionsData = additionalProfile.sanctionsData
        }
      }

      // Merge Gmail OSINT (prefer existing if both exist)
      if (additionalProfile.gmailOSINT && !merged.gmailOSINT) {
        merged.gmailOSINT = additionalProfile.gmailOSINT
      }
    })

    return merged
  }

  // Handle combining searches
  const handleCombineSearches = async () => {
    if (combinedSearches.length === 0) return

    setLoading(true)
    setShowCombineDialog(false)

    try {
      console.log('Combining searches:', combinedSearches.map(s => s.query))

      // Fetch graph data for all selected searches
      const additionalGraphs: Graph[] = []
      const additionalProfiles: PIIData[] = []

      for (const search of combinedSearches) {
        try {
          console.log(`Fetching data for search: ${search.query}`)
          const searchGraph = await apiClient.searchBreaches(search.query) as Graph
          
          if (searchGraph && searchGraph.nodes) {
            additionalGraphs.push(searchGraph)
            
            // Extract profile data from this graph
            const profileData = extractComprehensivePIIFromLeakOSINT(searchGraph, search.query)
            additionalProfiles.push(profileData)
          }
        } catch (error) {
          console.error(`Failed to fetch data for search ${search.query}:`, error)
          // Continue with other searches even if one fails
        }
      }

      if (additionalGraphs.length === 0) {
        alert('Failed to fetch data for selected searches. Please try again.')
        setLoading(false)
        return
      }

      // Merge graphs
      const mergedGraph = mergeGraphs(graph, additionalGraphs)
      
      // Merge profile data
      const mergedProfile = mergeProfileData(profileData || {
        personalInfo: {},
        contactDetails: {},
        socialMedia: {},
        governmentIDs: {},
        photos: [],
        addresses: {},
        financial: {},
        breachSources: [],
        webResults: []
      }, additionalProfiles)

      // Store merged graph in state (this will trigger useEffect to rebuild profile)
      setMergedGraph(mergedGraph)
      
      // Re-extract profile data from merged graph to ensure consistency
      const finalProfileData = extractComprehensivePIIFromLeakOSINT(mergedGraph, query)
      
      // Merge the re-extracted profile with our manually merged one to get all attributes
      const finalMergedProfile = mergeProfileData(finalProfileData, [mergedProfile])
      
      // Update profile data
      setProfileData(finalMergedProfile)
      
      console.log('Successfully merged searches:', {
        nodes: mergedGraph.nodes.length,
        edges: mergedGraph.edges.length,
        breaches: finalMergedProfile.breachSources.length,
        webResults: finalMergedProfile.webResults.length,
        stealerLogs: finalMergedProfile.stealerLogs?.length || 0
      })

      // Show success message
      alert(`Successfully combined ${combinedSearches.length} searches!\n\nMerged data:\n- ${finalMergedProfile.breachSources.length} total breaches\n- ${finalMergedProfile.webResults.length} web results\n- ${finalMergedProfile.stealerLogs?.length || 0} stealer logs\n- ${mergedGraph.nodes.length} nodes\n- ${mergedGraph.edges.length} edges`)
      
      // Clear combined searches
      setCombinedSearches([])
      
    } catch (error: any) {
      console.error('Failed to combine searches:', error)
      alert(`Failed to combine searches: ${error.message || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  // Interactive Network Graph Component using GoJS
  const NetworkGraph = ({ profileData, originalGraph }: { profileData: PIIData, originalGraph?: any }) => {
    const handleNodeClick = useCallback((nodeData: any) => {
      // Handle node click - could show details or navigate
      console.log('Node clicked:', nodeData)
      
      // If OCCRP summary node is clicked, try to extract and display results
      if (nodeData?.source === 'occrp_aleph' && nodeData?.type === 'source' && nodeData?.id?.startsWith('occrp_aleph_summary:')) {
        console.log('OCCRP summary node clicked, attempting to extract raw_results', {
          nodeId: nodeData.id,
          hasRawResults: !!nodeData.props?.raw_results,
          rawResultsCount: nodeData.props?.raw_results?.length || 0
        })
        
        // If we have raw_results, extract them and update profileData
        if (nodeData.props?.raw_results && Array.isArray(nodeData.props.raw_results) && nodeData.props.raw_results.length > 0) {
          const extractedEntities = nodeData.props.raw_results.map((entity: any) => {
            const collection = entity.collection || {}
            const collectionLabel = typeof collection === 'object' ? collection.label || '' : collection || ''
            const properties = entity.properties || {}
            const schema = entity.schema || 'Thing'
            
            // Extract name
            let entityName = 'Unknown'
            if (schema === 'Pages' || schema === 'Document') {
              entityName = properties.fileName?.[0] || properties.title?.[0] || properties.name?.[0] || `Document ${entity.id?.substring(0, 20)}`
            } else {
              entityName = properties.name?.[0] || properties.alias?.[0] || 'Unknown Entity'
            }
            
            return {
              id: entity.id || '',
              schema: schema,
              name: entityName,
              collection_label: collectionLabel,
              collection_id: typeof collection === 'object' ? collection.collection_id || '' : '',
              country: properties.country || [],
              address: properties.address || [],
              highlight: entity.highlight || [],
              ui_link: entity.links?.ui || '',
              api_link: entity.links?.self || '',
              score: entity.score || 0,
              properties: properties
            }
          })
          
          // Update profileData with extracted entities
          setProfileData(prev => {
            if (!prev) return prev
            return {
              ...prev,
              occrpAlephData: {
                total_results: nodeData.props?.total_results || extractedEntities.length,
                results: extractedEntities
              }
            }
          })
          
          // Scroll to OCCRP section
          setTimeout(() => {
            const occrpSection = document.querySelector('[data-occrp-section]')
            if (occrpSection) {
              occrpSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }, 100)
        }
      }
    }, [])

    // Use original graph if available
    const graphToUse = originalGraph || (profileData ? {
      nodes: [],
      edges: []
    } : null)

    if (!graphToUse) {
      return (
        <div className="relative w-full max-w-full h-[600px] bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg overflow-hidden flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl mb-2 text-slate-700 dark:text-slate-300">⚠️ No Graph Data Available</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">Waiting for data to load...</p>
          </div>
        </div>
      )
    }

    return (
      <D3LinkAnalysisGraph 
        graph={graphToUse} 
        onNodeClick={handleNodeClick}
        height={600}
      />
    )
  }

  // Legacy code removed - using D3LinkAnalysisGraph instead
  // The old ForceGraph3D implementation has been replaced with D3LinkAnalysisGraph

  // Node Detail Modal
  const NodeDetailModal = ({ node, onClose }: { node: any, onClose: () => void }) => {
    if (!node) return null
    
    return (
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-black border border-red-500/30 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", duration: 0.5 }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{node.icon}</span>
                <div>
                  <h3 className="text-white font-mono text-lg">{node.label}</h3>
                  <div className="text-red-400 text-sm font-mono">[{node.type.toUpperCase()}]</div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-red-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {node.type === 'breach' && node.breach && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-red-900/20 border border-red-500/30 rounded p-3">
                    <div className="text-red-400 text-xs font-mono mb-1">[SOURCE]</div>
                    <div className="text-white">{node.breach.source}</div>
                  </div>
                  <div className="bg-red-900/20 border border-red-500/30 rounded p-3">
                    <div className="text-red-400 text-xs font-mono mb-1">[DATE]</div>
                    <div className="text-white">{node.breach.date}</div>
                  </div>
                </div>
                
                <div className="bg-black/50 border border-red-500/30 rounded p-4">
                  <div className="text-red-400 text-sm font-mono mb-3">[EXPOSED DATA]</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(node.breach).filter(([key, value]) => 
                      value && key !== 'source' && key !== 'date' && key !== 'records' && key !== 'dataTypes'
                    ).map(([key, value]) => (
                      <div key={key} className="bg-red-900/10 border border-red-500/20 rounded p-2">
                        <div className="text-red-400 text-xs font-mono">{key.toUpperCase()}</div>
                        <div className="text-white text-sm truncate">{String(value)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {node.type === 'attribute' && (
              <div className="space-y-4">
                <div className="bg-blue-900/20 border border-blue-500/30 rounded p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-blue-400">📊</span>
                    <span className="text-blue-400 text-sm font-mono">[ATTRIBUTE TYPE]</span>
                  </div>
                  <div className="text-white font-mono">{node.attribute}</div>
                </div>
                
                <div className="bg-black/50 border border-red-500/30 rounded p-4">
                  <div className="text-red-400 text-sm font-mono mb-3">[FOUND IN BREACHES]</div>
                  <div className="space-y-2">
                    {profileData?.breachSources.filter(breach => breach[node.attribute as keyof typeof breach]).map((breach, index) => (
                      <div key={index} className="bg-red-900/10 border border-red-500/20 rounded p-2">
                        <div className="text-red-400 text-xs font-mono">{breach.source}</div>
                        <div className="text-white text-sm">{String(breach[node.attribute as keyof typeof breach])}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {node.type === 'target' && (
              <div className="space-y-4">
                <div className="bg-green-900/20 border border-green-500/30 rounded p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-green-400">🎯</span>
                    <span className="text-green-400 text-sm font-mono">[TARGET ANALYSIS]</span>
                  </div>
                  <div className="text-white">Primary target for intelligence gathering</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-black/50 border border-red-500/30 rounded p-3">
                    <div className="text-red-400 text-xs font-mono mb-1">[TOTAL BREACHES]</div>
                    <div className="text-white text-2xl font-bold">{profileData?.breachSources.length || 0}</div>
                  </div>
                  <div className="bg-black/50 border border-red-500/30 rounded p-3">
                    <div className="text-red-400 text-xs font-mono mb-1">[RISK LEVEL]</div>
                    <div className="text-red-400 text-2xl font-bold">HIGH</div>
                  </div>
                </div>
              </div>
            )}
            
            {node.type === 'web_result' && (
              <div className="space-y-4">
                <div className="bg-indigo-900/20 border border-indigo-500/30 rounded p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-indigo-400">🔗</span>
                    <span className="text-indigo-400 text-sm font-mono">[WEB SEARCH RESULT]</span>
                  </div>
                  <div className="text-white">{node.props?.title || node.label}</div>
                </div>
                
                {node.props?.url && (
                  <div className="bg-black/50 border border-indigo-500/30 rounded p-3">
                    <div className="text-indigo-400 text-xs font-mono mb-1">[URL]</div>
                    <a 
                      href={node.props.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-indigo-300 hover:text-indigo-100 underline break-all"
                    >
                      {node.props.url}
                    </a>
                  </div>
                )}
                
                {node.props?.snippet && (
                  <div className="bg-black/50 border border-indigo-500/30 rounded p-3">
                    <div className="text-indigo-400 text-xs font-mono mb-1">[SNIPPET]</div>
                    <div className="text-white text-sm">{node.props.snippet}</div>
                  </div>
                )}
                
                {node.props?.extracted_snippets && node.props.extracted_snippets.length > 0 && (
                  <div className="bg-black/50 border border-indigo-500/30 rounded p-4">
                    <div className="text-indigo-400 text-sm font-mono mb-3">[EXTRACTED CONTENT]</div>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {node.props.extracted_snippets.slice(0, 5).map((snippet: string, idx: number) => (
                        <div key={idx} className="bg-indigo-900/10 border border-indigo-500/20 rounded p-2">
                          <div className="text-white text-xs">{snippet}</div>
                        </div>
                      ))}
                    </div>
                    {node.props.content_length && (
                      <div className="text-indigo-400 text-xs mt-2">
                        Total: {node.props.content_length.toLocaleString()} characters
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    )
  }

  useEffect(() => {
    if (graph && (graph.nodes || graph.NumOfResults || graph.List)) {
      buildProfile()
    }
  }, [graph])

  const buildProfile = async (graphToUse?: Graph) => {
    setLoading(true)
    
    // Simulate scary loading delay
    await new Promise(resolve => setTimeout(resolve, 3000))
    
    const graphForExtraction = graphToUse || mergedGraph || graph
    const piiData = extractComprehensivePIIFromLeakOSINT(graphForExtraction, query)
    
    // Check if query or any breach contains a Gmail address
    const gmailEmail = extractGmailAddress(query, piiData)
    if (gmailEmail) {
      try {
        console.log('Fetching Gmail OSINT for:', gmailEmail)
        const gmailData = await apiClient.getGmailOSINT(gmailEmail) as GmailOSINTData
        piiData.gmailOSINT = gmailData
        
        // Add Gmail profile picture to photos if available
        if (gmailData.profile_picture) {
          piiData.photos.push({
            url: gmailData.profile_picture,
            source: 'Gmail OSINT',
            type: 'profile_photo'
          })
        }
        
        console.log('Gmail OSINT data fetched:', gmailData)
      } catch (error) {
        console.error('Failed to fetch Gmail OSINT:', error)
      }
    }
    
    setProfileData(piiData)
    
    // Save to recent searches
    saveToRecentSearches(query, graph)
    
    setLoading(false)
  }

  const extractGmailAddress = (query: string, piiData: PIIData): string | null => {
    // Check if query itself is a Gmail address
    const queryLower = query.toLowerCase().trim()
    if (queryLower.includes('@gmail.com')) {
      // Extract email from query
      const emailMatch = queryLower.match(/([a-z0-9._%+-]+@gmail\.com)/)
      if (emailMatch) {
        return emailMatch[1]
      }
    }
    
    // Check breach sources for Gmail addresses
    for (const breach of piiData.breachSources) {
      if (breach.email && breach.email.toLowerCase().includes('@gmail.com')) {
        return breach.email.toLowerCase()
      }
    }
    
    // Check contact details
    if (piiData.contactDetails.email && piiData.contactDetails.email.toLowerCase().includes('@gmail.com')) {
      return piiData.contactDetails.email.toLowerCase()
    }
    
    return null
  }

  const extractComprehensivePIIFromLeakOSINT = (graph: any, query: string): PIIData => {
    const piiData: PIIData = {
      personalInfo: {},
      contactDetails: {},
      socialMedia: {},
      governmentIDs: {},
      photos: [],
      addresses: {},
      financial: {},
      breachSources: [],
      webResults: [],
      sycekEnrichment: []
    }
    
    console.log('🔍 Processing LeakOSINT graph - FULL STRUCTURE:', JSON.stringify(graph, null, 2))
    
    // Handle different data structures
    let leakosintData: any = null
    
    // PRIORITY 1: Check metadata for original intelligence result (most reliable)
    console.log('🔍 Checking metadata:', {
      hasMetadata: !!graph?.metadata,
      metadataType: typeof graph?.metadata,
      metadataKeys: graph?.metadata ? Object.keys(graph.metadata) : [],
      hasOriginalIntelligence: !!(graph?.metadata?.original_intelligence_result),
      hasOriginalList: !!(graph?.metadata?.original_list)
    })
    
    if (graph?.metadata?.original_intelligence_result) {
      const originalResult = graph.metadata.original_intelligence_result
      console.log('🔍 original_intelligence_result structure:', JSON.stringify(originalResult, null, 2))
      if (originalResult?.List && Object.keys(originalResult.List).length > 0) {
        leakosintData = {
          NumOfResults: originalResult.NumOfResults || Object.keys(originalResult.List).length,
          List: originalResult.List
        }
        console.log(`✅ Using original_intelligence_result from metadata: ${Object.keys(leakosintData.List).length} breaches`)
        console.log('Sample breach names:', Object.keys(leakosintData.List).slice(0, 10))
      }
    }
    
    // PRIORITY 2: Check metadata for original_list
    if (!leakosintData && graph?.metadata?.original_list) {
      console.log('🔍 original_list structure:', JSON.stringify(graph.metadata.original_list, null, 2))
      leakosintData = {
        NumOfResults: graph.metadata.original_num_results || Object.keys(graph.metadata.original_list).length,
        List: graph.metadata.original_list
      }
      console.log(`✅ Using original_list from graph metadata: ${Object.keys(leakosintData.List).length} breaches`)
      console.log('Sample breach names:', Object.keys(leakosintData.List).slice(0, 10))
    }
    
    // PRIORITY 3: Check if graph itself is the LeakOSINT API response format
    if (!leakosintData && graph?.NumOfResults && graph?.List) {
      leakosintData = graph
      console.log('✅ Using direct LeakOSINT API response format', {
        NumOfResults: leakosintData.NumOfResults,
        breachCount: Object.keys(leakosintData.List || {}).length,
        sampleBreaches: Object.keys(leakosintData.List || {}).slice(0, 5)
      })
    }
    
    // PRIORITY 4: Check nodes for original_data props (backend stores data in node props)
    if (!leakosintData && graph?.nodes) {
      console.log('🔍 Checking nodes for original_data...')
      const breachNodesWithData = graph.nodes.filter((n: any) => 
        n.props?.original_data && Array.isArray(n.props.original_data) && n.props.original_data.length > 0
      )
      
      if (breachNodesWithData.length > 0) {
        console.log(`✅ Found ${breachNodesWithData.length} breach nodes with original_data`)
        leakosintData = {
          NumOfResults: breachNodesWithData.length,
          List: {}
        }
        
        breachNodesWithData.forEach((node: any) => {
          const breachName = node.props?.breach_name || node.props?.sycek_intelligence_id || node.label?.replace('Breach: ', '') || `Breach_${Object.keys(leakosintData.List).length + 1}`
          leakosintData.List[breachName] = {
            Data: node.props.original_data,
            InfoLeak: node.props.original_info_leak || node.props.breach_description || '',
            NumOfResults: node.props.original_num_results || node.props.original_data.length || 1
          }
        })
        
        console.log(`✅ Constructed leakosintData from node props: ${Object.keys(leakosintData.List).length} breaches`)
      }
    }
    
    // PRIORITY 4: Fallback to extracting from graph nodes (less reliable)
    if (!leakosintData) {
      // Log graph structure for debugging
      console.log('⚠️ No LeakOSINT data found in metadata, falling back to node extraction')
      console.log('Graph structure:', {
        hasNodes: !!graph?.nodes,
        nodeCount: graph?.nodes?.length || 0,
        hasEdges: !!graph?.edges,
        edgeCount: graph?.edges?.length || 0,
        hasMetadata: !!graph?.metadata,
        metadataKeys: graph?.metadata ? Object.keys(graph.metadata) : [],
        metadataContent: graph?.metadata ? JSON.stringify(graph.metadata, null, 2).substring(0, 500) : 'none',
        sampleNodes: graph?.nodes?.slice(0, 3).map((n: any) => ({
          id: n.id,
          type: n.type,
          label: n.label,
          source: n.source,
          props_keys: Object.keys(n.props || {}),
          hasOriginalData: !!n.props?.original_data
        }))
      })
    }
    
    // If we still don't have leakosintData, try extracting from graph nodes
    if (!leakosintData && graph && graph.nodes) {
      // Graph structure - extract all breach information from nodes and edges
      console.log('Processing graph structure with', graph.nodes.length, 'nodes and', graph.edges?.length || 0, 'edges')
      // Graph structure with nodes - extract breach information
      // Look for breach nodes (type="source" with breach props OR source="sycek_intelligence"/"leakosint_api")
      // Also find breaches by tracing edges from entity nodes
      let breachNodes = graph.nodes.filter((node: any) => {
        // Check if it's a breach node by props
        if (node.props?.node_type === 'breach' || node.props?.breach_name) {
          return true
        }
        // Check if it's from LeakOSINT source
        if (node.source === 'sycek_intelligence' || node.source === 'leakosint_api') {
          return true
        }
        // Check if it has breach-related information in label
        if (node.type === 'source' && node.label?.toLowerCase().includes('breach:')) {
          return true
        }
        // Check if label starts with "Breach:"
        if (node.label && node.label.startsWith('Breach:')) {
          return true
        }
        // Check if it has breach-related props
        if (node.props?.breach_source || node.props?.breach_date || node.props?.records_affected) {
          return true
        }
        return false
      })
      
      // Also find breaches by tracing edges from entity nodes
      // Look for nodes that have "exposed_*" edges pointing to them
      if (graph.edges) {
        const breachNodeIds = new Set<string>()
        graph.edges.forEach((edge: any) => {
          // Check if edge relation indicates a breach connection
          const relation = edge.relation || ''
          if (relation.includes('exposed_') || relation === 'compromised_in') {
            // The source or target might be a breach
            const sourceNode = graph.nodes.find((n: any) => n.id === edge.source)
            const targetNode = graph.nodes.find((n: any) => n.id === edge.target)
            
            if (sourceNode && (sourceNode.props?.breach_name || sourceNode.props?.node_type === 'breach')) {
              breachNodeIds.add(sourceNode.id)
            }
            if (targetNode && (targetNode.props?.breach_name || targetNode.props?.node_type === 'breach')) {
              breachNodeIds.add(targetNode.id)
            }
          }
        })
        
        // Add any found breach nodes
        breachNodeIds.forEach(id => {
          const node = graph.nodes.find((n: any) => n.id === id)
          if (node && !breachNodes.find((n: any) => n.id === id)) {
            breachNodes.push(node)
          }
        })
      }
      
      console.log('Processing LeakOSINT nodes:', breachNodes.length, 'total nodes:', graph.nodes.length)
      console.log('Sample nodes:', graph.nodes.slice(0, 5).map((n: any) => ({
        id: n.id,
        type: n.type,
        label: n.label,
        source: n.source,
        props_keys: Object.keys(n.props || {}),
        node_type: n.props?.node_type,
        entity_type: n.props?.entity_type,
        breach_name: n.props?.breach_name,
        full_props: n.props
      })))
      
      // If no breach nodes found but we have nodes, try to extract from all nodes
      if (breachNodes.length === 0 && graph.nodes.length > 0) {
        console.log('No breach nodes found with strict filtering, trying flexible extraction...')
        
        // Try to find any source nodes (breaches, summaries, etc.)
        const allSourceNodes = graph.nodes.filter((node: any) => {
          return node.type === 'source' || node.source === 'sycek_intelligence' || node.source === 'leakosint_api'
        })
        
        console.log('Found source nodes:', allSourceNodes.length)
        
        // If we have source nodes, treat them as breaches
        if (allSourceNodes.length > 0) {
          breachNodes.push(...allSourceNodes)
        }
        
        // Also check for summary nodes
        const summaryNodes = graph.nodes.filter((node: any) => {
          return node.label?.toLowerCase().includes('found in') || 
                 node.label?.toLowerCase().includes('breach') ||
                 node.label?.toLowerCase().includes('compromised') ||
                 node.props?.status === 'COMPROMISED' ||
                 node.props?.total_breaches !== undefined
        })
        
        console.log('Found summary nodes:', summaryNodes.length)
        
        if (summaryNodes.length > 0) {
          breachNodes.push(...summaryNodes)
        }
      }
      
      // Group breach nodes by breach_name
      const breachesMap = new Map<string, any>()
      
      breachNodes.forEach((node: any) => {
        // Extract breach name from various possible fields
        let breachName = node.props?.breach_name || 
                        node.props?.breach_source ||
                        node.label?.replace('Breach: ', '') ||
                        node.label?.replace('Found in ', '') ||
                        node.label ||
                        node.props?.sycek_intelligence_id ||
                        `Breach_${breachesMap.size + 1}`
        
        // Clean up breach name
        if (breachName.includes('breach') || breachName.includes('Breach')) {
          breachName = breachName.replace(/^.*?[Bb]reach[:\s]+/i, '').trim()
        }
        if (!breachName || breachName === 'Unknown Breach' || breachName.toLowerCase().includes('no results')) {
          breachName = `Breach_${breachesMap.size + 1}`
        }
        
        if (!breachesMap.has(breachName)) {
          // Check if node has original_data array (from backend)
          const originalData = node.props?.original_data || []
          
          breachesMap.set(breachName, {
            breach_name: breachName,
            breach_date: node.props?.breach_date || 
                        node.props?.date || 
                        node.props?.InfoLeak ||
                        node.props?.original_info_leak ||
                        'Unknown Date',
            breach_description: node.props?.breach_description || 
                               node.props?.description ||
                               node.props?.InfoLeak || 
                               node.props?.original_info_leak ||
                               node.label || '',
            breach_url: node.props?.breach_url || node.props?.url || '',
            records_affected: node.props?.records_affected || 
                             node.props?.records_found ||
                             node.props?.total_breaches ||
                             node.props?.NumOfResults ||
                             node.props?.original_num_results ||
                             0,
            data_types: node.props?.data_types || 
                      node.props?.dataTypes ||
                      'Email, Password, Personal Data',
            // Use original_data if available, otherwise empty array
            Data: Array.isArray(originalData) ? originalData : []
          })
          
          // Log if we found original data
          if (originalData.length > 0) {
            console.log(`Found ${originalData.length} original data records for breach: ${breachName}`)
          }
        }
      })
      
      // Now find associated data nodes for each breach
      // Look for edges connecting breach nodes to data nodes
      if (graph.edges) {
        graph.edges.forEach((edge: any) => {
          const sourceNode = graph.nodes.find((n: any) => n.id === edge.source)
          const targetNode = graph.nodes.find((n: any) => n.id === edge.target)
          
          if (sourceNode && targetNode) {
            // If source is a breach and target is an entity (new structure)
            const isSourceBreach = sourceNode.props?.node_type === 'breach' || 
                                   sourceNode.props?.breach_name || 
                                   sourceNode.label?.startsWith('Breach:') ||
                                   (sourceNode.type === 'source' && sourceNode.source === 'sycek_intelligence') ||
                                   (sourceNode.type === 'source' && sourceNode.source === 'leakosint_api')
            
            const isTargetEntity = targetNode.props?.entity_type || 
                                  targetNode.props?.node_type === 'exposed_data' || 
                                  targetNode.props?.node_type === 'data_field' ||
                                  targetNode.type === 'email' ||
                                  targetNode.type === 'phone' ||
                                  targetNode.type === 'person' ||
                                  targetNode.type === 'location' ||
                                  targetNode.type === 'ip_address' ||
                                  targetNode.type === 'document' ||
                                  targetNode.type === 'password'
            
            if (isSourceBreach && isTargetEntity) {
              // Extract breach name with better fallback
              let breachName = sourceNode.props?.breach_name || 
                              sourceNode.props?.breach_source ||
                              sourceNode.label?.replace('Breach: ', '') ||
                              sourceNode.label?.replace(/^.*?[Bb]reach[:\s]+/i, '') ||
                              'Unknown Breach'
              
              // Clean up breach name
              if (breachName.toLowerCase().includes('no results') || breachName.toLowerCase().includes('no breaches')) {
                breachName = `Breach_${breachesMap.size + 1}`
              }
              if (!breachesMap.has(breachName)) {
                breachesMap.set(breachName, {
                  breach_name: breachName,
                  breach_date: sourceNode.props?.breach_date || 'Unknown Date',
                  breach_description: sourceNode.props?.breach_description || '',
                  breach_url: sourceNode.props?.breach_url || '',
                  records_affected: sourceNode.props?.records_affected || 0,
                  data_types: sourceNode.props?.data_types || 'Email, Password, Personal Data',
                  Data: []
                })
              }
              const breach = breachesMap.get(breachName)!
              
              // Extract data from entity node
              const entityType = targetNode.props?.entity_type || ''
              const dataEntry: any = {}
              
              // Extract based on entity type
              if (entityType === 'email') {
                dataEntry.Email = targetNode.props?.email_address || targetNode.label
              } else if (entityType === 'phone') {
                dataEntry.Phone = targetNode.props?.phone_number || targetNode.label
              } else if (entityType === 'person') {
                dataEntry.FullName = targetNode.props?.full_name || targetNode.label
                if (targetNode.props?.first_name) dataEntry.FirstName = targetNode.props.first_name
                if (targetNode.props?.last_name) dataEntry.LastName = targetNode.props.last_name
              } else if (entityType === 'location') {
                if (targetNode.props?.address) dataEntry.Address = targetNode.props.address
                if (targetNode.props?.city) dataEntry.City = targetNode.props.city
                if (targetNode.props?.state) dataEntry.State = targetNode.props.state
                if (targetNode.props?.country) dataEntry.Country = targetNode.props.country
              } else if (entityType === 'ip_address') {
                dataEntry.IP = targetNode.props?.ip_address || targetNode.label
              } else if (entityType === 'document') {
                dataEntry[targetNode.props?.document_type || 'ID'] = targetNode.props?.document_number || targetNode.label
              } else if (entityType === 'password') {
                dataEntry.Password = targetNode.props?.password_hash || targetNode.label
              }
              
              // Also check for raw_data or other props
              if (targetNode.props?.raw_data) {
                Object.assign(dataEntry, targetNode.props.raw_data)
              } else if (targetNode.props?.field_name && targetNode.props?.field_value) {
                dataEntry[targetNode.props.field_name] = targetNode.props.field_value
              } else if (Object.keys(dataEntry).length === 0 && targetNode.props) {
                // Fallback: use all props as data
                Object.keys(targetNode.props).forEach(key => {
                  if (!['node_type', 'breach_name', 'data_fields', 'entity_type'].includes(key) && targetNode.props[key]) {
                    dataEntry[key] = targetNode.props[key]
                  }
                })
              }
              
              if (Object.keys(dataEntry).length > 0) {
                breach.Data.push(dataEntry)
              }
            }
          }
        })
      }
      
      // Convert map to LeakOSINT format
      if (breachesMap.size > 0) {
        leakosintData = {
          NumOfResults: breachesMap.size,
          List: {}
        }
        
        breachesMap.forEach((breach, breachName) => {
          // Ensure we have at least one data entry
          if (breach.Data.length === 0) {
            breach.Data.push({
              breach_name: breach.breach_name,
              breach_date: breach.breach_date,
              records_affected: breach.records_affected,
              Email: query // Use the search query as email if available
            })
          }
          
          leakosintData.List[breachName] = {
            Data: breach.Data,
            InfoLeak: breach.breach_description || `Breach data from ${breachName}`,
            NumOfResults: breach.Data.length || breach.records_affected || 1
          }
        })
        
        console.log(`Converted ${breachesMap.size} breaches to LeakOSINT format`)
      } else {
        // If no breaches found via nodes, try to extract directly from graph structure
        // Sometimes the backend returns the data in a different format
        console.log('No breaches found in breachesMap, checking graph structure directly...')
        
        // Check if graph has any breach-related metadata
        if (graph.metadata) {
          console.log('Graph metadata:', graph.metadata)
        }
        
        // Try to find any source nodes that might be breaches
        const allSourceNodes = graph.nodes.filter((node: any) => node.type === 'source')
        console.log(`Found ${allSourceNodes.length} source nodes in graph`)
        
        if (allSourceNodes.length > 0) {
          leakosintData = {
            NumOfResults: allSourceNodes.length,
            List: {}
          }
          
          allSourceNodes.forEach((node: any, index: number) => {
            const breachName = node.props?.breach_name || 
                              node.label?.replace('Breach: ', '') || 
                              node.label ||
                              `Breach_${index + 1}`
            
            // Create data entry from node props
            const dataEntry: any = { Email: query }
            if (node.props) {
              Object.keys(node.props).forEach(key => {
                if (key !== 'node_type' && key !== 'breach_name' && node.props[key]) {
                  dataEntry[key] = node.props[key]
                }
              })
            }
            
            leakosintData.List[breachName] = {
              Data: [dataEntry],
              InfoLeak: node.props?.breach_description || node.label || '',
              NumOfResults: 1
            }
          })
          
          console.log(`Created ${allSourceNodes.length} breaches from source nodes`)
        }
      }
    }
    
    // Extract images from graph nodes (Google Images)
    if (graph && graph.nodes) {
      const imageNodes = graph.nodes.filter((node: any) => 
        node.type === 'image' || 
        (node.source === 'google_images') ||
        (node.props?.original_url || node.props?.thumbnail_url)
      )
      console.log('Found image nodes:', imageNodes.length)
      imageNodes.forEach((node: any) => {
        const imageUrl = node.props?.original_url || node.props?.thumbnail_url || node.props?.url || ''
        if (imageUrl && !piiData.photos.some((photo: any) => photo.url === imageUrl)) {
          piiData.photos.push({
            url: imageUrl,
            source: node.source || 'google_images',
            type: node.props?.title || node.label || 'image'
          })
        }
      })
    }
    
    // Extract web results from graph nodes
    if (graph && graph.nodes) {
      const webResultNodes = graph.nodes.filter((node: any) => node.type === 'web_result')
      console.log('Found web result nodes:', webResultNodes.length)
      webResultNodes.forEach((node: any) => {
        piiData.webResults.push({
          id: node.id || `web_${piiData.webResults.length}`,
          title: node.props?.title || node.label || 'Web Result',
          url: node.props?.url || '',
          snippet: node.props?.snippet || '',
          extracted_snippets: node.props?.extracted_snippets || [],
          props: node.props || {}
        })
      })
    }
    
    if (!leakosintData || !leakosintData.List || Object.keys(leakosintData.List).length === 0) {
      console.log('No LeakOSINT data found in List format')
      console.log('Graph structure:', {
        hasNodes: !!graph?.nodes,
        nodeCount: graph?.nodes?.length || 0,
        hasEdges: !!graph?.edges,
        edgeCount: graph?.edges?.length || 0,
        breachSourcesCount: piiData.breachSources.length
      })
      
      // If we have breachSources from edge extraction, that's fine
      // But we should try to process what we have
      if (piiData.breachSources.length > 0) {
        console.log(`Found ${piiData.breachSources.length} breach sources from edge extraction`)
      }
    }
    
    // Process LeakOSINT data - only if we have List data
    if (leakosintData && leakosintData.List && Object.keys(leakosintData.List).length > 0) {
      console.log(`Processing ${Object.keys(leakosintData.List).length} breaches from LeakOSINT List`)
      console.log('Full leakosintData structure:', JSON.stringify(leakosintData, null, 2))
      
      Object.entries(leakosintData.List).forEach(([breachName, breachData]: [string, any]) => {
      console.log(`Processing breach: ${breachName}`, breachData)
      
      // Process all records in this breach
      if (breachData.Data && Array.isArray(breachData.Data)) {
        console.log(`  Found ${breachData.Data.length} records in breach: ${breachName}`)
        
        // IMPORTANT: Add ONE breach source entry per breach name, but store all records
        // The breachSources array should have one entry per unique breach, not per record
        const existingBreachIndex = piiData.breachSources.findIndex((bs: any) => bs.source === breachName)
        
        // Collect all data types and records for this breach
        const allDataTypes = new Set<string>()
        const allRecords: any[] = []
        
        breachData.Data.forEach((record: any, recordIndex: number) => {
          if (recordIndex < 10) { // Only log first 10 to avoid console spam
            console.log(`Processing record ${recordIndex} from ${breachName}:`, record)
          }
          
          const parsedProps = record
          allRecords.push(parsedProps)
          
          // Collect data types from this record
          Object.keys(parsedProps).forEach(key => {
            if (parsedProps[key] && key !== 'Date' && key !== 'RegDate') {
              allDataTypes.add(key)
            }
          })
      
      // Extract Personal Information
      if (parsedProps.FirstName || parsedProps.LastName || parsedProps.FullName) {
        piiData.personalInfo = {
          ...piiData.personalInfo,
          fullName: parsedProps.FullName || `${parsedProps.FirstName || ''} ${parsedProps.LastName || ''}`.trim(),
          firstName: parsedProps.FirstName || '',
          lastName: parsedProps.LastName || '',
          nickName: parsedProps.NickName || '',
          dob: parsedProps.BDay || parsedProps.BirthDate || '',
          gender: parsedProps.Gender || '',
          age: parsedProps.Age || '',
          nationality: parsedProps.Nationality || parsedProps.Country || ''
        }
      }
      
      // Extract Contact Details
      if (parsedProps.Email || parsedProps.Phone || parsedProps.PhoneNumber) {
        piiData.contactDetails = {
          ...piiData.contactDetails,
          // Keep a real email already found from an earlier record in this breach
          // rather than blanking it out just because this record only has a phone —
          // and never fall back to the search query itself (it isn't an email).
          email: parsedProps.Email || piiData.contactDetails?.email || '',
          phone: parsedProps.Phone || parsedProps.PhoneNumber || '',
          alternativeEmails: parsedProps.AlternativeEmail || '',
          phoneNumbers: parsedProps.PhoneNumbers || ''
        }
      }
      
      // Extract Social Media
      if (parsedProps.LinkedIn || parsedProps.Facebook || parsedProps.Twitter || parsedProps.Instagram || parsedProps.VK) {
        piiData.socialMedia = {
          ...piiData.socialMedia,
          linkedin: parsedProps.LinkedIn || '',
          facebook: parsedProps.Facebook || '',
          twitter: parsedProps.Twitter || '',
          instagram: parsedProps.Instagram || '',
          vk: parsedProps.VK || '',
          socialProfiles: parsedProps.SocialProfiles || ''
        }
      }
      
      // Extract Government IDs
      if (parsedProps.SSN || parsedProps.TIN || parsedProps.Passport || parsedProps.DriverLicense || parsedProps.ID) {
        piiData.governmentIDs = {
          ...piiData.governmentIDs,
          ssn: parsedProps.SSN || '',
          tin: parsedProps.TIN || parsedProps.TaxID || '',
          passport: parsedProps.Passport || '',
          driverLicense: parsedProps.DriverLicense || '',
          nationalID: parsedProps.ID || parsedProps.NationalID || ''
        }
      }
      
      // Extract Photos/Avatars
      if (parsedProps.Avatar || parsedProps.Photo || parsedProps.ProfilePicture || parsedProps.Image) {
        if (!piiData.photos) piiData.photos = []
        
        const photoUrls = [
          parsedProps.Avatar,
          parsedProps.Photo,
          parsedProps.ProfilePicture,
          parsedProps.Image
        ].filter(Boolean)
        
        photoUrls.forEach(url => {
          if (url && !piiData.photos.some((photo: any) => photo.url === url)) {
            piiData.photos.push({
              url: url,
              source: breachName,
              type: 'profile_photo'
            })
          }
        })
      }
      
      // Extract Addresses
      if (parsedProps.Address || parsedProps.City || parsedProps.State || parsedProps.Country) {
        piiData.addresses = {
          ...piiData.addresses,
          street: parsedProps.Address || parsedProps.Street || '',
          city: parsedProps.City || '',
          state: parsedProps.State || parsedProps.Province || '',
          country: parsedProps.Country || '',
          postCode: parsedProps.PostCode || parsedProps.ZipCode || '',
          latitude: parsedProps.Latitude || '',
          longitude: parsedProps.Longitude || '',
          fullAddress: `${parsedProps.Address || ''} ${parsedProps.City || ''} ${parsedProps.State || ''} ${parsedProps.Country || ''}`.trim()
        }
      }
      
      // Extract Financial Information
      if (parsedProps.Password || parsedProps.PayMetod || parsedProps.Balance || parsedProps.CreditCard || parsedProps.BankAccount) {
        piiData.financial = {
          ...piiData.financial,
          password: parsedProps.Password || '',
          paymentMethod: parsedProps.PayMetod || parsedProps.PaymentMethod || '',
          balance: parsedProps.Balance || '',
          creditCard: parsedProps.CreditCard || '',
          bankAccount: parsedProps.BankAccount || '',
          financialData: parsedProps.FinancialData || ''
        }
      }
        })
        
        // Create or update breach source entry with aggregated data
        // Collect all unique values for each field across all records
        const aggregatedFields: Record<string, string[]> = {}
        allRecords.forEach((record: any) => {
          Object.keys(record).forEach(key => {
            if (record[key] && key !== 'Date' && key !== 'RegDate') {
              if (!aggregatedFields[key]) {
                aggregatedFields[key] = []
              }
              const value = String(record[key]).trim()
              if (value && !aggregatedFields[key].includes(value)) {
                aggregatedFields[key].push(value)
              }
            }
          })
        })
        
        // Convert arrays to single values for common fields
        const getFirstValue = (fieldName: string) => {
          const values = aggregatedFields[fieldName] || []
          return values.length > 0 ? values[0] : ''
        }
        
        const breachSourceEntry: any = {
          source: breachName,
          date: allRecords[0]?.Date || allRecords[0]?.RegDate || breachData.InfoLeak || 'Unknown',
          records: breachData.NumOfResults?.toString() || allRecords.length.toString(),
          dataTypes: Array.from(allDataTypes).join(', ') || 'Unknown',
          // Real per-source match-quality signal (token overlap between the
          // search query and this breach's identity fields), computed
          // server-side (breach_service.py's _compute_match_relevance) since
          // LeakOSINT does unfiltered free-text matching with no
          // entity-type awareness. null/undefined when the backend had
          // nothing to score against (e.g. an older cached result).
          matchRelevance: typeof breachData._match_relevance === 'number' ? breachData._match_relevance : null,
          // Use first record's data as primary display values. No query
          // fallback — the search term isn't an email, and this breach may
          // genuinely not have one (e.g. a phone-only or username-only record).
          email: getFirstValue('Email') || '',
          password: getFirstValue('Password') || '',
          fullName: getFirstValue('FullName') || `${getFirstValue('FirstName') || ''} ${getFirstValue('LastName') || ''}`.trim(),
          phone: getFirstValue('Phone') || getFirstValue('PhoneNumber') || '',
          address: getFirstValue('Address') || '',
          city: getFirstValue('City') || '',
          birthDate: getFirstValue('BDay') || getFirstValue('BirthDate') || '',
          ipAddress: getFirstValue('IP') || getFirstValue('IPAddress') || '',
          gender: getFirstValue('Gender') || '',
          nationality: getFirstValue('Nationality') || getFirstValue('Country') || '',
          jobTitle: getFirstValue('JobTitle') || getFirstValue('Title') || '',
          company: getFirstValue('Company') || getFirstValue('CompanyName') || '',
          website: getFirstValue('Website') || getFirstValue('URL') || '',
          socialMedia: getFirstValue('LinkedIn') || getFirstValue('Facebook') || getFirstValue('Twitter') || '',
          avatar: getFirstValue('Avatar') || getFirstValue('ProfilePicture') || '',
          // Store all records for this breach (CRITICAL for displaying all data)
          allRecords: allRecords,
          // Store aggregated fields for easy access (as arrays, not Sets)
          aggregatedFields: aggregatedFields,
          breachDescription: breachData.InfoLeak || '',
          numOfResults: breachData.NumOfResults || allRecords.length
        }
        
        if (existingBreachIndex === -1) {
          // New breach source - add it
          piiData.breachSources.push(breachSourceEntry)
        } else {
          // Update existing breach source with aggregated data
          piiData.breachSources[existingBreachIndex] = breachSourceEntry
        }
      } else {
        // No Data array, create breach entry from breachData itself
        const existingBreachIndex = piiData.breachSources.findIndex((bs: any) => bs.source === breachName)
        if (existingBreachIndex === -1) {
          piiData.breachSources.push({
            source: breachName,
            date: breachData.InfoLeak || 'Unknown',
            records: breachData.NumOfResults?.toString() || '1',
            dataTypes: 'Unknown',
            email: '',
            matchRelevance: typeof breachData._match_relevance === 'number' ? breachData._match_relevance : null,
            allRecords: [],
            breachDescription: breachData.InfoLeak || '',
            numOfResults: breachData.NumOfResults || 1
          })
        }
      }
      })
    }
    
    // Extract stealer logs (Hudson Rock)
    piiData.stealerLogs = []
    piiData.mapLocations = []
    
    // Extract LeakIX data
    piiData.leakixData = null
    
    if (graph?.nodes) {
      // Find LeakIX summary node
      const leakixNodes = graph.nodes.filter((node: any) => 
        node.source === 'leakix' && 
        node.type === 'source' && 
        node.id?.startsWith('leakix:')
      )
      
      if (leakixNodes.length > 0) {
        const leakixNode = leakixNodes[0]
        const services: any[] = []
        const leaks: any[] = []
        
        // Find all service and leak nodes connected to this LeakIX node
        if (graph.edges) {
          const serviceEdges = graph.edges.filter((edge: any) => 
            edge.source === leakixNode.id && 
            edge.relation === 'has_service'
          )
          const leakEdges = graph.edges.filter((edge: any) => 
            edge.source === leakixNode.id && 
            edge.relation === 'has_leak'
          )
          
          serviceEdges.forEach((edge: any) => {
            const serviceNode = graph.nodes.find((n: any) => n.id === edge.target)
            if (serviceNode) {
              services.push(serviceNode.props)
            }
          })
          
          leakEdges.forEach((edge: any) => {
            const leakNode = graph.nodes.find((n: any) => n.id === edge.target)
            if (leakNode) {
              leaks.push(leakNode.props)
            }
          })
        }
        
        piiData.leakixData = {
          domain: leakixNode.props?.domain || query,
          services_count: leakixNode.props?.services_count || services.length,
          leaks_count: leakixNode.props?.leaks_count || leaks.length,
          services: services,
          leaks: leaks
        }
      }
      
      // Extract OCCRP Aleph data - more robust extraction
      piiData.occrpAlephData = null
      const occrpSummaryNodes = graph.nodes.filter((node: any) => 
        node.source === 'occrp_aleph' && 
        node.type === 'source' && 
        node.id?.startsWith('occrp_aleph_summary:')
      )
      
      // Also find all OCCRP Aleph entity/document nodes directly (in case edges are missing)
      // Handle all node types: entity, document, person, company (from schemas: LegalEntity, Company, Person, Pages, Document, etc.)
      const allOccrpNodes = graph.nodes.filter((node: any) => 
        node.source === 'occrp_aleph' && 
        (node.type === 'entity' || node.type === 'document' || node.type === 'person' || node.type === 'company') &&
        (node.id?.startsWith('occrp_aleph_entity:') || node.props?.source === 'occrp_aleph')
      )
      
      console.log('OCCRP Extraction Debug:', {
        summaryNodes: occrpSummaryNodes.length,
        allOccrpNodes: allOccrpNodes.length,
        totalGraphNodes: graph.nodes.length,
        occrpNodes: graph.nodes.filter((n: any) => n.source === 'occrp_aleph').length
      })
      
      const occrpEntities: any[] = []
      
      if (occrpSummaryNodes.length > 0) {
        const summaryNode = occrpSummaryNodes[0]
        
        // FIRST: Try to extract from raw_results stored in summary node (most reliable)
        if (summaryNode.props?.raw_results && Array.isArray(summaryNode.props.raw_results)) {
          console.log('Extracting OCCRP entities from raw_results in summary node', {
            raw_results_count: summaryNode.props.raw_results.length
          })
          
          summaryNode.props.raw_results.forEach((entity: any) => {
            const entityId = entity.id || ''
            if (!entityId || occrpEntities.some(e => e.id === entityId)) return
            
            const collection = entity.collection || {}
            const collectionLabel = typeof collection === 'object' ? collection.label || '' : collection || ''
            const properties = entity.properties || {}
            
            // Extract name
            let entityName = 'Unknown'
            const schema = entity.schema || 'Thing'
            
            if (schema === 'Pages' || schema === 'Document') {
              entityName = properties.fileName?.[0] || properties.title?.[0] || properties.name?.[0] || `Document ${entityId.substring(0, 20)}`
            } else {
              entityName = properties.name?.[0] || properties.alias?.[0] || 'Unknown Entity'
            }
            
            // Normalize country and address to arrays
            const countries = Array.isArray(properties.country)
              ? properties.country
              : (properties.country ? [properties.country] : []);
            const addresses = Array.isArray(properties.address)
              ? properties.address
              : (properties.address ? [properties.address] : []);
            
            occrpEntities.push({
              id: entityId,
              schema: schema,
              name: entityName,
              collection_label: collectionLabel,
              collection_id: typeof collection === 'object' ? collection.collection_id || '' : '',
              country: countries,
              address: addresses,
              highlight: entity.highlight || [],
              ui_link: entity.links?.ui || '',
              api_link: entity.links?.self || '',
              score: entity.score || 0,
              properties: properties
            })
          })
        }
        
        // Find all OCCRP Aleph entity nodes via edges
        if (graph.edges) {
          const entityEdges = graph.edges.filter((edge: any) => 
            edge.source === summaryNode.id && 
            edge.relation === 'has_entity'
          )
          
          console.log('Found OCCRP entity edges from summary node', {
            summaryNodeId: summaryNode.id,
            entityEdgesCount: entityEdges.length
          })
          
          entityEdges.forEach((edge: any) => {
            const entityNode = graph.nodes.find((n: any) => n.id === edge.target)
            if (entityNode && entityNode.source === 'occrp_aleph') {
              // Extract name - handle documents differently
              let entityName = entityNode.label?.replace(/^(Person|Company|Entity|Document|LegalEntity|Mention|Page|Pages|HyperText):\s*/, '') || 'Unknown'
              if (entityName === 'Unknown' || !entityName) {
                // Try to get name from props
                if (entityNode.props?.name) {
                  entityName = Array.isArray(entityNode.props.name) ? entityNode.props.name[0] : entityNode.props.name
                } else if (entityNode.props?.fileName) {
                  entityName = Array.isArray(entityNode.props.fileName) ? entityNode.props.fileName[0] : entityNode.props.fileName
                } else if (entityNode.props?.title) {
                  entityName = Array.isArray(entityNode.props.title) ? entityNode.props.title[0] : entityNode.props.title
                } else if (entityNode.props?.alias) {
                  entityName = Array.isArray(entityNode.props.alias) ? entityNode.props.alias[0] : entityNode.props.alias
                }
              }
              
              // Normalize country and address to arrays
              const countries = Array.isArray(entityNode.props?.country)
                ? entityNode.props.country
                : (entityNode.props?.country ? [entityNode.props.country] : []);
              const addresses = Array.isArray(entityNode.props?.address)
                ? entityNode.props.address
                : (entityNode.props?.address ? [entityNode.props.address] : []);
              const highlights = Array.isArray(entityNode.props?.highlight)
                ? entityNode.props.highlight
                : (entityNode.props?.highlight ? [entityNode.props.highlight] : []);
              
              occrpEntities.push({
                id: entityNode.props?.entity_id || entityNode.id,
                schema: entityNode.props?.schema || 'Thing',
                name: entityName,
                collection_label: entityNode.props?.collection_label || '',
                collection_id: entityNode.props?.collection_id || '',
                country: countries,
                address: addresses,
                highlight: highlights,
                ui_link: entityNode.props?.ui_link || '',
                api_link: entityNode.props?.api_link || '',
                score: entityNode.props?.score || 0,
                properties: entityNode.props || {}
              })
            }
          })
        }
      }
      
      // Find query node first
      const queryNodes = graph.nodes.filter((node: any) => node.type === 'query')
      const queryNodeId = queryNodes.length > 0 ? queryNodes[0].id : null
      
      // Also find entities via direct edges from query node (related_entity relation)
      if (graph.edges && queryNodeId) {
        const directEntityEdges = graph.edges.filter((edge: any) => 
          edge.source === queryNodeId && 
          edge.relation === 'related_entity' &&
          edge.edge_source === 'occrp_aleph'
        )
        
        directEntityEdges.forEach((edge: any) => {
          const entityNode = graph.nodes.find((n: any) => n.id === edge.target)
          if (entityNode && entityNode.source === 'occrp_aleph') {
            const nodeId = entityNode.props?.entity_id || entityNode.id
            if (!occrpEntities.some(e => e.id === nodeId)) {
              // Extract name
              let entityName = entityNode.label?.replace(/^(Person|Company|Entity|Document|LegalEntity|Mention|Page|Pages|HyperText):\s*/, '') || 'Unknown'
              if (entityName === 'Unknown' || !entityName) {
                if (entityNode.props?.name) {
                  entityName = Array.isArray(entityNode.props.name) ? entityNode.props.name[0] : entityNode.props.name
                } else if (entityNode.props?.fileName) {
                  entityName = Array.isArray(entityNode.props.fileName) ? entityNode.props.fileName[0] : entityNode.props.fileName
                } else if (entityNode.props?.title) {
                  entityName = Array.isArray(entityNode.props.title) ? entityNode.props.title[0] : entityNode.props.title
                } else if (entityNode.props?.alias) {
                  entityName = Array.isArray(entityNode.props.alias) ? entityNode.props.alias[0] : entityNode.props.alias
                }
              }
              
              // Normalize country and address to arrays
              const countries = Array.isArray(entityNode.props?.country)
                ? entityNode.props.country
                : (entityNode.props?.country ? [entityNode.props.country] : []);
              const addresses = Array.isArray(entityNode.props?.address)
                ? entityNode.props.address
                : (entityNode.props?.address ? [entityNode.props.address] : []);
              const highlights = Array.isArray(entityNode.props?.highlight)
                ? entityNode.props.highlight
                : (entityNode.props?.highlight ? [entityNode.props.highlight] : []);
              
              occrpEntities.push({
                id: nodeId,
                schema: entityNode.props?.schema || 'Thing',
                name: entityName,
                collection_label: entityNode.props?.collection_label || '',
                collection_id: entityNode.props?.collection_id || '',
                country: countries,
                address: addresses,
                highlight: highlights,
                ui_link: entityNode.props?.ui_link || '',
                api_link: entityNode.props?.api_link || '',
                score: entityNode.props?.score || 0,
                properties: entityNode.props || {}
              })
            }
          }
        })
      }
      
      // Also add direct OCCRP nodes (fallback if edges don't work)
      allOccrpNodes.forEach((entityNode: any) => {
        // Check if already added
        const nodeId = entityNode.props?.entity_id || entityNode.id
        if (occrpEntities.some(e => e.id === nodeId)) return
        
        // Extract name
        let entityName = entityNode.label?.replace(/^(Person|Company|Entity|Document|LegalEntity|Mention|Page|Pages|HyperText):\s*/, '') || 'Unknown'
        if (entityName === 'Unknown' || !entityName) {
          if (entityNode.props?.name) {
            entityName = Array.isArray(entityNode.props.name) ? entityNode.props.name[0] : entityNode.props.name
          } else if (entityNode.props?.fileName) {
            entityName = Array.isArray(entityNode.props.fileName) ? entityNode.props.fileName[0] : entityNode.props.fileName
          } else if (entityNode.props?.title) {
            entityName = Array.isArray(entityNode.props.title) ? entityNode.props.title[0] : entityNode.props.title
          } else if (entityNode.props?.alias) {
            entityName = Array.isArray(entityNode.props.alias) ? entityNode.props.alias[0] : entityNode.props.alias
          }
        }
        
        // Normalize country and address to arrays
        const countries = Array.isArray(entityNode.props?.country)
          ? entityNode.props.country
          : (entityNode.props?.country ? [entityNode.props.country] : []);
        const addresses = Array.isArray(entityNode.props?.address)
          ? entityNode.props.address
          : (entityNode.props?.address ? [entityNode.props.address] : []);
        const highlights = Array.isArray(entityNode.props?.highlight)
          ? entityNode.props.highlight
          : (entityNode.props?.highlight ? [entityNode.props.highlight] : []);
        
        occrpEntities.push({
          id: nodeId,
          schema: entityNode.props?.schema || 'Thing',
          name: entityName,
          collection_label: entityNode.props?.collection_label || '',
          collection_id: entityNode.props?.collection_id || '',
          country: countries,
          address: addresses,
          highlight: highlights,
          ui_link: entityNode.props?.ui_link || '',
          api_link: entityNode.props?.api_link || '',
          score: entityNode.props?.score || 0,
          properties: entityNode.props || {}
        })
      })
      
      // Always create OCCRP data structure if we found summary nodes (even with 0 results)
      if (occrpSummaryNodes.length > 0 || occrpEntities.length > 0) {
        const totalResults = occrpSummaryNodes.length > 0 
          ? (occrpSummaryNodes[0].props?.total_results || occrpEntities.length)
          : occrpEntities.length
        
        piiData.occrpAlephData = {
          total_results: totalResults,
          results: occrpEntities
        }
        
        console.log('OCCRP Data extracted:', {
          totalResults,
          entitiesFound: occrpEntities.length,
          summaryNode: occrpSummaryNodes.length > 0 ? occrpSummaryNodes[0].props : null
        })
        
        // Detect high-value data sources
        const detectedAlerts: Array<{source: string, severity: string, icon: string, collection: string}> = []
        occrpEntities.forEach((entity: any) => {
          const collectionLabel = (entity.collection_label || '').toLowerCase()
          console.log('Checking collection for high-value sources:', {
            collection: entity.collection_label,
            collectionLower: collectionLabel
          })
          
          HIGH_VALUE_SOURCES.forEach((source) => {
            const matched = source.keywords.some(keyword => {
              const keywordLower = keyword.toLowerCase()
              const found = collectionLabel.includes(keywordLower)
              if (found) {
                console.log('High-value source matched!', {
                  source: source.label,
                  keyword: keyword,
                  collection: entity.collection_label
                })
              }
              return found
            })
            
            if (matched) {
              // Check if already added
              if (!detectedAlerts.some(a => a.source === source.label && a.collection === entity.collection_label)) {
                detectedAlerts.push({
                  source: source.label,
                  severity: source.severity,
                  icon: source.icon,
                  collection: entity.collection_label
                })
              }
            }
          })
        })
        
        console.log('Detected high-value alerts:', detectedAlerts)
        
        // Update high-value alerts state
        if (detectedAlerts.length > 0) {
          setHighValueAlerts(prev => {
            const newAlerts = [...prev]
            detectedAlerts.forEach(alert => {
              if (!newAlerts.some(a => a.source === alert.source && a.collection === alert.collection)) {
                newAlerts.push(alert)
              }
            })
            console.log('Updated high-value alerts state:', newAlerts)
            return newAlerts
          })
        }
      } else if (occrpSummaryNodes.length > 0) {
        // Even if no entities extracted, check summary node for high-value indicators
        // This handles cases where entities exist but weren't extracted properly
        const summaryNode = occrpSummaryNodes[0]
        const queryText = (summaryNode.props?.query_text || query || '').toLowerCase()
        
        // Check raw_results in summary node for high-value collections
        const rawResults = summaryNode.props?.raw_results
        if (rawResults && Array.isArray(rawResults) && rawResults.length > 0) {
          console.log('Checking raw_results for high-value sources', {
            rawResultsCount: rawResults.length
          })
          
          rawResults.forEach((entity: any) => {
            const collection = entity.collection || {}
            const collectionLabel = typeof collection === 'object' ? (collection.label || '').toLowerCase() : (collection || '').toLowerCase()
            
            HIGH_VALUE_SOURCES.forEach((source) => {
              const matched = source.keywords.some(keyword => {
                const keywordLower = keyword.toLowerCase()
                return collectionLabel.includes(keywordLower)
              })
              
              if (matched) {
                const collectionLabelDisplay = typeof collection === 'object' ? collection.label : collection
                setHighValueAlerts(prev => {
                  if (!prev.some(a => a.source === source.label && a.collection === collectionLabelDisplay)) {
                    console.log('High-value alert detected from raw_results:', {
                      source: source.label,
                      collection: collectionLabelDisplay
                    })
                    return [...prev, {
                      source: source.label,
                      severity: source.severity,
                      icon: source.icon,
                      collection: collectionLabelDisplay || 'OCCRP Aleph'
                    }]
                  }
                  return prev
                })
              }
            })
          })
        }
        
        // Also check query text for high-value keywords (fallback)
        HIGH_VALUE_SOURCES.forEach((source) => {
          const matched = source.keywords.some(keyword => queryText.includes(keyword.toLowerCase()))
          if (matched && summaryNode.props?.total_results > 0) {
            setHighValueAlerts(prev => {
              if (!prev.some(a => a.source === source.label)) {
                return [...prev, {
                  source: source.label,
                  severity: source.severity,
                  icon: source.icon,
                  collection: `OCCRP Search Results (${summaryNode.props?.total_results} results)`
                }]
              }
              return prev
            })
          }
        })
      }
      
      // Extract OpenSanctions data
      piiData.sanctionsData = null
      const sanctionsNodes = graph.nodes.filter((node: any) =>
        node.source === 'open_sanctions' &&
        node.type === 'source' &&
        node.id?.startsWith('sanctions_check:')
      )
      if (sanctionsNodes.length > 0) {
        const sNode = sanctionsNodes[0]
        piiData.sanctionsData = {
          is_sanctioned: sNode.props?.is_sanctioned === true,
          highest_score: sNode.props?.highest_score || 0,
          total_hits: sNode.props?.total_hits || 0,
          hits: sNode.props?.hits || [],
          list_coverage: sNode.props?.list_coverage || [],
        }
        if (sNode.props?.is_sanctioned === true) {
          setHighValueAlerts(prev => {
            if (!prev.some(a => a.source === 'OpenSanctions')) {
              return [...prev, {
                source: 'OpenSanctions',
                severity: 'critical',
                icon: '⛔',
                collection: `SANCTIONED — ${sNode.props?.hits?.[0]?.caption || 'match found'} (score ${(sNode.props?.highest_score || 0).toFixed(2)})`
              }]
            }
            return prev
          })
        }
      }

      // Extract Sycek Enrichment data
      piiData.sycekEnrichment = []
      const enrichmentNodes = graph.nodes.filter((node: any) => 
        (node.type === 'enrichment' || node.source === 'sycek_enrichment') &&
        node.props?.enrichment_brand === 'Sycek Enrichment'
      )
      
      enrichmentNodes.forEach((node: any) => {
        const enrichmentData: SycekEnrichmentData = {
          enrichment_brand: node.props?.enrichment_brand || 'Sycek Enrichment',
          enrichment_provider: node.props?.enrichment_provider || 'RocketReach',
          enriched_email: node.props?.enriched_email || '',
          name: node.props?.name || node.props?.profile_name || '',
          linkedin_url: node.props?.linkedin_url,
          links: node.props?.links,
          location: node.props?.location,
          current_title: node.props?.current_title,
          current_employer: node.props?.current_employer,
          current_employer_domain: node.props?.current_employer_domain,
          current_employer_website: node.props?.current_employer_website,
          current_employer_linkedin_url: node.props?.current_employer_linkedin_url,
          job_history: node.props?.job_history,
          education: node.props?.education,
          skills: node.props?.skills,
          birth_year: node.props?.birth_year,
          city: node.props?.city,
          region: node.props?.region,
          country: node.props?.country,
          country_code: node.props?.country_code,
          region_latitude: node.props?.region_latitude,
          region_longitude: node.props?.region_longitude,
          emails: node.props?.emails,
          phones: node.props?.phones,
          recommended_email: node.props?.recommended_email,
          recommended_personal_email: node.props?.recommended_personal_email,
          recommended_professional_email: node.props?.recommended_professional_email,
          current_work_email: node.props?.current_work_email,
          current_personal_email: node.props?.current_personal_email
        }
        // sycekEnrichment is initialized above, so it's safe to push
        piiData.sycekEnrichment!.push(enrichmentData)
      })
      
      // First, find all Hudson Rock stealer nodes directly (both individual and domain-based)
      const directStealerNodes = graph.nodes.filter((node: any) => 
        node.source === 'hudson_rock' && 
        node.type === 'breach' && 
        node.props?.breach_type === 'stealer_log' &&
        node.props?.stealer_family
      )
      
      directStealerNodes.forEach((node: any) => {
        // Check if this is a domain-based stealer log
        const isDomainStealer = node.id?.startsWith('stealer_domain:') || node.props?.domain
        
        piiData.stealerLogs!.push({
          stealer_family: node.props.stealer_family || 'Unknown',
          date_compromised: node.props.date_compromised || '',
          computer_name: node.props.computer_name || (isDomainStealer ? `Domain: ${node.props?.domain || query}` : ''),
          operating_system: node.props.operating_system || '',
          malware_path: node.props.malware_path || '',
          ip: node.props.ip || '',
          antiviruses: Array.isArray(node.props.antiviruses) ? node.props.antiviruses : [],
          total_corporate_services: node.props.total_corporate_services || 0,
          total_user_services: node.props.total_user_services || (isDomainStealer ? node.props?.users_affected || 0 : 0),
          top_passwords: Array.isArray(node.props.top_passwords) ? node.props.top_passwords : [],
          top_logins: Array.isArray(node.props.top_logins) ? node.props.top_logins : [],
          source: 'hudson_rock',
          // Additional fields for domain-based stealers
          infection_count: node.props?.infection_count || (isDomainStealer ? 1 : undefined),
          users_affected: node.props?.users_affected || (isDomainStealer ? node.props?.total_user_services || 0 : undefined),
          employees_affected: node.props?.employees_affected || (isDomainStealer ? node.props?.total_corporate_services || 0 : undefined),
          domain: node.props?.domain || (isDomainStealer ? query : undefined)
        })
      })
      
      // Also check for nodes connected to Hudson Rock summary nodes via edges
      if (graph.edges) {
        const hudsonSummaryNodes = graph.nodes.filter((node: any) => 
          node.source === 'hudson_rock' && 
          node.type === 'source' && 
          (node.label?.includes('Stealer Infection') || 
           node.label?.includes('Hudson Rock') ||
           node.label?.includes('Stealer Family') ||
           node.id?.includes('hudson_rock_domain_stealers'))
        )
        
        hudsonSummaryNodes.forEach((summaryNode: any) => {
          // Find connected stealer nodes via contains_stealer edges
          const connectedStealerEdges = graph.edges.filter((edge: any) => 
            edge.source === summaryNode.id && 
            (edge.relation === 'contains_stealer' || edge.relation === 'has_stealer_infections')
          )
          
          connectedStealerEdges.forEach((edge: any) => {
            const stealerNode = graph.nodes.find((n: any) => n.id === edge.target)
            if (stealerNode && stealerNode.props?.stealer_family) {
              // Check if we already added this stealer
              const alreadyAdded = piiData.stealerLogs!.some(s => 
                s.computer_name === stealerNode.props.computer_name &&
                s.date_compromised === stealerNode.props.date_compromised &&
                s.stealer_family === stealerNode.props.stealer_family
              )
              
              if (!alreadyAdded) {
                piiData.stealerLogs!.push({
                  stealer_family: stealerNode.props.stealer_family || 'Unknown',
                  date_compromised: stealerNode.props.date_compromised || '',
                  computer_name: stealerNode.props.computer_name || '',
                  operating_system: stealerNode.props.operating_system || '',
                  malware_path: stealerNode.props.malware_path || '',
                  ip: stealerNode.props.ip || '',
                  antiviruses: Array.isArray(stealerNode.props.antiviruses) ? stealerNode.props.antiviruses : [],
                  total_corporate_services: stealerNode.props.total_corporate_services || 0,
                  total_user_services: stealerNode.props.total_user_services || 0,
                  top_passwords: Array.isArray(stealerNode.props.top_passwords) ? stealerNode.props.top_passwords : [],
                  top_logins: Array.isArray(stealerNode.props.top_logins) ? stealerNode.props.top_logins : [],
                  source: 'hudson_rock'
                })
              }
            }
          })
        })
      }
      
      // Extract map locations from OCCRP entities (prioritize OCCRP locations)
      if (piiData.occrpAlephData && piiData.occrpAlephData.results) {
        piiData.occrpAlephData.results.forEach((entity: any) => {
          // Ensure countries is always an array
          const countries = Array.isArray(entity.country) 
            ? entity.country 
            : (entity.country ? [entity.country] : [])
          // Ensure addresses is always an array
          const addresses = Array.isArray(entity.address)
            ? entity.address
            : (entity.address ? [entity.address] : [])
          
          // For each country mentioned, add a location
          countries.forEach((country: string) => {
            if (country && typeof country === 'string') {
              // Use country name to get approximate coordinates (centroid of country)
              // This is a simple mapping - in production, use a geocoding service
              const countryCoords: Record<string, { lat: number; lng: number }> = {
                'india': { lat: 20.5937, lng: 78.9629 },
                'united states': { lat: 37.0902, lng: -95.7129 },
                'united kingdom': { lat: 55.3781, lng: -3.4360 },
                'russia': { lat: 61.5240, lng: 105.3188 },
                'china': { lat: 35.8617, lng: 104.1954 },
                'germany': { lat: 51.1657, lng: 10.4515 },
                'france': { lat: 46.2276, lng: 2.2137 },
                'spain': { lat: 40.4637, lng: -3.7492 },
                'italy': { lat: 41.8719, lng: 12.5674 },
                'brazil': { lat: -14.2350, lng: -51.9253 },
                'mexico': { lat: 23.6345, lng: -102.5528 },
                'canada': { lat: 56.1304, lng: -106.3468 },
                'australia': { lat: -25.2744, lng: 133.7751 },
                'japan': { lat: 36.2048, lng: 138.2529 },
                'south korea': { lat: 35.9078, lng: 127.7669 },
                'singapore': { lat: 1.3521, lng: 103.8198 },
                'uae': { lat: 23.4241, lng: 53.8478 },
                'united arab emirates': { lat: 23.4241, lng: 53.8478 },
                'switzerland': { lat: 46.8182, lng: 8.2275 },
                'netherlands': { lat: 52.1326, lng: 5.2913 },
                'belgium': { lat: 50.5039, lng: 4.4699 },
                'sweden': { lat: 60.1282, lng: 18.6435 },
                'norway': { lat: 60.4720, lng: 8.4689 },
                'denmark': { lat: 56.2639, lng: 9.5018 },
                'poland': { lat: 51.9194, lng: 19.1451 },
                'turkey': { lat: 38.9637, lng: 35.2433 },
                'saudi arabia': { lat: 23.8859, lng: 45.0792 },
                'israel': { lat: 31.0461, lng: 34.8516 },
                'south africa': { lat: -30.5595, lng: 22.9375 },
                'argentina': { lat: -38.4161, lng: -63.6167 },
                'chile': { lat: -35.6751, lng: -71.5430 },
                'colombia': { lat: 4.5709, lng: -74.2973 },
                'peru': { lat: -9.1900, lng: -75.0152 },
                'venezuela': { lat: 6.4238, lng: -66.5897 },
                'thailand': { lat: 15.8700, lng: 100.9925 },
                'malaysia': { lat: 4.2105, lng: 101.9758 },
                'indonesia': { lat: -0.7893, lng: 113.9213 },
                'philippines': { lat: 12.8797, lng: 121.7740 },
                'vietnam': { lat: 14.0583, lng: 108.2772 },
                'pakistan': { lat: 30.3753, lng: 69.3451 },
                'bangladesh': { lat: 23.6850, lng: 90.3563 },
                'sri lanka': { lat: 7.8731, lng: 80.7718 },
                'nepal': { lat: 28.3949, lng: 84.1240 },
                'egypt': { lat: 26.0975, lng: 31.2357 },
                'nigeria': { lat: 9.0820, lng: 8.6753 },
                'kenya': { lat: -0.0236, lng: 37.9062 },
                'ghana': { lat: 7.9465, lng: -1.0232 },
                'morocco': { lat: 31.7917, lng: -7.0926 },
                'tunisia': { lat: 33.8869, lng: 9.5375 },
                'algeria': { lat: 28.0339, lng: 1.6596 },
                'libya': { lat: 26.3351, lng: 17.2283 },
                'sudan': { lat: 12.8628, lng: 30.2176 },
                'ethiopia': { lat: 9.1450, lng: 38.7667 },
                'uganda': { lat: 1.3733, lng: 32.2903 },
                'tanzania': { lat: -6.3690, lng: 34.8888 },
                'zimbabwe': { lat: -19.0154, lng: 29.1549 },
                'mozambique': { lat: -18.6657, lng: 35.5296 },
                'angola': { lat: -11.2027, lng: 17.8739 },
                'congo': { lat: -0.2280, lng: 15.8277 },
                'cameroon': { lat: 7.3697, lng: 12.3547 },
                'ivory coast': { lat: 7.5400, lng: -5.5471 },
                'senegal': { lat: 14.4974, lng: -14.4524 },
                'mali': { lat: 17.5707, lng: -3.9962 },
                'burkina faso': { lat: 12.2383, lng: -1.5616 },
                'niger': { lat: 17.6078, lng: 8.0817 },
                'chad': { lat: 15.4542, lng: 18.7322 },
                'central african republic': { lat: 6.6111, lng: 20.9394 },
                'democratic republic of the congo': { lat: -4.0383, lng: 21.7587 },
                'rwanda': { lat: -1.9441, lng: 29.8739 },
                'burundi': { lat: -3.3731, lng: 29.9189 },
                'eritrea': { lat: 15.1794, lng: 39.7823 },
                'djibouti': { lat: 11.8251, lng: 42.5903 },
                'somalia': { lat: 5.1521, lng: 46.1996 },
                'mauritania': { lat: 21.0079, lng: -10.9408 },
                'gambia': { lat: 13.4432, lng: -15.3101 },
                'guinea-bissau': { lat: 11.8037, lng: -15.1804 },
                'guinea': { lat: 9.9456, lng: -9.6966 },
                'sierra leone': { lat: 8.4606, lng: -11.7799 },
                'liberia': { lat: 6.4281, lng: -9.4295 },
                'togo': { lat: 8.6195, lng: 0.8248 },
                'benin': { lat: 9.3077, lng: 2.3158 },
                'gabon': { lat: -0.8037, lng: 11.6094 },
                'equatorial guinea': { lat: 1.6508, lng: 10.2679 },
                'sao tome and principe': { lat: 0.1864, lng: 6.6131 },
                'cape verde': { lat: 16.5388, lng: -24.0132 },
                'comoros': { lat: -11.6455, lng: 43.3333 },
                'seychelles': { lat: -4.6796, lng: 55.4920 },
                'mauritius': { lat: -20.3484, lng: 57.5522 },
                'madagascar': { lat: -18.7669, lng: 46.8691 },
                'malawi': { lat: -13.2543, lng: 34.3015 },
                'zambia': { lat: -13.1339, lng: 27.8493 },
                'botswana': { lat: -22.3285, lng: 24.6849 },
                'namibia': { lat: -22.9576, lng: 18.4904 },
                'lesotho': { lat: -29.6100, lng: 28.2336 },
                'eswatini': { lat: -26.5225, lng: 31.4659 },
                'afghanistan': { lat: 33.9391, lng: 67.7100 },
                'iran': { lat: 32.4279, lng: 53.6880 },
                'iraq': { lat: 33.2232, lng: 43.6793 },
                'syria': { lat: 34.8021, lng: 38.9968 },
                'lebanon': { lat: 33.8547, lng: 35.8623 },
                'jordan': { lat: 30.5852, lng: 36.2384 },
                'yemen': { lat: 15.5527, lng: 48.5164 },
                'oman': { lat: 21.4735, lng: 55.9754 },
                'kuwait': { lat: 29.3117, lng: 47.4818 },
                'qatar': { lat: 25.3548, lng: 51.1839 },
                'bahrain': { lat: 26.0667, lng: 50.5577 },
                'cyprus': { lat: 35.1264, lng: 33.4299 },
                'greece': { lat: 39.0742, lng: 21.8243 },
                'portugal': { lat: 39.3999, lng: -8.2245 },
                'ireland': { lat: 53.4129, lng: -8.2439 },
                'iceland': { lat: 64.9631, lng: -19.0208 },
                'finland': { lat: 61.9241, lng: 25.7482 },
                'estonia': { lat: 58.5953, lng: 25.0136 },
                'latvia': { lat: 56.8796, lng: 24.6032 },
                'lithuania': { lat: 55.1694, lng: 23.8813 },
                'belarus': { lat: 53.7098, lng: 27.9534 },
                'ukraine': { lat: 48.3794, lng: 31.1656 },
                'moldova': { lat: 47.4116, lng: 28.3699 },
                'romania': { lat: 45.9432, lng: 24.9668 },
                'bulgaria': { lat: 42.7339, lng: 25.4858 },
                'serbia': { lat: 44.0165, lng: 21.0059 },
                'croatia': { lat: 45.1000, lng: 15.2000 },
                'bosnia and herzegovina': { lat: 43.9159, lng: 17.6791 },
                'slovenia': { lat: 46.1512, lng: 14.9955 },
                'slovakia': { lat: 48.6690, lng: 19.6990 },
                'czech republic': { lat: 49.8175, lng: 15.4730 },
                'hungary': { lat: 47.1625, lng: 19.5033 },
                'austria': { lat: 47.5162, lng: 14.5501 },
                'luxembourg': { lat: 49.8153, lng: 6.1296 },
                'monaco': { lat: 43.7384, lng: 7.4246 },
                'liechtenstein': { lat: 47.1660, lng: 9.5554 },
                'san marino': { lat: 43.9424, lng: 12.4578 },
                'vatican city': { lat: 41.9029, lng: 12.4534 },
                'andorra': { lat: 42.5462, lng: 1.6016 },
                'malta': { lat: 35.9375, lng: 14.3754 },
                'albania': { lat: 41.1533, lng: 20.1683 },
                'north macedonia': { lat: 41.6086, lng: 21.7453 },
                'montenegro': { lat: 42.7087, lng: 19.3744 },
                'kosovo': { lat: 42.6026, lng: 20.9030 },
                'georgia': { lat: 42.3154, lng: 43.3569 },
                'armenia': { lat: 40.0691, lng: 45.0382 },
                'azerbaijan': { lat: 40.1431, lng: 47.5769 },
                'kazakhstan': { lat: 48.0196, lng: 66.9237 },
                'uzbekistan': { lat: 41.3775, lng: 64.5853 },
                'turkmenistan': { lat: 38.9697, lng: 59.5563 },
                'kyrgyzstan': { lat: 41.2044, lng: 74.7661 },
                'tajikistan': { lat: 38.8610, lng: 71.2761 },
                'mongolia': { lat: 46.8625, lng: 103.8467 },
                'north korea': { lat: 40.3399, lng: 127.5101 },
                'myanmar': { lat: 21.9162, lng: 95.9560 },
                'laos': { lat: 19.8563, lng: 102.4955 },
                'cambodia': { lat: 12.5657, lng: 104.9910 },
                'brunei': { lat: 4.5353, lng: 114.7277 },
                'east timor': { lat: -8.8742, lng: 125.7275 },
                'papua new guinea': { lat: -6.3150, lng: 143.9555 },
                'fiji': { lat: -16.5783, lng: 179.4144 },
                'new zealand': { lat: -40.9006, lng: 174.8860 },
                'samoa': { lat: -13.7590, lng: -172.1046 },
                'tonga': { lat: -21.1789, lng: -175.1982 },
                'vanuatu': { lat: -15.3767, lng: 166.9592 },
                'solomon islands': { lat: -9.6457, lng: 160.1562 },
                'palau': { lat: 7.5150, lng: 134.5825 },
                'micronesia': { lat: 7.4256, lng: 150.5508 },
                'marshall islands': { lat: 7.1315, lng: 171.1845 },
                'kiribati': { lat: -3.3704, lng: -168.7340 },
                'nauru': { lat: -0.5228, lng: 166.9315 },
                'tuvalu': { lat: -7.1095, lng: 177.6493 },
                'cuba': { lat: 21.5218, lng: -77.7812 },
                'jamaica': { lat: 18.1096, lng: -77.2975 },
                'haiti': { lat: 18.9712, lng: -72.2852 },
                'dominican republic': { lat: 18.7357, lng: -70.1627 },
                'puerto rico': { lat: 18.2208, lng: -66.5901 },
                'trinidad and tobago': { lat: 10.6918, lng: -61.2225 },
                'barbados': { lat: 13.1939, lng: -59.5432 },
                'bahamas': { lat: 25.0343, lng: -77.3963 },
                'belize': { lat: 17.1899, lng: -88.4976 },
                'costa rica': { lat: 9.7489, lng: -83.7534 },
                'panama': { lat: 8.5380, lng: -80.7821 },
                'nicaragua': { lat: 12.2650, lng: -85.2072 },
                'honduras': { lat: 15.2000, lng: -86.2419 },
                'el salvador': { lat: 13.7942, lng: -88.8965 },
                'guatemala': { lat: 15.7835, lng: -90.2308 },
                'ecuador': { lat: -1.8312, lng: -78.1834 },
                'bolivia': { lat: -16.2902, lng: -63.5887 },
                'paraguay': { lat: -23.4425, lng: -58.4438 },
                'uruguay': { lat: -32.5228, lng: -55.7658 },
                'guyana': { lat: 4.8604, lng: -58.9302 },
                'suriname': { lat: 3.9193, lng: -56.0278 },
                'french guiana': { lat: 3.9339, lng: -53.1258 }
              }
              
              const countryLower = country.toLowerCase().trim()
              const coords = countryCoords[countryLower]
              
              if (coords) {
                // Check if this location already exists
                const alreadyExists = piiData.mapLocations!.some(loc => 
                  Math.abs(loc.lat - coords.lat) < 0.01 && 
                  Math.abs(loc.lng - coords.lng) < 0.01 &&
                  loc.country?.toLowerCase() === countryLower
                )
                
                if (!alreadyExists) {
                  const addressStr = addresses.length > 0 ? addresses[0] : ''
                  piiData.mapLocations!.push({
                    lat: coords.lat,
                    lng: coords.lng,
                    address: addressStr,
                    country: country,
                    source: 'occrp_aleph',
                    label: `${entity.name || 'Entity'} - ${country}`
                  })
                }
              }
            }
          })
        })
      }
      
      // Extract map locations from all nodes
      graph.nodes.forEach((node: any) => {
        const props = node.props || {}
        
        // Helper function to extract coordinates from various formats
        const extractCoords = (): { lat: number; lng: number } | null => {
          // Try direct latitude/longitude fields (various case combinations)
          let lat = parseFloat(props.Latitude || props.latitude || props.lat || props.Lat || '')
          let lng = parseFloat(props.Longitude || props.longitude || props.lng || props.Lng || '')
          
          if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
            return { lat, lng }
          }
          
          // Try GPS string format
          if (props.gps) {
            const gpsMatch = props.gps.toString().match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/)
            if (gpsMatch) {
              lat = parseFloat(gpsMatch[1])
              lng = parseFloat(gpsMatch[2])
              if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                return { lat, lng }
              }
            }
          }
          
          // Try coordinates in label (sometimes coordinates are in the label)
          if (node.label) {
            const labelMatch = node.label.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/)
            if (labelMatch) {
              lat = parseFloat(labelMatch[1])
              lng = parseFloat(labelMatch[2])
              if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                return { lat, lng }
              }
            }
          }
          
          return null
        }
        
        const coords = extractCoords()
        
        // If we have coordinates, add location (check for duplicates first)
        if (coords) {
          // Check if this location already exists (within small tolerance)
          const alreadyExists = piiData.mapLocations!.some(loc => 
            Math.abs(loc.lat - coords.lat) < 0.0001 && 
            Math.abs(loc.lng - coords.lng) < 0.0001
          )
          
          if (!alreadyExists) {
            const address = props.Address || props.address || props.FullAddress || props.full_address || ''
            const city = props.City || props.city || ''
            const country = props.Country || props.country || ''
            
            piiData.mapLocations!.push({
              lat: coords.lat,
              lng: coords.lng,
              address: address,
              city: city,
              country: country,
              source: node.source || (node.type === 'location' ? 'location' : 'breach_data'),
              label: node.label || address || `${coords.lat}, ${coords.lng}` || 'Location'
            })
          }
        }
      })
      
      // Remove duplicate locations (same lat/lng)
      const locationMap = new Map<string, { lat: number; lng: number; address?: string; city?: string; country?: string; source: string; label: string }>()
      piiData.mapLocations!.forEach(loc => {
        const key = `${loc.lat.toFixed(4)},${loc.lng.toFixed(4)}`
        if (!locationMap.has(key)) {
          locationMap.set(key, loc)
        }
      })
      piiData.mapLocations = Array.from(locationMap.values())
    }
    
    console.log('Comprehensive PII extracted:', {
      breachSources: piiData.breachSources.length,
      photos: piiData.photos.length,
      webResults: piiData.webResults.length,
      personalInfo: Object.keys(piiData.personalInfo).length,
      contactDetails: Object.keys(piiData.contactDetails).length,
      stealerLogs: piiData.stealerLogs?.length || 0,
      mapLocations: piiData.mapLocations?.length || 0,
      totalNodes: graph?.nodes?.length || 0,
      imageNodes: graph?.nodes?.filter((n: any) => n.type === 'image').length || 0,
      webResultNodes: graph?.nodes?.filter((n: any) => n.type === 'web_result').length || 0,
      hudsonRockNodes: graph?.nodes?.filter((n: any) => n.source === 'hudson_rock').length || 0
    })
    return piiData
  }

  if (loading) {
    return <ScaryLoader message="Analyzing intelligence data..." />
  }

  if (!profileData) return null

  // Filter breach sources based on selected records
  const getFilteredBreachSources = () => {
    if (selectedRecordIds.size === 0) {
      return profileData.breachSources
    }
    
    // Group selected records by breach index
    const selectedBreachIndices = new Set<number>()
    const selectedRecordMap = new Map<number, Set<number>>()
    
    selectedRecordIds.forEach(recordId => {
      const match = recordId.match(/breach_(\d+)_record_(\d+)/)
      if (match) {
        const breachIndex = parseInt(match[1])
        const recordIndex = parseInt(match[2])
        selectedBreachIndices.add(breachIndex)
        
        if (!selectedRecordMap.has(breachIndex)) {
          selectedRecordMap.set(breachIndex, new Set())
        }
        selectedRecordMap.get(breachIndex)!.add(recordIndex)
      }
    })
    
    // Filter and modify breach sources
    return profileData.breachSources
      .map((breach: any, breachIndex: number) => {
        if (!selectedBreachIndices.has(breachIndex)) {
          return null
        }
        
        const selectedRecordIndices = selectedRecordMap.get(breachIndex) || new Set()
        
        // If breach has allRecords, filter them
        if (breach.allRecords && Array.isArray(breach.allRecords)) {
          const filteredRecords = breach.allRecords.filter((_: any, recordIndex: number) => 
            selectedRecordIndices.has(recordIndex)
          )
          
          return {
            ...breach,
            allRecords: filteredRecords,
            records: filteredRecords.length.toString(),
            numOfResults: filteredRecords.length
          }
        }
        
        // If no allRecords but breach is selected, include it
        if (selectedRecordIndices.has(0)) {
          return breach
        }
        
        return null
      })
      .filter((b: any) => b !== null)
  }

  return (
    <div className="fixed top-16 left-0 md:left-64 right-0 h-[calc(100dvh-4rem)] bg-[#09090f] text-gray-100 flex flex-col overflow-hidden z-10">
      {/* Record Selection Modal */}
      {showRecordSelectionModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 border-2 border-cyan-500 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="bg-gradient-to-r from-cyan-900 to-blue-900 p-4 border-b border-cyan-500">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-cyan-400 font-mono text-xl font-bold mb-1">
                    🔍 SELECT RELEVANT RECORDS
                  </h2>
                  <p className="text-cyan-300 text-sm">
                    Multiple records found for "{query}". Select which records belong to your target.
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (selectedRecordIds.size === 0) {
                      if (confirm('No records selected. Continue with all records?')) {
                        setShowRecordSelectionModal(false)
                      }
                    } else {
                      setShowRecordSelectionModal(false)
                    }
                  }}
                  className="text-cyan-400 hover:text-cyan-300 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-cyan-400 font-mono text-sm">
                  {allRecords.length} total records found • {selectedRecordIds.size} selected
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      if (selectedRecordIds.size === allRecords.length) {
                        setSelectedRecordIds(new Set())
                      } else {
                        setSelectedRecordIds(new Set(allRecords.map(r => r.id)))
                      }
                    }}
                    className="px-3 py-1 text-xs font-mono rounded border border-cyan-500 text-cyan-400 hover:bg-cyan-900/20"
                  >
                    {selectedRecordIds.size === allRecords.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                {allRecords.map((recordData) => {
                  const isSelected = selectedRecordIds.has(recordData.id)
                  const { keyInfo } = recordData
                  
                  return (
                    <div
                      key={recordData.id}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-cyan-500 bg-cyan-900/20'
                          : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                      }`}
                      onClick={() => {
                        setSelectedRecordIds(prev => {
                          const newSet = new Set(prev)
                          if (isSelected) {
                            newSet.delete(recordData.id)
                          } else {
                            newSet.add(recordData.id)
                          }
                          return newSet
                        })
                      }}
                    >
                      <div className="flex items-start space-x-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 w-5 h-5 text-cyan-600 bg-gray-700 border-gray-600 rounded focus:ring-cyan-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Badge className="bg-red-600 text-white text-xs font-mono">
                                {recordData.breachSource}
                              </Badge>
                              {isSelected && (
                                <Badge className="bg-cyan-600 text-white text-xs font-mono">
                                  SELECTED
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            {keyInfo.email && (
                              <div>
                                <div className="text-gray-400 text-xs font-mono mb-1">Email</div>
                                <div className="text-white text-sm">{keyInfo.email}</div>
                              </div>
                            )}
                            {keyInfo.fullName && (
                              <div>
                                <div className="text-gray-400 text-xs font-mono mb-1">Full Name</div>
                                <div className="text-white text-sm">{keyInfo.fullName}</div>
                              </div>
                            )}
                            {keyInfo.dob && (
                              <div>
                                <div className="text-gray-400 text-xs font-mono mb-1">Date of Birth</div>
                                <div className="text-white text-sm">{keyInfo.dob}</div>
                              </div>
                            )}
                            {keyInfo.phone && (
                              <div>
                                <div className="text-gray-400 text-xs font-mono mb-1">Phone</div>
                                <div className="text-white text-sm">{keyInfo.phone}</div>
                              </div>
                            )}
                            {keyInfo.address && (
                              <div>
                                <div className="text-gray-400 text-xs font-mono mb-1">Address</div>
                                <div className="text-white text-sm">{keyInfo.address}</div>
                              </div>
                            )}
                            {keyInfo.city && (
                              <div>
                                <div className="text-gray-400 text-xs font-mono mb-1">City</div>
                                <div className="text-white text-sm">{keyInfo.city}</div>
                              </div>
                            )}
                            {keyInfo.state && (
                              <div>
                                <div className="text-gray-400 text-xs font-mono mb-1">State</div>
                                <div className="text-white text-sm">{keyInfo.state}</div>
                              </div>
                            )}
                            {keyInfo.country && (
                              <div>
                                <div className="text-gray-400 text-xs font-mono mb-1">Country</div>
                                <div className="text-white text-sm">{keyInfo.country}</div>
                              </div>
                            )}
                            {keyInfo.ip && (
                              <div>
                                <div className="text-gray-400 text-xs font-mono mb-1">IP Address</div>
                                <div className="text-white text-sm">{keyInfo.ip}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            
            <div className="bg-gray-800 border-t border-cyan-500 p-4 flex items-center justify-between">
              <div className="text-cyan-300 text-sm font-mono">
                {selectedRecordIds.size === 0 
                  ? '⚠️ No records selected - all records will be analyzed'
                  : `✓ ${selectedRecordIds.size} record${selectedRecordIds.size !== 1 ? 's' : ''} selected for analysis`
                }
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    if (selectedRecordIds.size === 0) {
                      if (confirm('No records selected. Continue with all records?')) {
                        setShowRecordSelectionModal(false)
                      }
                    } else {
                      setShowRecordSelectionModal(false)
                    }
                  }}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 font-mono text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Update profileData with filtered records
                    if (selectedRecordIds.size > 0) {
                      const filtered = getFilteredBreachSources()
                      setProfileData({
                        ...profileData,
                        breachSources: filtered
                      })
                    }
                    setShowRecordSelectionModal(false)
                    // Auto-fetch AI insights after selection
                    setTimeout(() => {
                      fetchAIInsights(false)
                    }, 500)
                  }}
                  className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 font-mono text-sm font-bold"
                >
                  {selectedRecordIds.size === 0 ? 'Analyze All Records' : `Analyze ${selectedRecordIds.size} Selected`}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      {/* Sycek Header */}
      <header className="shrink-0 bg-[#07070f] border-b border-purple-900/25 px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Logo */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-purple-900 flex items-center justify-center shadow-lg shadow-purple-900/50">
            <span className="text-white font-black text-sm font-mono">S</span>
          </div>
          <div>
            <div className="text-white font-mono font-black text-xs tracking-widest">SYCEK</div>
            <div className="text-purple-500 font-mono text-[9px] tracking-widest leading-none">BreachINT</div>
          </div>
        </div>

        <div className="h-6 w-px bg-purple-900/40 shrink-0" />

        {/* Target */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-gray-600 font-mono text-[10px] uppercase tracking-widest shrink-0">Target</span>
          <span className="text-white font-mono text-sm font-bold truncate max-w-[220px]">{query}</span>
          <span className="px-1.5 py-0.5 rounded border border-purple-800/50 bg-purple-950/40 text-purple-400 font-mono text-[9px] font-bold tracking-widest shrink-0">TLP:AMBER</span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Action buttons */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={goBackToSearch}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0e0e1a] border border-purple-900/40 text-gray-300 hover:text-white hover:border-purple-700/60 font-mono text-xs transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>

          <button
            onClick={async () => {
              if (onRerunSearch) {
                setRerunningSearch(true)
                try { await onRerunSearch(query, true) }
                catch (error) { console.error('Failed to re-run search:', error) }
                finally { setRerunningSearch(false) }
              }
            }}
            disabled={rerunningSearch || !onRerunSearch}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0e0e1a] border border-purple-900/40 text-gray-300 hover:text-white hover:border-purple-700/60 disabled:opacity-40 font-mono text-xs transition-colors"
          >
            {rerunningSearch ? (
              <div className="w-3.5 h-3.5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {rerunningSearch ? 'Running...' : 'Re-run'}
          </button>

          <button
            onClick={handleExportPDF}
            disabled={exportingPDF}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0e0e1a] border border-purple-900/40 text-gray-300 hover:text-white hover:border-purple-700/60 disabled:opacity-40 font-mono text-xs transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            {exportingPDF ? 'Exporting...' : 'Export PDF'}
          </button>

          <div className="h-5 w-px bg-purple-900/40" />

          <button
            onClick={() => setShowRecentSearches(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0e0e1a] border border-purple-900/40 text-gray-300 hover:text-white hover:border-purple-700/60 font-mono text-xs transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Recent
          </button>

          <button
            onClick={() => setShowCombineDialog(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0e0e1a] border border-purple-900/40 text-gray-300 hover:text-white hover:border-purple-700/60 font-mono text-xs transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            Combine
          </button>

          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-green-400 font-mono text-[10px] tracking-widest">LIVE</span>
          </div>
        </div>
      </header>


      {/* Enterprise 3-column body */}
      <div className="flex-1 overflow-hidden flex min-h-0">

        {/* Left Sidebar */}
        <aside className={`flex-none h-full border-r border-purple-900/25 bg-[#07070f] flex flex-col transition-all duration-200 ${leftSidebarOpen ? 'w-[220px] lg:w-[260px] overflow-y-auto gap-3 p-3' : 'w-8 overflow-hidden items-center pt-3'}`}>
          {/* Collapse toggle */}
          <button
            onClick={() => setLeftSidebarOpen(v => !v)}
            className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-purple-400 hover:text-white hover:bg-purple-900/40 transition-colors"
            title={leftSidebarOpen ? 'Collapse panel' : 'Expand panel'}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              {leftSidebarOpen
                ? <path d="M8 2L4 6L8 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                : <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              }
            </svg>
          </button>
          {leftSidebarOpen && <>

          {/* Caller ID card */}
          {callerIdData?.status && callerIdData?.data && (
            <div className="bg-[#0e0e1a] border border-cyan-900/50 rounded-xl p-3">
              <div className="text-cyan-500 font-mono text-[9px] uppercase tracking-widest mb-2">Caller ID — Phone Intelligence</div>
              <div className="text-white font-semibold text-sm">{callerIdData.data.fullName || '—'}</div>
              {callerIdData.data.otherNames && callerIdData.data.otherNames.length > 0 && (
                <div className="mt-1 text-cyan-300/70 text-xs font-mono">
                  {callerIdData.data.otherNames.map((n: { name: string }) => n.name).join(', ')}
                </div>
              )}
              {callerIdData.data.images && callerIdData.data.images.length > 0 && (
                <div className="flex gap-2 mt-2">
                  {callerIdData.data.images.slice(0, 3).map((img: { id?: string; pictures?: { '200'?: string; '600'?: string } }, i: number) => {
                    const url = img.pictures?.['200'] || img.pictures?.['600']
                    return url ? (
                      <a key={img.id || i} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt="Profile" className="w-10 h-10 rounded object-cover border border-cyan-700/50 hover:border-cyan-400" />
                      </a>
                    ) : null
                  })}
                  <span className="text-green-400 font-mono text-[9px] self-end pb-0.5">Facebook linked</span>
                </div>
              )}
            </div>
          )}

          {/* Compact stats grid */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#0e0e1a] border border-purple-900/30 rounded-xl p-3">
              <div className="text-purple-400 font-mono text-[9px] uppercase tracking-widest mb-1">Target</div>
              <div className="text-white font-mono text-xs font-bold truncate">{query}</div>
            </div>
            <div className="bg-[#0e0e1a] border border-red-900/30 rounded-xl p-3 text-center">
              <div className="text-red-400 font-mono text-[9px] uppercase tracking-widest mb-1">Breaches</div>
              <div className="text-red-300 font-mono text-2xl font-black leading-none">{profileData.breachSources.length}</div>
            </div>
            <div className={`bg-[#0e0e1a] border rounded-xl p-3 text-center ${highValueAlerts.length > 0 ? 'border-red-700/60' : 'border-amber-900/30'}`}>
              <div className={`font-mono text-[9px] uppercase tracking-widest mb-1 ${highValueAlerts.length > 0 ? 'text-red-400' : 'text-amber-400'}`}>Exposure</div>
              <div className={`font-mono text-[10px] font-black ${highValueAlerts.length > 0 ? 'text-red-300' : 'text-amber-300'}`}>{highValueAlerts.length > 0 ? 'CRITICAL' : 'HIGH RISK'}</div>
            </div>
            <div className="bg-[#0e0e1a] border border-purple-900/30 rounded-xl p-3 text-center">
              <div className="text-purple-400 font-mono text-[9px] uppercase tracking-widest mb-1">Status</div>
              <div className="flex items-center justify-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
                <span className="text-purple-300 font-mono text-[10px] font-black">ACTIVE</span>
              </div>
            </div>
          </div>

          {/* Compact Profile Summary */}
          <div className="bg-[#0e0e1a] border border-purple-900/30 rounded-xl overflow-hidden">
            <div className="px-3 py-2 border-b border-purple-900/20">
              <span className="text-purple-400 font-mono text-[9px] uppercase tracking-widest">Profile Summary</span>
            </div>
            <div className="divide-y divide-purple-900/15">
              {/* Always show query as the primary identifier */}
              <div className="px-3 py-2 flex items-center gap-2">
                {profileData.photos && profileData.photos.length > 0 ? (
                  <img src={profileData.photos[0].url} alt="" className="w-9 h-9 rounded-lg object-cover border border-purple-900/40 shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-purple-950/50 border border-purple-800/40 flex items-center justify-center shrink-0">
                    <span className="text-purple-400 font-mono text-sm font-black">{query.charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-white font-mono text-xs font-bold truncate">{profileData.personalInfo?.fullName || query}</div>
                  {profileData.personalInfo?.fullName && <div className="text-gray-500 font-mono text-[9px] truncate">{query}</div>}
                </div>
              </div>
              {profileData.personalInfo?.dob && (
                <div className="px-3 py-2">
                  <div className="text-gray-500 font-mono text-[9px] mb-0.5">Date of Birth</div>
                  <div className="text-white font-mono text-xs truncate">{profileData.personalInfo.dob}</div>
                </div>
              )}
              {/* Email — show from contactDetails OR if query is email */}
              {(profileData.contactDetails?.email || query.includes('@')) && (
                <div className="px-3 py-2">
                  <div className="text-gray-500 font-mono text-[9px] mb-0.5">Email</div>
                  <div className="text-blue-300 font-mono text-xs truncate">{profileData.contactDetails?.email || query}</div>
                </div>
              )}
              {profileData.contactDetails?.phone && profileData.contactDetails.phone !== query && (
                <div className="px-3 py-2">
                  <div className="text-gray-500 font-mono text-[9px] mb-0.5">Phone</div>
                  <div className="text-green-300 font-mono text-xs truncate">{profileData.contactDetails.phone}</div>
                </div>
              )}
              {/* Phone as query */}
              {!profileData.contactDetails?.phone && /^\+?[\d\s\-().]{7,}$/.test(query) && (
                <div className="px-3 py-2">
                  <div className="text-gray-500 font-mono text-[9px] mb-0.5">Phone</div>
                  <div className="text-green-300 font-mono text-xs truncate">{query}</div>
                </div>
              )}
              {profileData.addresses?.city && (
                <div className="px-3 py-2">
                  <div className="text-gray-500 font-mono text-[9px] mb-0.5">Location</div>
                  <div className="text-white font-mono text-xs truncate">
                    {[profileData.addresses.city, profileData.addresses.state, profileData.addresses.country].filter(Boolean).join(', ')}
                  </div>
                </div>
              )}
              {profileData.governmentIDs && Object.keys(profileData.governmentIDs).filter(k => (profileData.governmentIDs as any)[k]).length > 0 && (
                <div className="px-3 py-2">
                  <div className="text-gray-500 font-mono text-[9px] mb-0.5">Gov ID fragments</div>
                  <div className="text-amber-300 font-mono text-xs">{Object.keys(profileData.governmentIDs).filter(k => (profileData.governmentIDs as any)[k]).length} found</div>
                </div>
              )}
              {profileData.socialMedia && Object.values(profileData.socialMedia).some(Boolean) && (
                <div className="px-3 py-2">
                  <div className="text-gray-500 font-mono text-[9px] mb-1">Social Profiles</div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(profileData.socialMedia).filter(([,v]) => v).map(([k]) => (
                      <span key={k} className="px-1.5 py-0.5 rounded bg-purple-950/50 border border-purple-800/40 text-purple-300 font-mono text-[9px]">{k}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          </>}
        </aside>

        {/* Center Column */}
        <main className="flex-1 overflow-hidden flex flex-col min-w-0">
          {/* Intelligence Brief — always visible, extracted from graph.metadata */}
          {graph?.metadata?.intelligence_brief && (() => {
            const brief = graph.metadata.intelligence_brief as IntelligenceBriefData
            return (
              <div className="shrink-0 px-4 pt-4 pb-2">
                <IntelligenceBrief brief={brief} />
              </div>
            )
          })()}

          {/* Compact alert banner */}
          {highValueAlerts.length > 0 && showAlerts && (
            <div className="shrink-0 flex items-center gap-3 px-4 py-2 bg-red-950/50 border-b border-red-800/40">
              <span className="text-red-400 font-mono text-[10px] font-black tracking-widest whitespace-nowrap">HIGH-VALUE INTEL</span>
              <div className="flex gap-1.5 flex-wrap overflow-hidden">
                {highValueAlerts.slice(0, 5).map((alert, idx) => (
                  <span key={idx} className={`px-2 py-0.5 rounded text-[9px] font-mono font-bold border whitespace-nowrap ${alert.severity === 'critical' ? 'bg-red-900/60 border-red-700/60 text-red-300' : 'bg-orange-900/60 border-orange-700/60 text-orange-300'}`}>
                    {alert.source}
                  </span>
                ))}
              </div>
              <button onClick={() => setShowAlerts(false)} className="ml-auto text-red-600 hover:text-red-400 text-base leading-none shrink-0">&times;</button>
            </div>
          )}

          {/* Tab navigation */}
          <div className="shrink-0 flex items-center border-b border-purple-900/25 bg-[#07070f] px-2 gap-0 overflow-x-auto">
            {[
              { id: 'overview',  label: 'OVERVIEW',   count: 0 },
              { id: 'identity',  label: 'IDENTITY',   count: profileData ? (profileData.personalInfo?.fullName ? 1 : 0) : 0 },
              { id: 'breaches',  label: 'BREACHES',   count: profileData ? (profileData.breachSources?.length ?? 0) : 0 },
              { id: 'web',       label: 'WEB',        count: profileData ? (profileData.webResults?.length ?? 0) : 0 },
              { id: 'network',   label: 'NETWORK',    count: 0 },
              { id: 'analytics',   label: 'ANALYTICS',  count: 0 },
              { id: 'ai',         label: 'AI INTEL',   count: 0 },
              { id: 'btra',       label: 'BTRA INTEL', count: 0 },
              { id: 'investigate', label: 'INVESTIGATE', count: 0 },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 font-mono text-[10px] tracking-widest font-bold whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-300 bg-purple-950/20'
                    : 'border-transparent text-gray-600 hover:text-gray-400 hover:bg-white/[0.02]'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`font-mono text-[8px] font-black px-1 py-0.5 rounded leading-none ${
                    activeTab === tab.id ? 'bg-purple-700/50 text-purple-200' : 'bg-gray-800 text-gray-500'
                  }`}>{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Tab content — scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="p-4 space-y-4 min-w-0">

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <>
              <AIInvestigationBrief
                aiInsights={aiInsights}
                loadingInsights={loadingInsights}
                breachCount={profileData.breachSources.length}
                query={query}
                onReanalyze={() => fetchAIInsights(false)}
              />

              {/* ── Intelligence Summary — always visible ── */}
              {/* KPI strip */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'BREACH SOURCES', value: profileData.breachSources.length, color: '#ef4444', border: 'border-red-900/40' },
                  { label: 'WEB RESULTS', value: profileData.webResults?.length ?? 0, color: '#3b82f6', border: 'border-blue-900/40' },
                  { label: 'STEALER LOGS', value: profileData.stealerLogs?.length ?? 0, color: '#f97316', border: 'border-orange-900/40' },
                  { label: 'PHOTOS INDEXED', value: profileData.photos?.length ?? 0, color: '#a855f7', border: 'border-purple-900/40' },
                ].map(({ label, value, color, border }) => (
                  <div key={label} className={`bg-white/[0.02] border ${border} rounded-xl p-4 text-center`}>
                    <div className="font-mono font-black text-2xl" style={{ color }}>{value}</div>
                    <div className="font-mono text-[9px] text-gray-600 tracking-widest mt-1">{label}</div>
                  </div>
                ))}
              </div>

              {/* INVESTIGATIVE INTELLIGENCE — pivot identifiers + anomalies computed from breach records */}
              {(() => {
                const phones: Map<string, string[]> = new Map()
                const altEmails: Map<string, string[]> = new Map()
                const profileLinks: Map<string, string[]> = new Map()
                const usernames: Map<string, string[]> = new Map()
                const platformIds: Map<string, string[]> = new Map()
                const ipAddrs: Map<string, string[]> = new Map()
                const names: Map<string, string[]> = new Map()
                const anomalies: Array<{ text: string; severity: 'critical' | 'high' | 'medium' }> = []

                const queryLower = query.toLowerCase().trim()

                const addToMap = (map: Map<string, string[]>, key: string, source: string) => {
                  if (!map.has(key)) map.set(key, [])
                  const arr = map.get(key)!
                  if (!arr.includes(source)) arr.push(source)
                }

                const SKIP_FIELDS = new Set(['source', 'date', 'records', 'datatypes', 'numofresults', 'breahdescription', '_breach_source', '_breach_date'])

                profileData.breachSources.forEach((breach: any) => {
                  const sourceName = breach.source || 'Unknown'
                  const records: any[] = (breach.allRecords && breach.allRecords.length > 0) ? breach.allRecords : [breach]

                  records.forEach((record: any) => {
                    Object.entries(record).forEach(([k, v]) => {
                      if (!v) return
                      const vs = String(v).trim()
                      if (!vs || vs.length < 2) return
                      const kl = k.toLowerCase().replace(/[^a-z]/g, '')
                      if (SKIP_FIELDS.has(kl)) return
                      const vsLower = vs.toLowerCase()

                      if (kl.includes('phone') || kl.includes('mobile') || kl === 'tel' || kl === 'cell') {
                        const digits = vs.replace(/\D/g, '')
                        if (digits.length >= 7 && vsLower !== queryLower) addToMap(phones, vs, sourceName)
                      } else if (kl === 'name' || kl === 'fullname' || kl === 'firstname' || kl === 'lastname') {
                        const digits = vs.replace(/\D/g, '')
                        if (digits.length >= 9 && vs.replace(/[^\d\s\-+().\s]/g, '').length >= vs.length * 0.85) {
                          anomalies.push({
                            text: `Phone sequence \`${vs}\` stored in the **${k}** field in **${sourceName}** — data entry anomaly that independently confirms this number belongs to this identity`,
                            severity: 'high'
                          })
                        } else if (vsLower !== queryLower && vs.length > 2 && !vs.includes('@')) {
                          addToMap(names, vs, sourceName)
                        }
                      } else if ((kl.includes('email') || kl.includes('mail')) && vs.includes('@')) {
                        if (vsLower !== queryLower) addToMap(altEmails, vs, sourceName)
                      } else if (kl.includes('linkedin') || kl === 'profileurl' || kl === 'profile_url') {
                        addToMap(profileLinks, vs, sourceName)
                      } else if (kl.includes('url') && vs.startsWith('http')) {
                        addToMap(profileLinks, vs, sourceName)
                      } else if (kl.includes('username') || kl.includes('nickname') || kl === 'nick' || kl === 'login' || kl === 'handle' || kl === 'screenname') {
                        if (vsLower !== queryLower && vs.length > 1) addToMap(usernames, vs, sourceName)
                      } else if (kl.includes('instagram') || kl.includes('facebook') || kl.includes('twitter') || kl.includes('vk') || kl.includes('tiktok') || kl.includes('snapchat')) {
                        addToMap(platformIds, `${k}: ${vs}`, sourceName)
                      } else if ((kl === 'ip' || kl === 'ipaddress' || kl === 'ip_address') && /^\d{1,3}\.\d{1,3}/.test(vs)) {
                        addToMap(ipAddrs, vs, sourceName)
                      }
                    })
                  })
                })

                // Name variants
                if (names.size > 1) {
                  const nameArr = Array.from(names.keys())
                  const normalized = nameArr.map(n => n.toLowerCase().replace(/\s+/g, ' ').trim())
                  const uniqueBase = new Set(normalized)
                  if (uniqueBase.size < nameArr.length) {
                    anomalies.push({ text: `Name variant detected: ${nameArr.slice(0, 4).map(n => `**${n}**`).join(' / ')} — same subject, inconsistent data entry across breach sources`, severity: 'medium' })
                  } else {
                    anomalies.push({ text: `Multiple distinct names across sources: ${Array.from(names.entries()).slice(0, 3).map(([n, s]) => `**${n}** (${s[0]})`).join(', ')} — possible aliases or multiple subjects sharing identifier`, severity: 'medium' })
                  }
                }

                // Stealer logs
                if (profileData.stealerLogs && profileData.stealerLogs.length > 0) {
                  const families = [...new Set(profileData.stealerLogs.map((s: any) => s.stealer_family || s.source))].filter(Boolean)
                  const totalCorp = profileData.stealerLogs.reduce((sum: number, s: any) => sum + (s.total_corporate_services || 0), 0)
                  const totalUser = profileData.stealerLogs.reduce((sum: number, s: any) => sum + (s.total_user_services || 0), 0)
                  const dates = profileData.stealerLogs.map((s: any) => s.date_compromised).filter(Boolean).sort()
                  anomalies.unshift({
                    text: `Active device compromise confirmed — **${profileData.stealerLogs.length} stealer infection${profileData.stealerLogs.length > 1 ? 's' : ''}** via **${families.join(', ') || 'infostealer'}**${dates.length > 0 ? ` (${dates[0]})` : ''}. ${totalCorp > 0 ? `**${totalCorp} corporate service credentials** exfiltrated.` : ''} ${totalUser > 0 ? `${totalUser} personal service credentials harvested.` : ''} All passwords stored on compromised device must be treated as known to attacker.`,
                    severity: 'critical'
                  })
                }

                // Password hashes
                const breachesWithPass: string[] = []
                profileData.breachSources.forEach((b: any) => {
                  const recs: any[] = (b.allRecords && b.allRecords.length > 0) ? b.allRecords : [b]
                  if (recs.some((r: any) => Object.keys(r).some(k => { const kl = k.toLowerCase(); return (kl.includes('password') || kl === 'pass' || kl === 'pwd' || kl.includes('hash')) && r[k] }))) {
                    breachesWithPass.push(b.source || 'Unknown')
                  }
                })
                if (breachesWithPass.length > 0) {
                  anomalies.push({
                    text: `Password/hash data present in ${breachesWithPass.length > 3 ? `**${breachesWithPass.length} sources**` : breachesWithPass.slice(0, 3).map(s => `**${s}**`).join(', ')}. MD5/unsalted SHA1 hashes are recoverable via rainbow tables — enabling credential stuffing across all linked accounts.`,
                    severity: 'high'
                  })
                }

                // Cross-source confirmation signals
                const multiSource: string[] = []
                ;[phones, altEmails, profileLinks, usernames].forEach(map => {
                  map.forEach((srcs, v) => {
                    if (srcs.length >= 2) multiSource.push(`\`${v.slice(0, 50)}\` confirmed in ${srcs.length} sources`)
                  })
                })
                if (multiSource.length > 0) {
                  anomalies.push({ text: `Multi-source identity confirmation: ${multiSource.slice(0, 3).join('; ')} — high attribution confidence`, severity: 'medium' })
                }

                const totalPivots = phones.size + altEmails.size + profileLinks.size + usernames.size + platformIds.size + ipAddrs.size

                if (anomalies.length === 0 && totalPivots === 0) return null

                const PIVOT_TOOLS: Record<string, string> = {
                  phones: 'TrueCaller · Pipl · BreachINT pivot',
                  emails: 'BreachINT · Gmail OSINT · RocketReach',
                  links: 'Open link · WebArchive · RocketReach',
                  usernames: 'Sherlock · WhatsMyName · BreachINT pivot',
                  platforms: 'Platform API · Social Mapper',
                  ips: 'Shodan · Censys · AbuseIPDB',
                }

                const copyVal = (text: string) => { try { navigator.clipboard?.writeText(text) } catch (_) {} }

                return (
                  <div className="space-y-3">
                    {/* Anomalies / Discoveries */}
                    {anomalies.length > 0 && (
                      <div className="bg-amber-950/10 border border-amber-800/35 rounded-xl overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-amber-900/25 flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />
                          <span className="font-mono text-[9px] text-amber-500 tracking-widest uppercase">Intelligence Discoveries — {anomalies.length} signals</span>
                        </div>
                        <div className="divide-y divide-amber-900/10">
                          {anomalies.map((a, i) => (
                            <div key={i} className="flex gap-3 px-4 py-2.5">
                              <div className={`w-0.5 shrink-0 rounded-full self-stretch ${a.severity === 'critical' ? 'bg-red-500' : a.severity === 'high' ? 'bg-orange-500' : 'bg-amber-500'}`} />
                              <div className="text-[11px] text-amber-100/75 leading-relaxed
                                [&_strong]:text-white [&_strong]:font-semibold
                                [&_code]:text-green-400 [&_code]:bg-green-950/40 [&_code]:border [&_code]:border-green-900/40 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[10px] [&_code]:font-mono
                              ">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{DOMPurify.sanitize(a.text)}</ReactMarkdown>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pivot identifiers */}
                    {totalPivots > 0 && (
                      <div className="bg-white/[0.02] border border-blue-900/30 rounded-xl overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-blue-900/20">
                          <span className="font-mono text-[9px] text-blue-400 tracking-widest uppercase">Pivot Identifiers — {totalPivots} queryable values extracted from breach records</span>
                        </div>
                        <div className="p-3 space-y-3">
                          {phones.size > 0 && (
                            <div>
                              <div className="font-mono text-[9px] text-gray-600 tracking-widest mb-1.5">PHONE NUMBERS &rarr; {PIVOT_TOOLS.phones}</div>
                              <div className="flex flex-wrap gap-1.5">
                                {Array.from(phones.entries()).map(([v, srcs]) => (
                                  <button key={v} onClick={() => copyVal(v)} title={`From: ${srcs.join(', ')} — click to copy`}
                                    className="group flex items-center gap-1.5 px-2 py-1 rounded bg-green-950/30 border border-green-900/40 hover:border-green-600/50 transition-colors">
                                    <span className="font-mono text-[11px] text-green-300">{v}</span>
                                    {srcs.length > 1 && <span className="font-mono text-[9px] text-green-700 bg-green-950/60 px-1 rounded">{srcs.length}x</span>}
                                    <span className="font-mono text-[9px] text-gray-700 group-hover:text-gray-500">copy</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {altEmails.size > 0 && (
                            <div>
                              <div className="font-mono text-[9px] text-gray-600 tracking-widest mb-1.5">ALTERNATE EMAILS &rarr; {PIVOT_TOOLS.emails}</div>
                              <div className="flex flex-wrap gap-1.5">
                                {Array.from(altEmails.entries()).map(([v, srcs]) => (
                                  <button key={v} onClick={() => copyVal(v)} title={`From: ${srcs.join(', ')} — click to copy`}
                                    className="group flex items-center gap-1.5 px-2 py-1 rounded bg-blue-950/30 border border-blue-900/40 hover:border-blue-600/50 transition-colors">
                                    <span className="font-mono text-[11px] text-blue-300">{v}</span>
                                    {srcs.length > 1 && <span className="font-mono text-[9px] text-blue-700 bg-blue-950/60 px-1 rounded">{srcs.length}x</span>}
                                    <span className="font-mono text-[9px] text-gray-700 group-hover:text-gray-500">copy</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {profileLinks.size > 0 && (
                            <div>
                              <div className="font-mono text-[9px] text-gray-600 tracking-widest mb-1.5">PROFILE LINKS &rarr; {PIVOT_TOOLS.links}</div>
                              <div className="flex flex-wrap gap-1.5">
                                {Array.from(profileLinks.entries()).map(([v, srcs]) => (
                                  <div key={v} className="flex items-center gap-1.5 px-2 py-1 rounded bg-cyan-950/30 border border-cyan-900/40">
                                    <a href={v.startsWith('http') ? v : `https://${v}`} target="_blank" rel="noopener noreferrer"
                                      className="font-mono text-[11px] text-cyan-300 hover:text-cyan-200 hover:underline truncate max-w-[220px]">{v}</a>
                                    {srcs.length > 1 && <span className="font-mono text-[9px] text-cyan-700 bg-cyan-950/60 px-1 rounded">{srcs.length}x</span>}
                                    <button onClick={() => copyVal(v)} className="font-mono text-[9px] text-gray-700 hover:text-gray-500">copy</button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {usernames.size > 0 && (
                            <div>
                              <div className="font-mono text-[9px] text-gray-600 tracking-widest mb-1.5">USERNAMES &rarr; {PIVOT_TOOLS.usernames}</div>
                              <div className="flex flex-wrap gap-1.5">
                                {Array.from(usernames.entries()).map(([v, srcs]) => (
                                  <button key={v} onClick={() => copyVal(v)} title={`From: ${srcs.join(', ')} — click to copy`}
                                    className="group flex items-center gap-1.5 px-2 py-1 rounded bg-purple-950/30 border border-purple-900/40 hover:border-purple-600/50 transition-colors">
                                    <span className="font-mono text-[11px] text-purple-300">{v}</span>
                                    {srcs.length > 1 && <span className="font-mono text-[9px] text-purple-700 bg-purple-950/60 px-1 rounded">{srcs.length}x</span>}
                                    <span className="font-mono text-[9px] text-gray-700 group-hover:text-gray-500">copy</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {platformIds.size > 0 && (
                            <div>
                              <div className="font-mono text-[9px] text-gray-600 tracking-widest mb-1.5">PLATFORM IDs &rarr; {PIVOT_TOOLS.platforms}</div>
                              <div className="flex flex-wrap gap-1.5">
                                {Array.from(platformIds.entries()).map(([v, srcs]) => {
                                  const parts = v.split(': ')
                                  const label = parts[0]
                                  const val = parts.slice(1).join(': ')
                                  return (
                                    <button key={v} onClick={() => copyVal(val)} title={`From: ${srcs.join(', ')} — click to copy value`}
                                      className="group flex items-center gap-1.5 px-2 py-1 rounded bg-pink-950/30 border border-pink-900/40 hover:border-pink-600/50 transition-colors">
                                      <span className="font-mono text-[9px] text-pink-600">{label}:</span>
                                      <span className="font-mono text-[11px] text-pink-300">{val}</span>
                                      {srcs.length > 1 && <span className="font-mono text-[9px] text-pink-700 bg-pink-950/60 px-1 rounded">{srcs.length}x</span>}
                                      <span className="font-mono text-[9px] text-gray-700 group-hover:text-gray-500">copy</span>
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                          {ipAddrs.size > 0 && (
                            <div>
                              <div className="font-mono text-[9px] text-gray-600 tracking-widest mb-1.5">IP ADDRESSES &rarr; {PIVOT_TOOLS.ips}</div>
                              <div className="flex flex-wrap gap-1.5">
                                {Array.from(ipAddrs.entries()).map(([v, srcs]) => (
                                  <button key={v} onClick={() => copyVal(v)} title={`From: ${srcs.join(', ')} — click to copy`}
                                    className="group flex items-center gap-1.5 px-2 py-1 rounded bg-orange-950/30 border border-orange-900/40 hover:border-orange-600/50 transition-colors">
                                    <span className="font-mono text-[11px] text-orange-300">{v}</span>
                                    {srcs.length > 1 && <span className="font-mono text-[9px] text-orange-700 bg-orange-950/60 px-1 rounded">{srcs.length}x</span>}
                                    <span className="font-mono text-[9px] text-gray-700 group-hover:text-gray-500">copy</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}

              {/* Exposed data types */}
              {(() => {
                const types = new Set<string>()
                profileData.breachSources.forEach((b: any) => {
                  if (b.dataTypes) b.dataTypes.split(',').forEach((t: string) => types.add(t.trim()))
                  if (b.email) types.add('Email')
                  if (b.password) types.add('Password')
                  if (b.phone) types.add('Phone')
                  if (b.address) types.add('Address')
                  if (b.birthDate) types.add('Date of Birth')
                })
                if (types.size === 0) return null
                return (
                  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                    <div className="font-mono text-[9px] text-gray-600 tracking-widest mb-3">EXPOSED DATA TYPES — {types.size} CATEGORIES</div>
                    <div className="flex flex-wrap gap-1.5">
                      {Array.from(types).map(t => (
                        <span key={t} className="font-mono text-[10px] px-2 py-0.5 rounded border border-red-900/40 bg-red-950/20 text-red-300">{t}</span>
                      ))}
                    </div>
                  </div>
                )
              })()}

              {/* Top breach sources table */}
              {profileData.breachSources.length > 0 && (
                <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-white/[0.05] flex items-center justify-between">
                    <span className="font-mono text-[9px] text-gray-500 tracking-widest">BREACH SOURCE INDEX</span>
                    <span className="font-mono text-[9px] text-gray-600">{profileData.breachSources.length} sources</span>
                  </div>
                  <div className="divide-y divide-white/[0.04]">
                    {profileData.breachSources.slice(0, 8).map((breach: any, i: number) => (
                      <div key={i} className="flex items-center gap-4 px-4 py-2.5 hover:bg-white/[0.02] transition-colors">
                        <div className="w-5 h-5 rounded flex items-center justify-center shrink-0 bg-red-950/50 border border-red-900/40">
                          <span className="font-mono text-[9px] font-bold text-red-400">{i + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <div className="font-mono text-xs font-bold text-white truncate">{breach.source}</div>
                            {typeof breach.matchRelevance === 'number' && (
                              <span
                                className={`shrink-0 font-mono text-[9px] px-1.5 py-0.5 rounded border ${
                                  breach.matchRelevance >= 0.7
                                    ? 'border-emerald-700/50 bg-emerald-950/30 text-emerald-400'
                                    : breach.matchRelevance >= 0.4
                                    ? 'border-amber-700/50 bg-amber-950/30 text-amber-400'
                                    : 'border-red-800/50 bg-red-950/30 text-red-400'
                                }`}
                                title="How much of the search query matched this breach's name/company/username fields — LeakOSINT does unfiltered free-text search, so a low score usually means a coincidental text match, not a confirmed identity hit."
                              >
                                {Math.round(breach.matchRelevance * 100)}% name match
                              </span>
                            )}
                          </div>
                          {breach.dataTypes && (
                            <div className="font-mono text-[9px] text-gray-600 truncate mt-0.5">{breach.dataTypes}</div>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          {breach.date && <div className="font-mono text-[9px] text-gray-600">{breach.date}</div>}
                          {(breach.records || breach.numOfResults) && (
                            <div className="font-mono text-[9px] text-gray-500">
                              {(parseInt(breach.records) || breach.numOfResults || breach.allRecords?.length || 0).toLocaleString()} records
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {profileData.breachSources.length > 8 && (
                      <div className="px-4 py-2 text-center">
                        <button onClick={() => setActiveTab('breaches')} className="font-mono text-[10px] text-purple-400 hover:text-purple-300 transition-colors">
                          View all {profileData.breachSources.length} sources in Breaches tab
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Identity quick-view */}
              {(() => {
                const pi = profileData.personalInfo
                const c = profileData.contactDetails
                const a = profileData.addresses
                const fields = [
                  { label: 'Full Name',  value: pi.fullName || [pi.firstName, pi.lastName].filter(Boolean).join(' ') },
                  { label: 'Email',      value: c.email },
                  { label: 'Phone',      value: c.phone || c.phoneNumbers },
                  { label: 'DOB',        value: pi.dob },
                  { label: 'Location',   value: [a.city, a.state, a.country].filter(Boolean).join(', ') },
                  { label: 'Alt Emails', value: c.alternativeEmails },
                ].filter(f => f.value)
                if (fields.length === 0) return null
                return (
                  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-white/[0.05]">
                      <span className="font-mono text-[9px] text-gray-500 tracking-widest">IDENTITY ATTRIBUTES EXTRACTED</span>
                    </div>
                    <div className="grid grid-cols-2 divide-x divide-y divide-white/[0.04]">
                      {fields.map(({ label, value }) => (
                        <div key={label} className="px-4 py-2.5">
                          <div className="font-mono text-[9px] text-gray-600 mb-0.5">{label.toUpperCase()}</div>
                          <div className="font-mono text-xs text-white truncate">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })()}
            </>
          )}

          {/* IDENTITY TAB */}
          {activeTab === 'identity' && (
          <>
          <IdentityProfileCard
            personalInfo={profileData.personalInfo}
            contactDetails={profileData.contactDetails}
            socialMedia={profileData.socialMedia}
            addresses={profileData.addresses}
            governmentIDs={profileData.governmentIDs}
            photos={profileData.photos || []}
            query={query}
          />
          <SourceIntelPanel
            title="Target Profile Intelligence"
            icon="🔍"
            color="red"
            description="Aggregated personal data, financial records, and enrichment from breach databases"
            defaultExpanded={true}
          >
            <div className="space-y-6">

              {/* Personal Information */}
              {Object.keys(profileData.personalInfo).length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-red-500">👤</span>
                    <h3 className="text-sm font-medium text-red-400 font-mono">[PERSONAL DATA]</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {profileData.personalInfo.fullName && (
                      <div className="bg-black/50 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[FULL NAME]</div>
                        <div className="text-white font-medium">{profileData.personalInfo.fullName}</div>
                      </div>
                    )}
                    {profileData.personalInfo.dob && (
                      <div className="bg-black/50 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[DATE OF BIRTH]</div>
                        <div className="text-white font-medium">{profileData.personalInfo.dob}</div>
                      </div>
                    )}
                    {profileData.personalInfo.gender && (
                      <div className="bg-black/50 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[GENDER]</div>
                        <div className="text-white font-medium">{profileData.personalInfo.gender}</div>
                      </div>
                    )}
                    {profileData.personalInfo.nationality && (
                      <div className="bg-black/50 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[NATIONALITY]</div>
                        <div className="text-white font-medium">{profileData.personalInfo.nationality}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Details */}
              {Object.keys(profileData.contactDetails).length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-red-500">📞</span>
                    <h3 className="text-sm font-medium text-red-400 font-mono">[CONTACT INTELLIGENCE]</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {profileData.contactDetails.email && (
                      <div className="bg-black/50 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[EMAIL]</div>
                        <div className="text-white font-medium">{profileData.contactDetails.email}</div>
                      </div>
                    )}
                    {profileData.contactDetails.phone && (
                      <div className="bg-black/50 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[PHONE]</div>
                        <div className="text-white font-medium">{profileData.contactDetails.phone}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Financial Information */}
              {Object.keys(profileData.financial).length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-red-500">💰</span>
                    <h3 className="text-sm font-medium text-red-400 font-mono">[FINANCIAL INTELLIGENCE]</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {profileData.financial.paymentMethod && (
                      <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3">
                        <div className="text-xs text-yellow-400 mb-1 font-mono">[PAYMENT METHOD]</div>
                        <div className="text-yellow-300 font-medium">{profileData.financial.paymentMethod}</div>
                      </div>
                    )}
                    {profileData.financial.balance && (
                      <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-3">
                        <div className="text-xs text-green-400 mb-1 font-mono">[ACCOUNT BALANCE]</div>
                        <div className="text-green-300 font-medium">{profileData.financial.balance}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Profile Photos */}
              {profileData.photos && Array.isArray(profileData.photos) && profileData.photos.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-red-500">📸</span>
                    <h3 className="text-sm font-medium text-red-400 font-mono">[PROFILE PHOTOS]</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {profileData.photos.map((photo: any, index: number) => (
                      <div key={index} className="bg-black/50 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-2 font-mono">[PHOTO {index + 1}]</div>
                        <div className="relative">
                        <img 
                          src={photo.url} 
                          alt={`Profile photo ${index + 1}`}
                          className="w-full h-24 object-cover rounded border border-red-500/30"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                          onError={(e) => {
                              const img = e.target as HTMLImageElement
                              // For Google profile images, try adding size parameter
                              if (photo.url.includes('googleusercontent.com') && !photo.url.includes('=s')) {
                                const newUrl = photo.url.split('?')[0] + '=s128-c'
                                console.log('Retrying Google image with size parameter:', newUrl)
                                img.src = newUrl
                              } else {
                                img.style.display = 'none'
                              }
                            }}
                            onLoad={(e) => {
                              (e.target as HTMLImageElement).style.display = 'block'
                            }}
                          />
                        </div>
                        <div className="text-xs text-red-300 mt-1 font-mono">{photo.source}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sycek Enrichment Data */}
              {profileData.sycekEnrichment && profileData.sycekEnrichment.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-purple-500">✨</span>
                    <h3 className="text-sm font-medium text-purple-400 font-mono">[SYCEK ENRICHMENT]</h3>
                    <Badge variant="default" className="bg-purple-600">ENRICHED</Badge>
                  </div>
                  {profileData.sycekEnrichment.map((enrichment: SycekEnrichmentData, index: number) => (
                    <div key={index} className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-purple-300 font-mono">
                          Powered by {enrichment.enrichment_provider} • {enrichment.enrichment_brand}
                        </div>
                      </div>
                      
                      {/* Basic Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {enrichment.name && (
                          <div className="bg-black/30 border border-purple-500/20 rounded-lg p-3">
                            <div className="text-xs text-purple-400 mb-1 font-mono">[NAME]</div>
                            <div className="text-white font-medium">{enrichment.name}</div>
                          </div>
                        )}
                        {enrichment.current_title && (
                          <div className="bg-black/30 border border-purple-500/20 rounded-lg p-3">
                            <div className="text-xs text-purple-400 mb-1 font-mono">[CURRENT TITLE]</div>
                            <div className="text-white font-medium">{enrichment.current_title}</div>
                          </div>
                        )}
                        {enrichment.current_employer && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-black/30 border border-purple-500/20 rounded-lg p-4 hover:border-purple-400/40 transition-colors"
                          >
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-purple-400 text-lg">🏢</span>
                              <div className="text-xs text-purple-400 font-mono">[CURRENT EMPLOYER]</div>
                            </div>
                            <div className="text-white font-semibold text-base mb-2">{enrichment.current_employer}</div>
                            {enrichment.current_title && (
                              <div className="text-purple-300 text-sm mb-2">{enrichment.current_title}</div>
                            )}
                            <div className="flex flex-wrap gap-2 mt-3">
                              {enrichment.current_employer_website && (
                                <a 
                                  href={enrichment.current_employer_website} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="inline-flex items-center space-x-1 text-purple-300 hover:text-purple-100 text-xs underline"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                                  </svg>
                                  <span>Website</span>
                                </a>
                              )}
                              {enrichment.current_employer_linkedin_url && (
                                <a 
                                  href={enrichment.current_employer_linkedin_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="inline-flex items-center space-x-1 text-blue-300 hover:text-blue-100 text-xs underline"
                                >
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                  </svg>
                                  <span>LinkedIn</span>
                                </a>
                              )}
                              {enrichment.current_employer_domain && (
                                <div className="text-gray-400 text-xs font-mono">
                                  Domain: {enrichment.current_employer_domain}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                        {enrichment.location && (
                          <div className="bg-black/30 border border-purple-500/20 rounded-lg p-3">
                            <div className="text-xs text-purple-400 mb-1 font-mono">[LOCATION]</div>
                            <div className="text-white font-medium">{enrichment.location}</div>
                          </div>
                        )}
                      </div>
                      
                      {/* Phone Numbers - Enhanced Display */}
                      {enrichment.phones && enrichment.phones.length > 0 && (
                        <div className="bg-black/30 border border-purple-500/20 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <span className="text-purple-400 text-lg">📞</span>
                            <div className="text-xs text-purple-400 font-mono">[PHONE NUMBERS]</div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {enrichment.phones.slice(0, 6).map((phoneObj: any, phoneIndex: number) => {
                              const phone = typeof phoneObj === 'string' ? phoneObj : phoneObj.number || phoneObj.phone
                              const phoneType = typeof phoneObj === 'object' ? (phoneObj.type || 'unknown') : 'unknown'
                              return (
                                <motion.div
                                  key={phoneIndex}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: phoneIndex * 0.05 }}
                                  className="flex items-center space-x-3 bg-purple-900/20 border border-purple-500/30 rounded-lg p-3 hover:border-purple-400/50 transition-colors"
                                >
                                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600/30 rounded-full flex items-center justify-center">
                                    <span className="text-purple-300 text-lg">📱</span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-white font-medium text-sm truncate">{phone}</div>
                                    <div className="text-purple-400 text-xs font-mono">{phoneType}</div>
                                  </div>
                                  <a 
                                    href={`tel:${phone}`}
                                    className="flex-shrink-0 text-purple-400 hover:text-purple-300 transition-colors"
                                    title="Call"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                  </a>
                                </motion.div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Social Media Links - Enhanced with Icons */}
                      {(enrichment.linkedin_url || enrichment.links?.linkedin || enrichment.links?.twitter || enrichment.links?.facebook || (enrichment.links as any)?.instagram || (enrichment.links as any)?.github) && (
                        <div className="bg-black/30 border border-purple-500/20 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <span className="text-purple-400 text-lg">🌐</span>
                            <div className="text-xs text-purple-400 font-mono">[SOCIAL MEDIA PROFILES]</div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {(enrichment.linkedin_url || enrichment.links?.linkedin) && (
                              <motion.a
                                href={enrichment.linkedin_url || enrichment.links?.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.05 }}
                                className="flex items-center space-x-3 bg-blue-900/30 border border-blue-500/30 rounded-lg p-3 hover:border-blue-400/50 hover:bg-blue-900/40 transition-all group"
                              >
                                <div className="flex-shrink-0 w-10 h-10 bg-blue-600/30 rounded-full flex items-center justify-center">
                                  <svg className="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-white font-medium text-sm group-hover:text-blue-300 transition-colors">LinkedIn</div>
                                  <div className="text-blue-400 text-xs font-mono truncate">Professional Network</div>
                                </div>
                                <svg className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </motion.a>
                            )}
                            
                            {enrichment.links?.twitter && (
                              <motion.a
                                href={enrichment.links.twitter}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.05 }}
                                className="flex items-center space-x-3 bg-sky-900/30 border border-sky-500/30 rounded-lg p-3 hover:border-sky-400/50 hover:bg-sky-900/40 transition-all group"
                              >
                                <div className="flex-shrink-0 w-10 h-10 bg-sky-600/30 rounded-full flex items-center justify-center">
                                  <svg className="w-6 h-6 text-sky-400" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-white font-medium text-sm group-hover:text-sky-300 transition-colors">Twitter / X</div>
                                  <div className="text-sky-400 text-xs font-mono truncate">Social Media</div>
                                </div>
                                <svg className="w-4 h-4 text-sky-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </motion.a>
                            )}
                            
                            {enrichment.links?.facebook && (
                              <motion.a
                                href={enrichment.links.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.05 }}
                                className="flex items-center space-x-3 bg-blue-800/30 border border-blue-500/30 rounded-lg p-3 hover:border-blue-400/50 hover:bg-blue-800/40 transition-all group"
                              >
                                <div className="flex-shrink-0 w-10 h-10 bg-blue-700/30 rounded-full flex items-center justify-center">
                                  <svg className="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-white font-medium text-sm group-hover:text-blue-300 transition-colors">Facebook</div>
                                  <div className="text-blue-400 text-xs font-mono truncate">Social Network</div>
                                </div>
                                <svg className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </motion.a>
                            )}
                            
                            {(enrichment.links as any)?.instagram && (
                              <motion.a
                                href={(enrichment.links as any).instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.05 }}
                                className="flex items-center space-x-3 bg-pink-900/30 border border-pink-500/30 rounded-lg p-3 hover:border-pink-400/50 hover:bg-pink-900/40 transition-all group"
                              >
                                <div className="flex-shrink-0 w-10 h-10 bg-pink-600/30 rounded-full flex items-center justify-center">
                                  <svg className="w-6 h-6 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162 0 3.403 2.759 6.162 6.162 6.162 3.403 0 6.162-2.759 6.162-6.162 0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-white font-medium text-sm group-hover:text-pink-300 transition-colors">Instagram</div>
                                  <div className="text-pink-400 text-xs font-mono truncate">Photo Sharing</div>
                                </div>
                                <svg className="w-4 h-4 text-pink-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </motion.a>
                            )}
                            
                            {(enrichment.links as any)?.github && (
                              <motion.a
                                href={(enrichment.links as any).github}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.05 }}
                                className="flex items-center space-x-3 bg-gray-800/30 border border-gray-500/30 rounded-lg p-3 hover:border-gray-400/50 hover:bg-gray-800/40 transition-all group"
                              >
                                <div className="flex-shrink-0 w-10 h-10 bg-gray-700/30 rounded-full flex items-center justify-center">
                                  <svg className="w-6 h-6 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482C19.138 20.197 22 16.425 22 12.017 22 6.484 17.522 2 12 2z" clipRule="evenodd"/>
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-white font-medium text-sm group-hover:text-gray-300 transition-colors">GitHub</div>
                                  <div className="text-gray-400 text-xs font-mono truncate">Code Repository</div>
                                </div>
                                <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </motion.a>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Job History - Enhanced with Company Icons */}
                      {enrichment.job_history && enrichment.job_history.length > 0 && (
                        <div className="bg-black/30 border border-purple-500/20 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <span className="text-purple-400 text-lg">💼</span>
                            <div className="text-xs text-purple-400 font-mono">[EMPLOYMENT HISTORY]</div>
                          </div>
                          <div className="space-y-3">
                            {enrichment.job_history.slice(0, 8).map((job: any, jobIndex: number) => (
                              <motion.div
                                key={jobIndex}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: jobIndex * 0.05 }}
                                className="bg-purple-900/10 border border-purple-500/20 rounded-lg p-3 hover:border-purple-400/40 transition-colors"
                              >
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600/30 rounded-lg flex items-center justify-center">
                                    <span className="text-purple-300 text-lg">🏢</span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-white font-semibold text-sm mb-1">
                                      {job.title || job.position || 'Position Not Specified'}
                                    </div>
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className="text-purple-300 font-medium text-sm">
                                        {job.company_name || job.company || 'Company Not Specified'}
                                      </span>
                                      {job.company_linkedin_url && (
                                        <a 
                                          href={job.company_linkedin_url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-blue-400 hover:text-blue-300"
                                          title="View Company LinkedIn"
                                        >
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                          </svg>
                                        </a>
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-3 text-gray-400 text-xs">
                                      <span className="flex items-center space-x-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span>
                                          {job.start_date || 'Start date unknown'} - {job.end_date === 'Present' || job.is_current ? (
                                            <span className="text-green-400 font-semibold">Present</span>
                                          ) : (
                                            job.end_date || 'End date unknown'
                                          )}
                                        </span>
                                      </span>
                                      {job.location && (
                                        <span className="flex items-center space-x-1">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                          </svg>
                                          <span>{job.location}</span>
                                        </span>
                                      )}
                                    </div>
                                    {job.description && (
                                      <div className="text-gray-300 text-xs mt-2 line-clamp-2">
                                        {job.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                            {enrichment.job_history.length > 8 && (
                              <div className="text-center pt-2 border-t border-purple-500/20">
                                <div className="text-purple-400 text-xs font-mono">
                                  +{enrichment.job_history.length - 8} more positions
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Education - Enhanced with Icons */}
                      {enrichment.education && enrichment.education.length > 0 && (
                        <div className="bg-black/30 border border-purple-500/20 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <span className="text-purple-400 text-lg">🎓</span>
                            <div className="text-xs text-purple-400 font-mono">[EDUCATION]</div>
                          </div>
                          <div className="space-y-3">
                            {enrichment.education.map((edu: any, eduIndex: number) => (
                              <motion.div
                                key={eduIndex}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: eduIndex * 0.05 }}
                                className="flex items-start space-x-3 bg-purple-900/10 border border-purple-500/20 rounded-lg p-3 hover:border-purple-400/40 transition-colors"
                              >
                                <div className="flex-shrink-0 w-10 h-10 bg-purple-600/30 rounded-lg flex items-center justify-center">
                                  <span className="text-purple-300 text-lg">🎓</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-white font-semibold text-sm mb-1">
                                    {edu.school || edu.institution || 'Educational Institution'}
                                  </div>
                                  {edu.degree && (
                                    <div className="text-purple-300 text-sm mb-1">
                                      {edu.degree}
                                      {edu.field_of_study && <span className="text-gray-400"> • {edu.field_of_study}</span>}
                                    </div>
                                  )}
                                  <div className="flex items-center space-x-3 text-gray-400 text-xs">
                                    {edu.start && edu.end && (
                                      <span className="flex items-center space-x-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span>{edu.start} - {edu.end}</span>
                                      </span>
                                    )}
                                    {edu.location && (
                                      <span className="flex items-center space-x-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span>{edu.location}</span>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Skills */}
                      {enrichment.skills && enrichment.skills.length > 0 && (
                        <div className="bg-black/30 border border-purple-500/20 rounded-lg p-3">
                          <div className="text-xs text-purple-400 mb-2 font-mono">[SKILLS]</div>
                          <div className="flex flex-wrap gap-2">
                            {enrichment.skills.slice(0, 10).map((skill: string, skillIndex: number) => (
                              <Badge key={skillIndex} variant="secondary" className="bg-purple-600/20 text-purple-300 border-purple-500/30">
                                {skill}
                              </Badge>
                            ))}
                            {enrichment.skills.length > 10 && (
                              <Badge variant="secondary" className="bg-purple-600/20 text-purple-300 border-purple-500/30">
                                +{enrichment.skills.length - 10} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Additional Emails */}
                      {enrichment.emails && enrichment.emails.length > 0 && (
                        <div className="bg-black/30 border border-purple-500/20 rounded-lg p-3">
                          <div className="text-xs text-purple-400 mb-2 font-mono">[ADDITIONAL EMAILS]</div>
                          <div className="space-y-1">
                            {enrichment.emails.slice(0, 5).map((emailObj: any, emailIndex: number) => {
                              const email = typeof emailObj === 'string' ? emailObj : emailObj.email
                              const emailType = typeof emailObj === 'object' ? emailObj.type : 'unknown'
                              const isValid = typeof emailObj === 'object' ? emailObj.smtp_valid === 'valid' : true
                              return (
                                <div key={emailIndex} className="text-white text-sm">
                                  {email}
                                  <span className="text-purple-300 text-xs ml-2">({emailType})</span>
                                  {!isValid && <span className="text-red-400 text-xs ml-2">[Invalid]</span>}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Recommended Emails */}
                      {(enrichment.recommended_email || enrichment.recommended_personal_email || enrichment.recommended_professional_email) && (
                        <div className="bg-black/30 border border-purple-500/20 rounded-lg p-3">
                          <div className="text-xs text-purple-400 mb-2 font-mono">[RECOMMENDED EMAILS]</div>
                          <div className="space-y-1">
                            {enrichment.recommended_email && (
                              <div className="text-white text-sm">{enrichment.recommended_email}</div>
                            )}
                            {enrichment.recommended_personal_email && (
                              <div className="text-white text-sm">{enrichment.recommended_personal_email} <span className="text-purple-300 text-xs">(Personal)</span></div>
                            )}
                            {enrichment.recommended_professional_email && (
                              <div className="text-white text-sm">{enrichment.recommended_professional_email} <span className="text-purple-300 text-xs">(Professional)</span></div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Sanctions Alert Banner */}
              {profileData.sanctionsData && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded border px-4 py-3 flex items-start gap-3 ${
                    profileData.sanctionsData.is_sanctioned
                      ? 'bg-red-950/60 border-red-500/70'
                      : 'bg-green-950/40 border-green-700/50'
                  }`}
                >
                  <span className="text-xl shrink-0 mt-0.5">
                    {profileData.sanctionsData.is_sanctioned ? '⛔' : '✅'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-bold font-mono uppercase ${profileData.sanctionsData.is_sanctioned ? 'text-red-400' : 'text-green-400'}`}>
                        {profileData.sanctionsData.is_sanctioned ? 'SANCTIONED ENTITY DETECTED' : 'NO SANCTIONS MATCH'}
                      </span>
                      <span className="text-xs text-slate-400 font-mono">OpenSanctions (200+ lists)</span>
                      {profileData.sanctionsData.is_sanctioned && (
                        <span className="text-xs bg-red-600 text-white px-1.5 py-0.5 rounded font-mono">
                          score {profileData.sanctionsData.highest_score.toFixed(2)}
                        </span>
                      )}
                    </div>
                    {profileData.sanctionsData.is_sanctioned && profileData.sanctionsData.hits.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {profileData.sanctionsData.hits.slice(0, 3).map((hit: any, i: number) => (
                          <div key={i} className="text-xs text-red-300/80 font-mono">
                            {hit.caption}
                            {hit.datasets && hit.datasets.length > 0 && (
                              <span className="text-red-500/60 ml-2">[{hit.datasets.slice(0, 3).join(', ')}]</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {profileData.sanctionsData.is_sanctioned && profileData.sanctionsData.list_coverage && profileData.sanctionsData.list_coverage.length > 0 && (
                      <div className="mt-1 text-xs text-red-400/60 font-mono">
                        Lists: {profileData.sanctionsData.list_coverage.slice(0, 5).join(', ')}
                        {profileData.sanctionsData.list_coverage.length > 5 && ` +${profileData.sanctionsData.list_coverage.length - 5} more`}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* OCCRP Aleph Intelligence - Critical Investigative Data */}
              {(profileData.occrpAlephData || (graph?.nodes && graph.nodes.some((n: any) => n.source === 'occrp_aleph'))) && (
                <motion.div 
                  data-occrp-section
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-orange-500">🔍</span>
                    <h3 className="text-sm font-medium text-orange-400 font-mono">[OCCRP ALEPH INTELLIGENCE]</h3>
                    <Badge variant="default" className="bg-orange-600">CRITICAL</Badge>
                    <Badge variant="outline" className="border-orange-500 text-orange-400 text-xs">
                      {profileData.occrpAlephData?.total_results || 0} Results
                    </Badge>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-900/20 via-black to-orange-900/20 border border-orange-500/30 rounded-lg p-6 space-y-4 shadow-[0_0_30px_rgba(249,115,22,0.2)]">
                    <div className="text-xs text-orange-300 font-mono mb-4">
                      Open Source Intelligence from OCCRP Aleph - Investigative Database
                    </div>
                    
                    {(() => {
                      // Show results if we have extracted entities
                      const hasExtractedResults = profileData.occrpAlephData?.results && profileData.occrpAlephData.results.length > 0
                      
                      // Also check if we have raw_results in summary node (fallback)
                      const occrpSummaryNode = graph?.nodes?.find((n: any) => n.source === 'occrp_aleph' && n.type === 'source' && n.id?.startsWith('occrp_aleph_summary:'))
                      const rawResults = occrpSummaryNode?.props?.raw_results
                      const hasRawResults = rawResults && Array.isArray(rawResults) && rawResults.length > 0
                      
                      // Use extracted results if available, otherwise try raw_results
                      const resultsToDisplay = hasExtractedResults 
                        ? (profileData.occrpAlephData?.results || [])
                        : (hasRawResults ? rawResults.map((entity: any) => {
                            const collection = entity.collection || {}
                            const collectionLabel = typeof collection === 'object' ? collection.label || '' : collection || ''
                            const properties = entity.properties || {}
                            const schema = entity.schema || 'Thing'
                            
                            // Extract name
                            let entityName = 'Unknown'
                            if (schema === 'Pages' || schema === 'Document') {
                              entityName = properties.fileName?.[0] || properties.title?.[0] || properties.name?.[0] || `Document ${entity.id?.substring(0, 20)}`
                            } else {
                              entityName = properties.name?.[0] || properties.alias?.[0] || 'Unknown Entity'
                            }
                            
                            // Ensure country and address are always arrays
                            const countries = Array.isArray(properties.country)
                              ? properties.country
                              : (properties.country ? [properties.country] : []);
                            const addresses = Array.isArray(properties.address)
                              ? properties.address
                              : (properties.address ? [properties.address] : []);
                            
                            return {
                              id: entity.id || '',
                              schema: schema,
                              name: entityName,
                              collection_label: collectionLabel,
                              collection_id: typeof collection === 'object' ? collection.collection_id || '' : '',
                              country: countries,
                              address: addresses,
                              highlight: entity.highlight || [],
                              ui_link: entity.links?.ui || '',
                              api_link: entity.links?.self || '',
                              score: entity.score || 0,
                              properties: properties
                            }
                          }) : [])
                      
                      if (resultsToDisplay && resultsToDisplay.length > 0) {
                        return resultsToDisplay.map((result: any, index: number) => (
                      <motion.div
                        key={result.id || index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-black/40 border border-orange-500/20 rounded-lg p-4 space-y-3 hover:border-orange-400/40 transition-colors"
                      >
                        {/* Entity Header */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <Badge className={`${
                                result.schema === 'Person' ? 'bg-blue-600' :
                                result.schema === 'Company' ? 'bg-green-600' :
                                'bg-gray-600'
                              } text-white text-xs font-mono`}>
                                {result.schema}
                              </Badge>
                              <h4 className="text-orange-300 font-mono font-bold text-sm">
                                {result.name}
                              </h4>
                              {result.score && (
                                <Badge variant="outline" className="border-orange-500/50 text-orange-400 text-xs">
                                  Score: {result.score.toFixed(1)}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Collection Information - CRITICAL */}
                            {result.collection_label && (
                              <div className="mb-3 p-3 bg-orange-900/30 border border-orange-500/30 rounded-lg">
                                <div className="text-xs text-orange-400 font-mono mb-1">[SOURCE DATASET]</div>
                                <div className="text-orange-200 font-semibold text-sm">
                                  {result.collection_label}
                                </div>
                                {result.collection_id && (
                                  <div className="text-orange-500/50 text-xs font-mono mt-1">
                                    Collection ID: {result.collection_id}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Highlighted Snippets - Shows where name appears */}
                            {result.highlight && result.highlight.length > 0 && (
                              <div className="mb-3 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                                <div className="text-xs text-yellow-400 font-mono mb-2">[MENTIONS & CONTEXT]</div>
                                <div className="space-y-2">
                                  {result.highlight.slice(0, 3).map((snippet: string, snippetIndex: number) => (
                                    <div 
                                      key={snippetIndex}
                                      className="text-yellow-200 text-sm leading-relaxed"
                                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(snippet, { ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'mark'] }) }}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Location Information */}
                            {(() => {
                              // Normalize country and address to arrays
                              const countries = Array.isArray(result.country)
                                ? result.country
                                : (result.country ? [result.country] : []);
                              const addresses = Array.isArray(result.address)
                                ? result.address
                                : (result.address ? [result.address] : []);
                              
                              return (countries.length > 0 || addresses.length > 0) ? (
                                <div className="mb-3">
                                  <div className="text-xs text-orange-400 font-mono mb-2">[LOCATION INTELLIGENCE]</div>
                                  <div className="space-y-1">
                                    {countries.length > 0 && (
                                      <div className="flex flex-wrap gap-2">
                                        {countries.map((country: string, countryIndex: number) => (
                                          <Badge key={countryIndex} variant="secondary" className="bg-orange-600/20 text-orange-300 border-orange-500/30">
                                            {country}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                    {addresses.length > 0 && (
                                      <div className="text-white text-sm mt-2">
                                        {addresses[0]}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : null;
                            })()}
                            
                            {/* Additional Properties */}
                            {result.properties && Object.keys(result.properties).length > 0 && (
                              <div className="mb-3">
                                <div className="text-xs text-orange-400 font-mono mb-2">[ADDITIONAL INTELLIGENCE]</div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  {Object.entries(result.properties).slice(0, 6).map(([key, value]: [string, any]) => {
                                    if (key === 'name' || key === 'alias' || key === 'collection_label' || key === 'highlight') return null
                                    if (!value || (Array.isArray(value) && value.length === 0)) return null
                                    const displayValue = Array.isArray(value) ? value.join(', ') : String(value)
                                    if (displayValue.length > 50) return null
                                    return (
                                      <div key={key} className="text-orange-200">
                                        <span className="text-orange-400 font-mono">{key}:</span> {displayValue}
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                            
                            {/* Links to OCCRP Aleph */}
                            {result.ui_link && (
                              <div className="mt-4 pt-3 border-t border-orange-500/20">
                                <a
                                  href={result.ui_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center space-x-2 text-orange-400 hover:text-orange-300 underline text-sm font-mono"
                                >
                                  <span>View on OCCRP Aleph</span>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                        </motion.div>
                      ))
                      } else {
                        return null
                      }
                    })()}
                    
                    {/* Show pagination info if we have results */}
                    {profileData.occrpAlephData?.results && profileData.occrpAlephData.results.length > 0 && profileData.occrpAlephData.total_results > profileData.occrpAlephData.results.length && (
                      <div className="text-center pt-4 border-t border-orange-500/20">
                        <div className="text-orange-400 text-xs font-mono">
                          Showing {profileData.occrpAlephData.results.length} of {profileData.occrpAlephData.total_results} results
                        </div>
                        <div className="text-orange-500/50 text-xs font-mono mt-1">
                          Visit OCCRP Aleph for complete results
                        </div>
                      </div>
                    )}
                    
                    {/* Show message if OCCRP was searched but no data extracted */}
                    {(!profileData.occrpAlephData || !profileData.occrpAlephData.results || profileData.occrpAlephData.results.length === 0) && (
                      <div className="text-center py-8">
                        <div className="text-orange-400 font-mono text-sm mb-2">[OCCRP ALEPH SEARCH COMPLETED]</div>
                        <div className="text-orange-500/70 font-mono text-xs mb-3">
                          {profileData.occrpAlephData?.total_results === 0 
                            ? "No matching entities found in OCCRP Aleph database"
                            : "OCCRP data found but extraction may have failed. Check browser console for details."}
                        </div>
                        <div className="text-orange-600/50 font-mono text-xs">
                          Total results: {profileData.occrpAlephData?.total_results || 0}
                        </div>
                        {graph?.nodes && (
                          <div className="text-orange-600/50 font-mono text-xs mt-2">
                            OCCRP nodes in graph: {graph.nodes.filter((n: any) => n.source === 'occrp_aleph').length}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Gmail OSINT Data */}
              {profileData.gmailOSINT && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-blue-500">📧</span>
                    <h3 className="text-sm font-medium text-blue-400 font-mono">[GMAIL OSINT INTELLIGENCE]</h3>
                    <Badge variant="default" className="bg-blue-600">ACTIVE</Badge>
                  </div>
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 space-y-3">
                    {/* Profile Picture */}
                    {profileData.gmailOSINT.profile_picture && (
                      <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                        <div className="text-xs text-blue-400 mb-2 font-mono">[PROFILE PICTURE]</div>
                        <div className="relative">
                          <img 
                            src={profileData.gmailOSINT.profile_picture} 
                            alt="Gmail profile"
                            className="w-32 h-32 object-cover rounded-lg border border-blue-500/30"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              console.error('Failed to load Gmail profile picture:', profileData.gmailOSINT?.profile_picture)
                              const img = e.target as HTMLImageElement
                              // Try adding size parameter if not present
                              if (!img.src.includes('=s')) {
                                const newUrl = img.src.split('?')[0] + '=s256-c'
                                console.log('Retrying with size parameter:', newUrl)
                                img.src = newUrl
                              } else {
                                img.style.display = 'none'
                                // Show fallback
                                const parent = img.parentElement
                                if (parent) {
                                  const fallback = document.createElement('div')
                                  fallback.className = 'w-32 h-32 bg-gray-700 rounded-lg border border-blue-500/30 flex items-center justify-center text-blue-400 text-xs font-mono'
                                  fallback.textContent = 'Image unavailable'
                                  parent.appendChild(fallback)
                                }
                              }
                            }}
                            onLoad={(e) => {
                              const imgElement = e.target as HTMLImageElement
                              imgElement.style.display = 'block'
                            }}
                          />
                        </div>
                        <div className="text-xs text-blue-300 mt-2 font-mono break-all">
                          {profileData.gmailOSINT.profile_picture}
                        </div>
                      </div>
                    )}
                    
                    {/* Google Account Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {profileData.gmailOSINT.gaia_id && (
                        <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                          <div className="text-xs text-blue-400 mb-1 font-mono">[GAIA ID]</div>
                          <div className="text-white font-medium text-sm">{profileData.gmailOSINT.gaia_id}</div>
                        </div>
                      )}
                      
                      {profileData.gmailOSINT.last_profile_edit && (
                        <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                          <div className="text-xs text-blue-400 mb-1 font-mono">[LAST PROFILE EDIT]</div>
                          <div className="text-white font-medium text-sm">{profileData.gmailOSINT.last_profile_edit}</div>
                        </div>
                      )}
                      
                      {profileData.gmailOSINT.entity_type && (
                        <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                          <div className="text-xs text-blue-400 mb-1 font-mono">[ENTITY TYPE]</div>
                          <div className="text-white font-medium text-sm">{profileData.gmailOSINT.entity_type}</div>
                        </div>
                      )}
                      
                      {profileData.gmailOSINT.enterprise_user !== undefined && (
                        <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                          <div className="text-xs text-blue-400 mb-1 font-mono">[ENTERPRISE USER]</div>
                          <div className="text-white font-medium text-sm">{profileData.gmailOSINT.enterprise_user ? 'Yes' : 'No'}</div>
                        </div>
                      )}
                    </div>
                    
                    {/* Activated Services */}
                    {profileData.gmailOSINT.activated_services && profileData.gmailOSINT.activated_services.length > 0 && (
                      <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                        <div className="text-xs text-blue-400 mb-2 font-mono">[ACTIVATED GOOGLE SERVICES]</div>
                        <div className="flex flex-wrap gap-2">
                          {profileData.gmailOSINT.activated_services.map((service: string, index: number) => (
                            <Badge key={index} variant="secondary" className="bg-blue-600/20 text-blue-300 border-blue-500/30">
                              {service}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Maps Profile */}
                    {profileData.gmailOSINT.maps_profile && (
                      <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                        <div className="text-xs text-blue-400 mb-1 font-mono">[GOOGLE MAPS PROFILE]</div>
                        <a 
                          href={profileData.gmailOSINT.maps_profile} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-300 hover:text-blue-100 underline text-sm break-all"
                        >
                          {profileData.gmailOSINT.maps_profile}
                        </a>
                      </div>
                    )}
                    
                    {/* Cover Picture */}
                    {profileData.gmailOSINT.cover_picture && (
                      <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                        <div className="text-xs text-blue-400 mb-2 font-mono">[COVER PICTURE]</div>
                        <div className="relative">
                          <img 
                            src={profileData.gmailOSINT.cover_picture} 
                            alt="Gmail cover"
                            className="w-full max-w-md h-32 object-cover rounded-lg border border-blue-500/30"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement
                              img.style.display = 'none'
                            }}
                          />
                        </div>
                        <div className="text-xs text-blue-300 mt-2 font-mono break-all">
                          {profileData.gmailOSINT.cover_picture}
                        </div>
                      </div>
                    )}
                    
                    {/* Customer ID */}
                    {profileData.gmailOSINT.customer_id && (
                      <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                        <div className="text-xs text-blue-400 mb-1 font-mono">[CUSTOMER ID]</div>
                        <div className="text-white font-medium text-sm font-mono">{profileData.gmailOSINT.customer_id}</div>
                      </div>
                    )}
                    
                    {/* User Types */}
                    {profileData.gmailOSINT.user_types && profileData.gmailOSINT.user_types.length > 0 && (
                      <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                        <div className="text-xs text-blue-400 mb-2 font-mono">[USER TYPES]</div>
                        <div className="flex flex-wrap gap-2">
                          {profileData.gmailOSINT.user_types.map((type: string, index: number) => (
                            <Badge key={index} variant="secondary" className="bg-blue-600/20 text-blue-300 border-blue-500/30">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Play Games */}
                    {profileData.gmailOSINT.play_games && (
                      <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                        <div className="text-xs text-blue-400 mb-1 font-mono">[PLAY GAMES PROFILE]</div>
                        <a 
                          href={profileData.gmailOSINT.play_games} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-300 hover:text-blue-100 underline text-sm break-all"
                        >
                          {profileData.gmailOSINT.play_games}
                        </a>
                      </div>
                    )}
                    
                    {/* Calendar */}
                    {profileData.gmailOSINT.calendar && (
                      <div className="bg-black/30 border border-blue-500/20 rounded-lg p-3">
                        <div className="text-xs text-blue-400 mb-1 font-mono">[GOOGLE CALENDAR]</div>
                        <a 
                          href={profileData.gmailOSINT.calendar} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-300 hover:text-blue-100 underline text-sm break-all"
                        >
                          {profileData.gmailOSINT.calendar}
                        </a>
                      </div>
                    )}
                    
                    {/* Gmail OSINT warnings hidden - not useful for end users */}
                  </div>
                </div>
              )}

              {/* Address Information */}
              {Object.keys(profileData.addresses).length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-red-500">📍</span>
                    <h3 className="text-sm font-medium text-red-400 font-mono">[LOCATION INTELLIGENCE]</h3>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {profileData.addresses.fullAddress && (
                      <div className="bg-black/50 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[FULL ADDRESS]</div>
                        <div className="text-white font-medium">{profileData.addresses.fullAddress}</div>
                      </div>
                    )}
                    {profileData.addresses.city && (
                      <div className="bg-black/50 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[CITY]</div>
                        <div className="text-white font-medium">{profileData.addresses.city}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Stealer Logs (Hudson Rock) */}
              {profileData.stealerLogs && Array.isArray(profileData.stealerLogs) && profileData.stealerLogs.length > 0 && (
                <div className="bg-black/50 border border-red-500/30 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <span className="text-red-500">⚠️</span>
                    <h3 className="text-white font-mono">[STEALER LOGS]</h3>
                    <Badge className="bg-red-600 text-white text-xs font-mono ml-2">
                      {profileData.stealerLogs.length} INFECTION{profileData.stealerLogs.length > 1 ? 'S' : ''}
                    </Badge>
                  </div>

                  {showSelectionMode && (
                    <SelectionToolbar
                      total={profileData.stealerLogs.length}
                      selectedCount={selectedItems.stealerLogs.size}
                      onSelectAll={() => setSelectedItems(prev => ({ ...prev, stealerLogs: new Set((profileData.stealerLogs || []).map((_: any, i: number) => String(i))) }))}
                      onClear={() => setSelectedItems(prev => ({ ...prev, stealerLogs: new Set() }))}
                      hideUnselected={hideUnselected}
                      onToggleHideUnselected={() => setHideUnselected(v => !v)}
                    />
                  )}

                  <div className="space-y-4 max-h-[600px] overflow-y-auto mt-3">
                    {profileData.stealerLogs
                      .map((stealer, index) => ({ stealer, index }))
                      .filter(({ index }) => !(showSelectionMode && hideUnselected) || selectedItems.stealerLogs.has(String(index)))
                      .map(({ stealer, index }) => (
                      <div key={index} className={`bg-red-900/20 border rounded-lg p-4 ${showSelectionMode && selectedItems.stealerLogs.has(String(index)) ? 'ring-2 ring-blue-500 border-blue-500' : 'border-red-500/30'}`}>
                        {showSelectionMode && (
                          <div className="mb-3 flex items-center space-x-2 p-2 bg-blue-900/10 rounded border border-blue-500/30">
                            <input
                              type="checkbox"
                              checked={selectedItems.stealerLogs.has(String(index))}
                              onChange={(e) => {
                                setSelectedItems(prev => {
                                  const newSet = new Set(prev.stealerLogs)
                                  if (e.target.checked) newSet.add(String(index)); else newSet.delete(String(index))
                                  return { ...prev, stealerLogs: newSet }
                                })
                              }}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className={`text-xs font-mono ${selectedItems.stealerLogs.has(String(index)) ? 'text-blue-400 font-bold' : 'text-gray-400'}`}>
                              {selectedItems.stealerLogs.has(String(index)) ? '✓ Selected for Report & Analysis' : 'Select for Report & Analysis'}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-red-400 font-mono text-xs">[STEALER #{index + 1}]</span>
                            <Badge className="bg-red-600 text-white text-xs">{stealer.stealer_family}</Badge>
                          </div>
                          <div className="text-red-300 text-xs font-mono">
                            {stealer.date_compromised ? new Date(stealer.date_compromised).toLocaleDateString() : 'Unknown Date'}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">COMPUTER NAME</div>
                            <div className="text-white">{stealer.computer_name || 'Not Found'}</div>
                          </div>
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">OPERATING SYSTEM</div>
                            <div className="text-white">{stealer.operating_system || 'Unknown'}</div>
                          </div>
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">IP ADDRESS</div>
                            <div className="text-white font-mono">{stealer.ip || 'Not Found'}</div>
                          </div>
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">MALWARE PATH</div>
                            <div className="text-white font-mono text-xs break-all">{stealer.malware_path || 'Not Found'}</div>
                          </div>
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">CORPORATE SERVICES</div>
                            <div className="text-white font-bold">{stealer.total_corporate_services}</div>
                          </div>
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">USER SERVICES</div>
                            <div className="text-white font-bold">{stealer.total_user_services}</div>
                          </div>
                        </div>
                        
                        {stealer.antiviruses && stealer.antiviruses.length > 0 && (
                          <div className="mt-3">
                            <div className="text-red-400 text-xs font-mono mb-1">ANTIVIRUS DETECTED</div>
                            <div className="flex flex-wrap gap-2">
                              {stealer.antiviruses.map((av, idx) => (
                                <Badge key={idx} variant="secondary" className="bg-yellow-600/20 text-yellow-300 border-yellow-500/30">
                                  {av}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {stealer.top_passwords && stealer.top_passwords.length > 0 && (
                          <div className="mt-3">
                            <div className="text-red-400 text-xs font-mono mb-1">TOP PASSWORDS (REDACTED)</div>
                            <div className="flex flex-wrap gap-2">
                              {stealer.top_passwords.slice(0, 5).map((pwd, idx) => (
                                <Badge key={idx} variant="secondary" className="bg-red-600/20 text-red-300 border-red-500/30 font-mono">
                                  {pwd}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {stealer.top_logins && stealer.top_logins.length > 0 && (
                          <div className="mt-3">
                            <div className="text-red-400 text-xs font-mono mb-1">TOP LOGINS</div>
                            <div className="flex flex-wrap gap-2">
                              {stealer.top_logins.slice(0, 5).map((login, idx) => (
                                <Badge key={idx} variant="secondary" className="bg-blue-600/20 text-blue-300 border-blue-500/30 font-mono text-xs">
                                  {login}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* GEOINT Map - Embedded in Overview */}
              {profileData.mapLocations && profileData.mapLocations.length > 0 && (
                <div className="bg-black/50 border border-red-500/30 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <span className="text-red-500">📍</span>
                    <h3 className="text-white font-mono">[GEOINT MAP - LOCATION INTELLIGENCE]</h3>
                    <Badge className="bg-red-600 text-white text-xs font-mono ml-2">
                      {profileData.mapLocations.length} LOCATION{profileData.mapLocations.length > 1 ? 'S' : ''}
                    </Badge>
                  </div>
                  <div className="h-[500px] w-full rounded-lg overflow-hidden border border-red-500/30">
                    <MapContainer
                      center={[profileData.mapLocations[0].lat, profileData.mapLocations[0].lng]}
                      zoom={profileData.mapLocations.length === 1 ? 15 : 3}
                      style={{ height: '100%', width: '100%' }}
                      className="z-0"
                    >
                      <TileLayer
                        attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      />
                      <TileLayer
                        attribution=''
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                        opacity={0.8}
                      />
                      {profileData.mapLocations.map((location, index) => {
                        const icon = L.icon({
                          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
                          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
                          iconSize: [25, 41],
                          iconAnchor: [12, 41],
                          popupAnchor: [1, -34],
                          shadowSize: [41, 41]
                        })
                        
                        return (
                          <Marker
                            key={index}
                            position={[location.lat, location.lng]}
                            icon={icon}
                          >
                            <Popup>
                              <div className="text-sm">
                                <div className="font-bold text-red-600 mb-2">{location.label}</div>
                                {location.address && <div><strong>Address:</strong> {location.address}</div>}
                                {location.city && <div><strong>City:</strong> {location.city}</div>}
                                {location.country && <div><strong>Country:</strong> {location.country}</div>}
                                <div className="mt-2 text-xs text-gray-500">
                                  <strong>Source:</strong> {location.source}
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                        )
                      })}
                    </MapContainer>
                  </div>
                </div>
              )}

              {/* PII Data Sections */}
              {Object.keys(profileData.personalInfo).length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-400">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {profileData.personalInfo.fullName && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Full Name</div>
                        <div className="text-white font-medium">{profileData.personalInfo.fullName}</div>
                      </div>
                    )}
                    {profileData.personalInfo.dob && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Date of Birth</div>
                        <div className="text-white font-medium">{profileData.personalInfo.dob}</div>
                      </div>
                    )}
                    {profileData.personalInfo.gender && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Gender</div>
                        <div className="text-white font-medium">{profileData.personalInfo.gender}</div>
                      </div>
                    )}
                    {profileData.personalInfo.nationality && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Nationality</div>
                        <div className="text-white font-medium">{profileData.personalInfo.nationality}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Details */}
              {Object.keys(profileData.contactDetails).length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-400">Contact Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {profileData.contactDetails.email && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Primary Email</div>
                        <div className="text-white font-medium break-all">{profileData.contactDetails.email}</div>
                      </div>
                    )}
                    {profileData.contactDetails.alternativeEmails && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Alternative Emails</div>
                        <div className="text-white font-medium break-all">{profileData.contactDetails.alternativeEmails}</div>
                      </div>
                    )}
                    {profileData.contactDetails.phone && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Primary Phone</div>
                        <div className="text-white font-medium">{profileData.contactDetails.phone}</div>
                      </div>
                    )}
                    {profileData.contactDetails.phoneNumbers && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Additional Phone Numbers</div>
                        <div className="text-white font-medium">{profileData.contactDetails.phoneNumbers}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Social Media */}
              {Object.keys(profileData.socialMedia).length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-400">Social Media</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {profileData.socialMedia.linkedin && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">LinkedIn</div>
                        <a href={profileData.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline break-all">
                          {profileData.socialMedia.linkedin}
                        </a>
                      </div>
                    )}
                    {profileData.socialMedia.facebook && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Facebook</div>
                        <a href={profileData.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline break-all">
                          {profileData.socialMedia.facebook}
                        </a>
                      </div>
                    )}
                    {profileData.socialMedia.twitter && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Twitter/X</div>
                        <a href={profileData.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline break-all">
                          {profileData.socialMedia.twitter}
                        </a>
                      </div>
                    )}
                    {profileData.socialMedia.instagram && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Instagram</div>
                        <a href={profileData.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline break-all">
                          {profileData.socialMedia.instagram}
                        </a>
                      </div>
                    )}
                    {profileData.socialMedia.vk && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">VK (VKontakte)</div>
                        <a href={profileData.socialMedia.vk} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline break-all">
                          {profileData.socialMedia.vk}
                        </a>
                      </div>
                    )}
                    {profileData.socialMedia.socialProfiles && (
                      <div className="bg-slate-800/50 rounded-lg p-3 md:col-span-2">
                        <div className="text-xs text-slate-400 mb-1">Additional Social Profiles</div>
                        <div className="text-white font-medium break-all">{profileData.socialMedia.socialProfiles}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Government IDs */}
              {Object.keys(profileData.governmentIDs).length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-orange-500">🆔</span>
                    <h3 className="text-sm font-medium text-orange-400 font-mono">[GOVERNMENT IDENTIFICATION]</h3>
                    <Badge className="bg-orange-600 text-white text-xs">CRITICAL</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {profileData.governmentIDs.ssn && (
                      <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-3">
                        <div className="text-xs text-orange-400 mb-1 font-mono">[SSN]</div>
                        <div className="text-orange-300 font-medium font-mono">{profileData.governmentIDs.ssn}</div>
                      </div>
                    )}
                    {profileData.governmentIDs.tin && (
                      <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-3">
                        <div className="text-xs text-orange-400 mb-1 font-mono">[TIN/TAX ID]</div>
                        <div className="text-orange-300 font-medium font-mono">{profileData.governmentIDs.tin}</div>
                      </div>
                    )}
                    {profileData.governmentIDs.passport && (
                      <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-3">
                        <div className="text-xs text-orange-400 mb-1 font-mono">[PASSPORT]</div>
                        <div className="text-orange-300 font-medium font-mono">{profileData.governmentIDs.passport}</div>
                      </div>
                    )}
                    {profileData.governmentIDs.driverLicense && (
                      <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-3">
                        <div className="text-xs text-orange-400 mb-1 font-mono">[DRIVER LICENSE]</div>
                        <div className="text-orange-300 font-medium font-mono">{profileData.governmentIDs.driverLicense}</div>
                      </div>
                    )}
                    {profileData.governmentIDs.nationalID && (
                      <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-3">
                        <div className="text-xs text-orange-400 mb-1 font-mono">[NATIONAL ID]</div>
                        <div className="text-orange-300 font-medium font-mono">{profileData.governmentIDs.nationalID}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Financial Data */}
              {Object.keys(profileData.financial).length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-red-500">💰</span>
                    <h3 className="text-sm font-medium text-red-400 font-mono">[FINANCIAL INFORMATION]</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {profileData.financial.password && (
                      <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[EXPOSED PASSWORD]</div>
                        <div className="text-red-300 font-medium font-mono break-all">{profileData.financial.password}</div>
                      </div>
                    )}
                    {profileData.financial.paymentMethod && (
                      <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-3">
                        <div className="text-xs text-yellow-400 mb-1 font-mono">[PAYMENT METHOD]</div>
                        <div className="text-yellow-300 font-medium">{profileData.financial.paymentMethod}</div>
                      </div>
                    )}
                    {profileData.financial.balance && (
                      <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-3">
                        <div className="text-xs text-green-400 mb-1 font-mono">[ACCOUNT BALANCE]</div>
                        <div className="text-green-300 font-medium">{profileData.financial.balance}</div>
                      </div>
                    )}
                    {profileData.financial.creditCard && (
                      <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[CREDIT CARD]</div>
                        <div className="text-red-300 font-medium font-mono">{profileData.financial.creditCard}</div>
                      </div>
                    )}
                    {profileData.financial.bankAccount && (
                      <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3">
                        <div className="text-xs text-red-400 mb-1 font-mono">[BANK ACCOUNT]</div>
                        <div className="text-red-300 font-medium font-mono">{profileData.financial.bankAccount}</div>
                      </div>
                    )}
                    {profileData.financial.financialData && (
                      <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-3 md:col-span-2">
                        <div className="text-xs text-yellow-400 mb-1 font-mono">[ADDITIONAL FINANCIAL DATA]</div>
                        <div className="text-yellow-300 font-medium break-all">{profileData.financial.financialData}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Photos */}
              {profileData.photos && profileData.photos.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-purple-500">📷</span>
                    <h3 className="text-sm font-medium text-purple-400 font-mono">[PHOTOS & IMAGES]</h3>
                    <Badge className="bg-purple-600 text-white text-xs">{profileData.photos.length}</Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {profileData.photos.map((photo: any, index: number) => (
                      <div key={index} className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                        <div className="relative mb-2">
                          <img 
                            src={photo.url} 
                            alt={photo.type || 'Photo'}
                            className="w-full h-32 object-cover rounded-lg border border-purple-500/30"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement
                              img.style.display = 'none'
                              const parent = img.parentElement
                              if (parent) {
                                const fallback = document.createElement('div')
                                fallback.className = 'w-full h-32 bg-gray-700 rounded-lg border border-purple-500/30 flex items-center justify-center text-purple-400 text-xs'
                                fallback.textContent = 'Image unavailable'
                                parent.appendChild(fallback)
                              }
                            }}
                          />
                        </div>
                        <div className="text-xs text-purple-400 mb-1 font-mono">{photo.type || 'Photo'}</div>
                        <div className="text-xs text-purple-300 break-all">{photo.source || 'Unknown'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Address Information */}
              {Object.keys(profileData.addresses).length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-400">Address Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {profileData.addresses.fullAddress && (
                      <div className="bg-slate-800/50 rounded-lg p-3 md:col-span-2">
                        <div className="text-xs text-slate-400 mb-1">Full Address</div>
                        <div className="text-white font-medium">{profileData.addresses.fullAddress}</div>
                      </div>
                    )}
                    {profileData.addresses.street && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Street</div>
                        <div className="text-white font-medium">{profileData.addresses.street}</div>
                      </div>
                    )}
                    {profileData.addresses.city && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">City</div>
                        <div className="text-white font-medium">{profileData.addresses.city}</div>
                      </div>
                    )}
                    {profileData.addresses.state && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">State/Province</div>
                        <div className="text-white font-medium">{profileData.addresses.state}</div>
                      </div>
                    )}
                    {profileData.addresses.country && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Country</div>
                        <div className="text-white font-medium">{profileData.addresses.country}</div>
                      </div>
                    )}
                    {profileData.addresses.postCode && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">Postal Code</div>
                        <div className="text-white font-medium">{profileData.addresses.postCode}</div>
                      </div>
                    )}
                    {(profileData.addresses.latitude || profileData.addresses.longitude) && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-1">GPS Coordinates</div>
                        <div className="text-white font-medium font-mono">
                          {profileData.addresses.latitude && profileData.addresses.longitude 
                            ? `${profileData.addresses.latitude}, ${profileData.addresses.longitude}`
                            : profileData.addresses.latitude || profileData.addresses.longitude}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </SourceIntelPanel>
          </>)}

          {/* BREACHES TAB */}
          {activeTab === 'breaches' && (<>
          {(() => {
            // A breach entry is real if it has actual field data (not an ES "no results" error message)
            const isEmptyBreach = (b: any) => {
              const date = b.date || ''
              const hasHtml = date.includes('<em>') || date.toLowerCase().includes('no results were found')
              const zeroRecords = parseInt(b.records || '0') === 0
              const noActualRecords = !b.allRecords || b.allRecords.length === 0
              return hasHtml || (zeroRecords && noActualRecords)
            }
            // Carry each breach's index in the ORIGINAL profileData.breachSources
            // array through the "real" filter — selection state, the export
            // filter (getFilteredBreachSources), and this list must all agree
            // on the same index for the same breach, or a checkbox can end up
            // recording a completely different breach than the one clicked.
            // (Previously this used realBreaches.map's own index — which only
            // matched the original array when zero empty breaches preceded a
            // given breach — silently selecting/exporting the wrong record
            // whenever any source came back with "no results.")
            const indexedBreaches = profileData.breachSources
              .map((b: any, originalIndex: number) => ({ b, originalIndex }))
            const realBreaches = indexedBreaches.filter(({ b }) => !isEmptyBreach(b))
            const emptyBreaches = indexedBreaches.filter(({ b }) => isEmptyBreach(b))
            const visibleBreaches = (showSelectionMode && hideUnselected)
              ? realBreaches.filter(({ originalIndex }) => selectedItems.breaches.has(String(originalIndex)))
              : realBreaches
            return (
          <SourceIntelPanel
            title="Breach Intelligence"
            icon="🗄️"
            color="red"
            itemCount={visibleBreaches.length}
            countLabel="breaches"
            description="Individual breach records with exposed data fields from LeakOSINT and Elasticsearch"
            defaultExpanded={true}
          >
              <div className="space-y-4">
                {showSelectionMode && realBreaches.length > 0 && (
                  <SelectionToolbar
                    total={realBreaches.length}
                    selectedCount={realBreaches.filter(({ originalIndex }) => selectedItems.breaches.has(String(originalIndex))).length}
                    onSelectAll={() => setSelectedItems(prev => ({ ...prev, breaches: new Set(realBreaches.map(({ originalIndex }) => String(originalIndex))) }))}
                    onClear={() => setSelectedItems(prev => ({ ...prev, breaches: new Set() }))}
                    hideUnselected={hideUnselected}
                    onToggleHideUnselected={() => setHideUnselected(v => !v)}
                  />
                )}
                {realBreaches.length === 0 && (
                  <div className="text-center py-8 text-gray-500 font-mono text-sm">
                    No breach records found for this target.
                    {emptyBreaches.length > 0 && (
                      <div className="mt-1 text-gray-700 text-xs">{emptyBreaches.length} source{emptyBreaches.length > 1 ? 's' : ''} searched — no matches.</div>
                    )}
                  </div>
                )}
                {showSelectionMode && hideUnselected && realBreaches.length > 0 && visibleBreaches.length === 0 && (
                  <div className="text-center py-8 text-gray-500 font-mono text-sm">
                    Nothing selected yet — turn off "Hide unselected" to browse and pick breaches.
                  </div>
                )}
                {visibleBreaches.map(({ b: breach, originalIndex }) => (
                  <div key={originalIndex} className={`bg-black/50 border rounded-lg p-4 transition-all ${
                    showSelectionMode && selectedItems.breaches.has(String(originalIndex))
                      ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-900/20'
                      : 'border-red-500/30'
                  } ${showSelectionMode ? 'cursor-pointer hover:bg-red-900/20 hover:border-red-500/50' : ''}`}>
                    {showSelectionMode && (
                      <div className="mb-3 flex items-center space-x-2 p-2 bg-blue-900/10 rounded border border-blue-500/30">
                        <input
                          type="checkbox"
                          checked={selectedItems.breaches.has(String(originalIndex))}
                          onChange={(e) => {
                            e.stopPropagation()
                            setSelectedItems(prev => {
                              const newSet = new Set(prev.breaches)
                              if (e.target.checked) {
                                newSet.add(String(originalIndex))
                              } else {
                                newSet.delete(String(originalIndex))
                              }
                              return { ...prev, breaches: newSet }
                            })
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className={`text-xs font-mono ${
                          selectedItems.breaches.has(String(originalIndex))
                            ? 'text-blue-400 font-bold'
                            : 'text-gray-400'
                        }`}>
                          {selectedItems.breaches.has(String(originalIndex))
                            ? '✓ Selected for Report & Analysis'
                            : 'Select for Report & Analysis'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-red-500 text-xl">🔴</span>
                        <h3 className="text-white font-medium font-mono text-lg">{breach.source}</h3>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-red-600 text-white border border-red-500 font-mono">COMPROMISED</Badge>
                        <span className="text-red-400 text-xs font-mono">[CRITICAL]</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-red-900/20 border border-red-500/30 rounded p-3 min-w-0">
                        <div className="text-red-400 text-xs font-mono mb-1">[BREACH DATE]</div>
                        <div className="text-white font-medium text-sm break-words">
                          {(breach.date || 'Unknown').replace(/<[^>]+>/g, '').trim() || 'Unknown'}
                        </div>
                      </div>
                      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3 min-w-0">
                        <div className="text-yellow-400 text-xs font-mono mb-1">[RECORDS AFFECTED]</div>
                        <div className="text-white font-medium">{breach.records}</div>
                      </div>
                      <div className="bg-orange-900/20 border border-orange-500/30 rounded p-3 min-w-0">
                        <div className="text-orange-400 text-xs font-mono mb-1">[DATA TYPES]</div>
                        <div className="text-white font-medium text-sm break-words">{breach.dataTypes || 'Unknown'}</div>
                      </div>
                    </div>

                    {/* Detailed breach attributes with values - Show ALL fields from allRecords */}
                    <div className="mt-4">
                      <div className="text-red-400 text-xs font-mono mb-3">
                        [EXPOSED ATTRIBUTES & VALUES]
                        {breach.allRecords && breach.allRecords.length > 1 && (
                          <span className="text-gray-400 ml-2">({breach.allRecords.length} records found)</span>
                        )}
                      </div>
                      
                      {/* Collect all unique fields from all records */}
                      {(() => {
                        const allFields = new Map<string, any[]>()
                        
                        // If we have allRecords, collect all unique values for each field
                        if (breach.allRecords && Array.isArray(breach.allRecords) && breach.allRecords.length > 0) {
                          breach.allRecords.forEach((record: any) => {
                            Object.keys(record).forEach(key => {
                              if (record[key] && key !== 'Date' && key !== 'RegDate') {
                                if (!allFields.has(key)) {
                                  allFields.set(key, [])
                                }
                                const value = String(record[key]).trim()
                                if (value && !allFields.get(key)!.includes(value)) {
                                  allFields.get(key)!.push(value)
                                }
                              }
                            })
                          })
                        } else {
                          // Fallback to direct breach fields
                          const breachAny = breach as any
                          Object.keys(breachAny).forEach(key => {
                            if (breachAny[key] && !['source', 'date', 'records', 'dataTypes', 'allRecords', 'breachDescription', 'numOfResults'].includes(key)) {
                              allFields.set(key, [String(breachAny[key])])
                            }
                          })
                        }
                        
                        // Field icons mapping
                        const getFieldIcon = (fieldName: string) => {
                          const lower = fieldName.toLowerCase()
                          if (lower.includes('email')) return '📧'
                          if (lower.includes('phone')) return '📞'
                          if (lower.includes('name') || lower.includes('fullname')) return '👤'
                          if (lower.includes('address') || lower.includes('street')) return '📍'
                          if (lower.includes('city')) return '🏙️'
                          if (lower.includes('birth') || lower.includes('bday') || lower.includes('dob')) return '🎂'
                          if (lower.includes('ip') || lower.includes('address')) return '🌐'
                          if (lower.includes('password')) return '🔒'
                          if (lower.includes('ssn') || lower.includes('tin')) return '🆔'
                          if (lower.includes('passport') || lower.includes('license')) return '📄'
                          if (lower.includes('company') || lower.includes('employer')) return '🏢'
                          if (lower.includes('job') || lower.includes('title')) return '💼'
                          if (lower.includes('website') || lower.includes('url')) return '🌍'
                          if (lower.includes('social') || lower.includes('linkedin') || lower.includes('facebook')) return '🔗'
                          if (lower.includes('gender')) return '⚧️'
                          if (lower.includes('nationality') || lower.includes('country')) return '🌎'
                          if (lower.includes('balance') || lower.includes('payment') || lower.includes('card')) return '💳'
                          return '📋'
                        }
                        
                        const getFieldColorClasses = (fieldName: string): { bg: string; border: string; text: string } => {
                          const lower = fieldName.toLowerCase()
                          if (lower.includes('email')) return { bg: 'bg-blue-900/20', border: 'border-blue-500/30', text: 'text-blue-400' }
                          if (lower.includes('phone')) return { bg: 'bg-purple-900/20', border: 'border-purple-500/30', text: 'text-purple-400' }
                          if (lower.includes('name')) return { bg: 'bg-green-900/20', border: 'border-green-500/30', text: 'text-green-400' }
                          if (lower.includes('address') || lower.includes('street')) return { bg: 'bg-yellow-900/20', border: 'border-yellow-500/30', text: 'text-yellow-400' }
                          if (lower.includes('city')) return { bg: 'bg-orange-900/20', border: 'border-orange-500/30', text: 'text-orange-400' }
                          if (lower.includes('birth') || lower.includes('bday')) return { bg: 'bg-pink-900/20', border: 'border-pink-500/30', text: 'text-pink-400' }
                          if (lower.includes('ip')) return { bg: 'bg-cyan-900/20', border: 'border-cyan-500/30', text: 'text-cyan-400' }
                          if (lower.includes('password')) return { bg: 'bg-red-900/20', border: 'border-red-500/30', text: 'text-red-400' }
                          return { bg: 'bg-gray-900/20', border: 'border-gray-500/30', text: 'text-gray-400' }
                        }
                        
                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {Array.from(allFields.entries()).map(([fieldName, values]) => {
                              const colors = getFieldColorClasses(fieldName)
                              const icon = getFieldIcon(fieldName)
                              const displayValue = values.length === 1 
                                ? values[0] 
                                : values.length <= 3 
                                  ? values.join(', ') 
                                  : `${values.slice(0, 2).join(', ')} (+${values.length - 2} more)`
                              
                              return (
                                <div key={fieldName} className={`${colors.bg} ${colors.border} border rounded p-3`}>
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className={colors.text}>{icon}</span>
                                    <span className={`${colors.text} text-xs font-mono`}>
                                      [{fieldName.toUpperCase().replace(/([A-Z])/g, ' $1').trim()}]
                                    </span>
                                  </div>
                                  <div className="text-white font-medium text-sm break-words">
                                    {displayValue}
                                  </div>
                                  {values.length > 1 && (
                                    <div className="text-gray-400 text-xs mt-1">
                                      {values.length} unique values
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )
                      })()}
                    </div>
                    
                    {/* Show all records if available */}
                    {breach.allRecords && Array.isArray(breach.allRecords) && breach.allRecords.length > 1 && (
                      <div className="mt-4">
                        <div className="text-red-400 text-xs font-mono mb-3">
                          [ALL RECORDS - {breach.allRecords.length} TOTAL]
                        </div>
                        <div className="max-h-96 overflow-y-auto space-y-2">
                          {breach.allRecords.map((record: any, recordIndex: number) => (
                            <div key={recordIndex} className="bg-gray-900/50 border border-gray-700/30 rounded p-3">
                              <div className="text-gray-400 text-xs font-mono mb-2">
                                Record #{recordIndex + 1}
                                {record.Date && ` - ${record.Date}`}
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {Object.entries(record).filter(([key]) => key !== 'Date' && key !== 'RegDate').map(([key, value]) => (
                                  <div key={key} className="text-xs">
                                    <span className="text-gray-500 font-mono">{key}:</span>
                                    <span className="text-white ml-1">{String(value)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
          </SourceIntelPanel>
          )
          })()}

          </>)}

          {/* WEB TAB */}
          {activeTab === 'web' && (() => {
            const indexedWebResults = (profileData.webResults ?? []).map((w: any, index: number) => ({ w, index }))
            const visibleWebResults = (showSelectionMode && hideUnselected)
              ? indexedWebResults.filter(({ index }) => selectedItems.webResults.has(String(index)))
              : indexedWebResults
            return (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-3 rounded-full bg-indigo-500" />
                  <span className="text-indigo-400 font-mono text-xs font-bold tracking-widest">WEB INTELLIGENCE</span>
                  <span className="font-mono text-[10px] text-gray-600">{profileData.webResults?.length ?? 0} results</span>
                </div>
              </div>
              {showSelectionMode && indexedWebResults.length > 0 && (
                <SelectionToolbar
                  total={indexedWebResults.length}
                  selectedCount={indexedWebResults.filter(({ index }) => selectedItems.webResults.has(String(index))).length}
                  onSelectAll={() => setSelectedItems(prev => ({ ...prev, webResults: new Set(indexedWebResults.map(({ index }) => String(index))) }))}
                  onClear={() => setSelectedItems(prev => ({ ...prev, webResults: new Set() }))}
                  hideUnselected={hideUnselected}
                  onToggleHideUnselected={() => setHideUnselected(v => !v)}
                />
              )}
              {!profileData.webResults?.length ? (
                <div className="text-gray-700 font-mono text-xs py-10 text-center border border-dashed border-gray-800 rounded-xl">No web results found for this query</div>
              ) : showSelectionMode && hideUnselected && visibleWebResults.length === 0 ? (
                <div className="text-gray-700 font-mono text-xs py-10 text-center border border-dashed border-gray-800 rounded-xl">Nothing selected yet — turn off "Hide unselected" to browse and pick results.</div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {visibleWebResults.map(({ w: webResult, index }) => {
                    const domain = webResult.url ? (() => { try { return new URL(webResult.url).hostname } catch { return '' } })() : ''
                    return (
                      <div
                        key={webResult.id || index}
                        className={`group bg-[#0a0a14] border border-indigo-900/30 hover:border-indigo-700/50 rounded-xl overflow-hidden transition-all cursor-pointer ${showSelectionMode && selectedItems.webResults.has(String(index)) ? 'ring-1 ring-indigo-500 border-indigo-500/60' : ''}`}
                        onClick={() => {
                          if (showSelectionMode) {
                            setSelectedItems(prev => {
                              const s = new Set(prev.webResults)
                              s.has(String(index)) ? s.delete(String(index)) : s.add(String(index))
                              return { ...prev, webResults: s }
                            })
                          } else {
                            const webNode = graph?.nodes?.find((n: any) => n.id === webResult.id || (n.type === 'web_result' && n.props?.url === webResult.url))
                            if (webNode) setSelectedNode(webNode)
                          }
                        }}
                      >
                        {/* Top bar: favicon + domain + selection */}
                        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-indigo-900/20 bg-indigo-950/10">
                          {domain && (
                            <img
                              src={`https://www.google.com/s2/favicons?sz=16&domain=${domain}`}
                              alt=""
                              className="w-4 h-4 rounded shrink-0"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                            />
                          )}
                          <span className="font-mono text-[10px] text-indigo-400 truncate flex-1">{domain || 'Unknown source'}</span>
                          {showSelectionMode && (
                            <input
                              type="checkbox"
                              checked={selectedItems.webResults.has(String(index))}
                              onChange={(e) => {
                                e.stopPropagation()
                                setSelectedItems(prev => {
                                  const s = new Set(prev.webResults)
                                  if (e.target.checked) s.add(String(index)); else s.delete(String(index))
                                  return { ...prev, webResults: s }
                                })
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-3.5 h-3.5 shrink-0"
                            />
                          )}
                          <span className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded border text-indigo-500 border-indigo-800/40 bg-indigo-950/40 shrink-0">WEB</span>
                        </div>
                        {/* Content */}
                        <div className="px-4 py-3 space-y-2">
                          {webResult.title && (
                            <div className="text-white font-semibold text-sm leading-snug line-clamp-2 group-hover:text-indigo-100 transition-colors">{webResult.title}</div>
                          )}
                          {webResult.url && (
                            <a
                              href={webResult.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-indigo-400 hover:text-indigo-200 font-mono text-[10px] truncate transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {webResult.url}
                            </a>
                          )}
                          {webResult.snippet && (
                            <p className="text-gray-400 text-xs leading-relaxed line-clamp-3">{webResult.snippet}</p>
                          )}
                          {webResult.extracted_snippets?.length > 0 && (
                            <div className="space-y-1 mt-1">
                              <div className="text-indigo-600 font-mono text-[9px] tracking-widest">EXTRACTED CONTENT</div>
                              {webResult.extracted_snippets.slice(0, 2).map((s: string, si: number) => (
                                <div key={si} className="text-gray-500 text-[11px] px-2 py-1.5 bg-indigo-950/20 border border-indigo-900/25 rounded line-clamp-2">{s}</div>
                              ))}
                              {webResult.extracted_snippets.length > 2 && (
                                <div className="text-indigo-700 font-mono text-[9px]">+{webResult.extracted_snippets.length - 2} more snippets</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            )
          })()}

          {/* NETWORK TAB */}
          {activeTab === 'network' && (
            <div className="space-y-6 w-full">
              
              {/* Interactive Network Graph */}
              <div className="mb-6 w-full max-w-full overflow-hidden" data-graph-container>
                <NetworkGraph profileData={profileData} originalGraph={mergedGraph || graph} />
              </div>
              
              {/* Network Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-black/50 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-red-500">🎯</span>
                    <span className="text-red-400 text-sm font-mono">[TARGET NODES]</span>
                  </div>
                  <div className="text-white text-2xl font-bold">1</div>
                  <div className="text-red-300 text-xs">Primary Target</div>
                </div>
                
                <div className="bg-black/50 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-red-500">🗄️</span>
                    <span className="text-red-400 text-sm font-mono">[BREACH NODES]</span>
                  </div>
                  <div className="text-white text-2xl font-bold">{profileData.breachSources.length}</div>
                  <div className="text-red-300 text-xs">Compromised Sources</div>
                </div>
                
                <div className="bg-black/50 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-red-500">📊</span>
                    <span className="text-red-400 text-sm font-mono">[ATTRIBUTE NODES]</span>
                  </div>
                  <div className="text-white text-2xl font-bold">
                    {Object.keys(profileData.personalInfo).length + 
                     Object.keys(profileData.contactDetails).length + 
                     Object.keys(profileData.addresses).length}
                  </div>
                  <div className="text-red-300 text-xs">PII Attributes</div>
                </div>
              </div>
              
              {/* Stealer Logs (Hudson Rock) */}
              {profileData.stealerLogs && Array.isArray(profileData.stealerLogs) && profileData.stealerLogs.length > 0 && (
                <div className="bg-black/50 border border-red-500/30 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <span className="text-red-500">⚠️</span>
                    <h3 className="text-white font-mono">[STEALER LOGS - HUDSON ROCK]</h3>
                    <Badge className="bg-red-600 text-white text-xs font-mono ml-2">
                      {profileData.stealerLogs.length} INFECTION{profileData.stealerLogs.length > 1 ? 'S' : ''}
                    </Badge>
                  </div>

                  {showSelectionMode && (
                    <SelectionToolbar
                      total={profileData.stealerLogs.length}
                      selectedCount={selectedItems.stealerLogs.size}
                      onSelectAll={() => setSelectedItems(prev => ({ ...prev, stealerLogs: new Set((profileData.stealerLogs || []).map((_: any, i: number) => String(i))) }))}
                      onClear={() => setSelectedItems(prev => ({ ...prev, stealerLogs: new Set() }))}
                      hideUnselected={hideUnselected}
                      onToggleHideUnselected={() => setHideUnselected(v => !v)}
                    />
                  )}

                  <div className="space-y-4 max-h-[600px] overflow-y-auto mt-3">
                    {profileData.stealerLogs
                      .map((stealer, index) => ({ stealer, index }))
                      .filter(({ index }) => !(showSelectionMode && hideUnselected) || selectedItems.stealerLogs.has(String(index)))
                      .map(({ stealer, index }) => (
                      <div key={index} className={`bg-red-900/20 border rounded-lg p-4 ${showSelectionMode && selectedItems.stealerLogs.has(String(index)) ? 'ring-2 ring-blue-500 border-blue-500' : 'border-red-500/30'}`}>
                        {showSelectionMode && (
                          <div className="mb-3 flex items-center space-x-2 p-2 bg-blue-900/10 rounded border border-blue-500/30">
                            <input
                              type="checkbox"
                              checked={selectedItems.stealerLogs.has(String(index))}
                              onChange={(e) => {
                                setSelectedItems(prev => {
                                  const newSet = new Set(prev.stealerLogs)
                                  if (e.target.checked) newSet.add(String(index)); else newSet.delete(String(index))
                                  return { ...prev, stealerLogs: newSet }
                                })
                              }}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className={`text-xs font-mono ${selectedItems.stealerLogs.has(String(index)) ? 'text-blue-400 font-bold' : 'text-gray-400'}`}>
                              {selectedItems.stealerLogs.has(String(index)) ? '✓ Selected for Report & Analysis' : 'Select for Report & Analysis'}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-red-400 font-mono text-xs">[STEALER #{index + 1}]</span>
                            <Badge className="bg-red-600 text-white text-xs">{stealer.stealer_family}</Badge>
                          </div>
                          <div className="text-red-300 text-xs font-mono">
                            {stealer.date_compromised ? new Date(stealer.date_compromised).toLocaleDateString() : 'Unknown Date'}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">COMPUTER NAME</div>
                            <div className="text-white">{stealer.computer_name || 'Not Found'}</div>
                          </div>
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">OPERATING SYSTEM</div>
                            <div className="text-white">{stealer.operating_system || 'Unknown'}</div>
                          </div>
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">IP ADDRESS</div>
                            <div className="text-white font-mono">{stealer.ip || 'Not Found'}</div>
                          </div>
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">MALWARE PATH</div>
                            <div className="text-white font-mono text-xs break-all">{stealer.malware_path || 'Not Found'}</div>
                          </div>
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">CORPORATE SERVICES</div>
                            <div className="text-white font-bold">{stealer.total_corporate_services}</div>
                          </div>
                          <div>
                            <div className="text-red-400 text-xs font-mono mb-1">USER SERVICES</div>
                            <div className="text-white font-bold">{stealer.total_user_services}</div>
                          </div>
                        </div>
                        
                        {stealer.antiviruses && stealer.antiviruses.length > 0 && (
                          <div className="mt-3">
                            <div className="text-red-400 text-xs font-mono mb-1">ANTIVIRUS DETECTED</div>
                            <div className="flex flex-wrap gap-2">
                              {stealer.antiviruses.map((av, idx) => (
                                <Badge key={idx} variant="secondary" className="bg-yellow-600/20 text-yellow-300 border-yellow-500/30">
                                  {av}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {stealer.top_passwords && stealer.top_passwords.length > 0 && (
                          <div className="mt-3">
                            <div className="text-red-400 text-xs font-mono mb-1">TOP PASSWORDS (REDACTED)</div>
                            <div className="flex flex-wrap gap-2">
                              {stealer.top_passwords.slice(0, 5).map((pwd, idx) => (
                                <Badge key={idx} variant="secondary" className="bg-red-600/20 text-red-300 border-red-500/30 font-mono">
                                  {pwd}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {stealer.top_logins && stealer.top_logins.length > 0 && (
                          <div className="mt-3">
                            <div className="text-red-400 text-xs font-mono mb-1">TOP LOGINS</div>
                            <div className="flex flex-wrap gap-2">
                              {stealer.top_logins.slice(0, 5).map((login, idx) => (
                                <Badge key={idx} variant="secondary" className="bg-blue-600/20 text-blue-300 border-blue-500/30 font-mono text-xs">
                                  {login}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* LeakIX Domain Intelligence */}
              {profileData.leakixData && (profileData.leakixData.services_count > 0 || profileData.leakixData.leaks_count > 0) && (
                <div className="bg-black/50 border border-purple-500/30 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-purple-500">🔍</span>
                      <h3 className="text-white font-mono">[LEAKIX DOMAIN INTELLIGENCE]</h3>
                      <Badge className="bg-purple-600 text-white text-xs font-mono ml-2">
                        {profileData.leakixData.services_count} SERVICES, {profileData.leakixData.leaks_count} LEAKS
                      </Badge>
                    </div>
                  </div>

                  {showSelectionMode && ((profileData.leakixData?.services?.length ?? 0) + (profileData.leakixData?.leaks?.length ?? 0) > 0) && (
                    <SelectionToolbar
                      total={(profileData.leakixData?.services?.length ?? 0) + (profileData.leakixData?.leaks?.length ?? 0)}
                      selectedCount={selectedItems.leakixServices.size + selectedItems.leakixLeaks.size}
                      onSelectAll={() => {
                        const allServiceIds = (profileData.leakixData?.services || []).map((_: any, i: number) => String(i))
                        const allLeakIds = (profileData.leakixData?.leaks || []).map((_: any, i: number) => String(i))
                        setSelectedItems(prev => ({ ...prev, leakixServices: new Set(allServiceIds), leakixLeaks: new Set(allLeakIds) }))
                      }}
                      onClear={() => setSelectedItems(prev => ({ ...prev, leakixServices: new Set(), leakixLeaks: new Set() }))}
                      hideUnselected={hideUnselected}
                      onToggleHideUnselected={() => setHideUnselected(v => !v)}
                    />
                  )}

                  {/* Services */}
                  {profileData.leakixData.services && profileData.leakixData.services.length > 0 && (
                    <div className="mb-4 mt-4">
                      <h4 className="text-purple-400 text-sm font-mono mb-2">🌐 Services Discovered ({profileData.leakixData.services.length})</h4>
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {profileData.leakixData.services
                          .map((service: any, index: number) => ({ service, index }))
                          .filter(({ index }) => !(showSelectionMode && hideUnselected) || selectedItems.leakixServices.has(String(index)))
                          .map(({ service, index }) => (
                          <div key={index} className={`bg-purple-900/20 border border-purple-500/30 rounded-lg p-3 ${showSelectionMode ? 'cursor-pointer hover:bg-purple-900/40' : ''}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                {showSelectionMode && (
                                  <input
                                    type="checkbox"
                                    checked={selectedItems.leakixServices.has(String(index))}
                                    onChange={(e) => {
                                      setSelectedItems(prev => {
                                        const newSet = new Set(prev.leakixServices)
                                        if (e.target.checked) {
                                          newSet.add(String(index))
                                        } else {
                                          newSet.delete(String(index))
                                        }
                                        return { ...prev, leakixServices: newSet }
                                      })
                                    }}
                                    className="mr-2"
                                  />
                                )}
                                <div className="text-white font-medium">{service.host || 'Unknown'}:{service.port || ''}</div>
                                <div className="text-purple-300 text-xs mt-1">
                                  IP: {service.ip || 'N/A'} | Protocol: {service.protocol || 'N/A'}
                                </div>
                                {service.software && (
                                  <div className="text-purple-400 text-xs mt-1">
                                    Software: {service.software.name || 'Unknown'} {service.software.version ? `v${service.software.version}` : ''}
                                  </div>
                                )}
                                {service.http_title && (
                                  <div className="text-purple-300 text-xs mt-1">
                                    HTTP Title: {service.http_title}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Leaks */}
                  {profileData.leakixData.leaks && profileData.leakixData.leaks.length > 0 && (
                    <div>
                      <h4 className="text-red-400 text-sm font-mono mb-2">⚠️ Leaks Discovered ({profileData.leakixData.leaks.length})</h4>
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {profileData.leakixData.leaks
                          .map((leak: any, index: number) => ({ leak, index }))
                          .filter(({ index }) => !(showSelectionMode && hideUnselected) || selectedItems.leakixLeaks.has(String(index)))
                          .map(({ leak, index }) => (
                          <div key={index} className={`bg-red-900/20 border border-red-500/30 rounded-lg p-3 ${showSelectionMode ? 'cursor-pointer hover:bg-red-900/40' : ''}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                {showSelectionMode && (
                                  <input
                                    type="checkbox"
                                    checked={selectedItems.leakixLeaks.has(String(index))}
                                    onChange={(e) => {
                                      setSelectedItems(prev => {
                                        const newSet = new Set(prev.leakixLeaks)
                                        if (e.target.checked) {
                                          newSet.add(String(index))
                                        } else {
                                          newSet.delete(String(index))
                                        }
                                        return { ...prev, leakixLeaks: newSet }
                                      })
                                    }}
                                    className="mr-2"
                                  />
                                )}
                                <div className="flex items-center space-x-2 mb-1">
                                  <div className="text-white font-medium">{leak.host || 'Unknown'}</div>
                                  {leak.leak_type && (
                                    <Badge className="bg-red-600 text-white text-xs">{leak.leak_type}</Badge>
                                  )}
                                  {leak.leak_severity && (
                                    <Badge className={`${leak.leak_severity === 'high' ? 'bg-red-700' : 'bg-orange-600'} text-white text-xs`}>
                                      {leak.leak_severity.toUpperCase()}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-red-300 text-xs mt-1">
                                  IP: {leak.ip || 'N/A'} | Stage: {leak.leak_stage || 'N/A'}
                                </div>
                                {leak.dataset && (
                                  <div className="text-red-400 text-xs mt-1">
                                    Dataset: {leak.dataset.rows || 0} rows, {leak.dataset.files || 0} files
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ANALYTICS TAB */}
          {activeTab === 'analytics' && (<>
          <SourceIntelPanel
            title="Breach Timeline"
            icon="📅"
            color="amber"
            description="Chronological view of breach events and data exposure dates"
            defaultExpanded={true}
          >
            <BreachTimeline graph={graph} query={query} profileData={profileData} />
          </SourceIntelPanel>

          <SourceIntelPanel
            title="Breach Analytics"
            icon="📊"
            color="blue"
            description="Statistical analysis and breakdown of breach data"
            defaultExpanded={true}
          >
            <BreachAnalytics graph={graph} query={query} profileData={profileData} />
          </SourceIntelPanel>
          </>)}

          {/* AI INTEL TAB */}
          {activeTab === 'ai' && (
          <SourceIntelPanel
            title="AI Intelligence Analysis"
            icon="🤖"
            color="purple"
            description="Detailed AI-powered threat assessment and investigation report"
            defaultExpanded={false}
          >
            <div className="space-y-6 w-full">
              <div className="flex items-center space-x-2 mb-6">
                <span className="text-purple-500">🤖</span>
                <h2 className="text-lg font-semibold text-white font-mono">[AI INTELLIGENCE ANALYSIS]</h2>
              </div>
              
              {/* AI-Powered OSINT Intelligence - Cyber Profiler Style */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="relative bg-gradient-to-br from-black via-gray-900 to-black border border-purple-500/30 rounded-lg p-6 shadow-[0_0_30px_rgba(147,51,234,0.2)] overflow-hidden"
              >
                {/* Animated background grid */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `
                      linear-gradient(rgba(147,51,234,0.1) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(147,51,234,0.1) 1px, transparent 1px)
                    `,
                    backgroundSize: '20px 20px'
                  }} />
                </div>
                
                {/* Scanning effect */}
                <motion.div
                  className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-purple-400 to-transparent"
                  animate={{
                    y: [0, 400, 0],
                    opacity: [0, 1, 0]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center space-x-3"
                    >
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        className="w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full"
                      />
                      <h3 className="text-purple-400 font-mono text-sm tracking-wider">[AI INTELLIGENCE ANALYSIS]</h3>
                      <motion.div
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="text-purple-500 text-xs font-mono"
                      >
                        ACTIVE
                      </motion.div>
                    </motion.div>
                    {aiInsights?.model && (
                      <Badge className={`${aiInsights.model === 'error' || aiInsights.model === 'fallback' ? 'bg-yellow-600' : 'bg-purple-600'} text-white text-xs font-mono ml-2`}>
                        {aiInsights.model === 'error' ? 'FALLBACK' : aiInsights.model.toUpperCase()}
                      </Badge>
                    )}
                    {aiInsights?.error && (
                      <Badge className="bg-red-600 text-white text-xs font-mono ml-2">
                        ERROR
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-col space-y-2 mb-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowSelectionMode(!showSelectionMode)}
                        className={`px-3 py-1 text-xs font-mono rounded border transition-colors ${
                          showSelectionMode 
                            ? 'bg-blue-600 text-white border-blue-500' 
                            : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                        }`}
                      >
                        {showSelectionMode ? '✓ Selection Mode Active' : '🔘 Select Items for Report'}
                      </button>
                      {showSelectionMode && (
                        <button
                          onClick={() => {
                            const totalSelected = 
                              selectedItems.breaches.size +
                              selectedItems.stealerLogs.size +
                              selectedItems.leakixServices.size +
                              selectedItems.leakixLeaks.size +
                              selectedItems.webResults.size
                            
                            if (totalSelected === 0) {
                              alert('Please select at least one item to analyze')
                              return
                            }
                            
                            fetchAIInsights(true)
                          }}
                          className="px-3 py-1 text-xs font-mono rounded bg-green-600 text-white border border-green-500 hover:bg-green-700"
                        >
                          Analyze Selected ({selectedItems.breaches.size + selectedItems.stealerLogs.size + selectedItems.leakixServices.size + selectedItems.leakixLeaks.size + selectedItems.webResults.size})
                        </button>
                      )}
                      <button
                        onClick={() => fetchAIInsights(false)}
                        className="px-3 py-1 text-xs font-mono rounded bg-purple-600 text-white border border-purple-500 hover:bg-purple-700"
                      >
                        Analyze All
                      </button>
                    </div>
                    {showSelectionMode && (
                      <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-3">
                        <div className="text-purple-300 text-xs font-mono mb-1">
                          📊 Selection Summary
                        </div>
                        <div className="text-white text-sm">
                          {(() => {
                            const totalSelected = 
                              selectedItems.breaches.size +
                              selectedItems.stealerLogs.size +
                              selectedItems.leakixServices.size +
                              selectedItems.leakixLeaks.size +
                              selectedItems.webResults.size
                            
                            if (totalSelected === 0) {
                              return <span className="text-yellow-400">No items selected. Select items below to narrow down your report.</span>
                            }
                            
                            return (
                              <div className="space-y-1">
                                <div>✓ <strong>{totalSelected}</strong> item{totalSelected !== 1 ? 's' : ''} selected</div>
                                {selectedItems.breaches.size > 0 && (
                                  <div className="text-xs text-purple-300 ml-4">
                                    • {selectedItems.breaches.size} breach{selectedItems.breaches.size !== 1 ? 'es' : ''}
                                  </div>
                                )}
                                {selectedItems.stealerLogs.size > 0 && (
                                  <div className="text-xs text-purple-300 ml-4">
                                    • {selectedItems.stealerLogs.size} stealer log{selectedItems.stealerLogs.size !== 1 ? 's' : ''}
                                  </div>
                                )}
                                {selectedItems.webResults.size > 0 && (
                                  <div className="text-xs text-purple-300 ml-4">
                                    • {selectedItems.webResults.size} web result{selectedItems.webResults.size !== 1 ? 's' : ''}
                                  </div>
                                )}
                                {selectedItems.leakixServices.size > 0 && (
                                  <div className="text-xs text-purple-300 ml-4">
                                    • {selectedItems.leakixServices.size} LeakIX service{selectedItems.leakixServices.size !== 1 ? 's' : ''}
                                  </div>
                                )}
                                {selectedItems.leakixLeaks.size > 0 && (
                                  <div className="text-xs text-purple-300 ml-4">
                                    • {selectedItems.leakixLeaks.size} LeakIX leak{selectedItems.leakixLeaks.size !== 1 ? 's' : ''}
                                  </div>
                                )}
                                <div className="text-xs text-green-400 mt-2 pt-2 border-t border-purple-500/30">
                                  💡 Selected items will be included in AI Analysis and PDF Report
                                </div>
                              </div>
                            )
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                
                {loadingInsights ? (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center py-12 space-y-4"
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full"
                    />
                    <div className="text-purple-400 font-mono text-sm tracking-wider">ANALYZING INTELLIGENCE...</div>
                    <motion.div
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="text-purple-500/50 font-mono text-xs"
                    >
                      Processing {profileData.breachSources.length} breach sources
                    </motion.div>
                  </motion.div>
                ) : aiInsights ? (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-4"
                  >
                    {/* Risk Level Badge with Animation */}
                    <motion.div 
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="flex items-center space-x-4 mb-6 p-4 bg-black/40 rounded-lg border border-purple-500/20"
                    >
                      <div className="text-purple-400 text-xs font-mono tracking-wider">THREAT ASSESSMENT:</div>
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className={`px-4 py-2 rounded-lg font-mono text-sm font-bold ${
                          aiInsights.risk_level === 'HIGH' ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-[0_0_20px_rgba(239,68,68,0.5)]' :
                          aiInsights.risk_level === 'MEDIUM' ? 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-white shadow-[0_0_20px_rgba(234,179,8,0.5)]' :
                          'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-[0_0_20px_rgba(34,197,94,0.5)]'
                        }`}
                      >
                        {aiInsights.risk_level}
                      </motion.div>
                      <div className="text-purple-300 text-xs font-mono ml-auto flex items-center space-x-2">
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="w-2 h-2 bg-purple-400 rounded-full"
                        />
                        <span>{aiInsights.breach_count} BREACHES DETECTED</span>
                      </div>
                    </motion.div>
                      
                    {/* AI Insights Content with Sycek OSINT Report Style */}
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="bg-gradient-to-br from-black via-gray-900 to-black max-h-[70vh] overflow-y-auto font-mono text-xs relative"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(15,23,42,0.95) 100%)',
                        border: '4px solid rgb(168, 85, 247)',
                        borderRadius: '8px',
                        boxShadow: '0 0 30px rgba(147,51,234,0.5), inset 0 0 20px rgba(147,51,234,0.1)',
                      }}
                    >
                      {/* Decorative Corner Graphics */}
                      <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-purple-500 rounded-tl-lg pointer-events-none" />
                      <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-purple-500 rounded-tr-lg pointer-events-none" />
                      <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-purple-500 rounded-bl-lg pointer-events-none" />
                      <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-purple-500 rounded-br-lg pointer-events-none" />
                      
                      {/* Inner Border */}
                      <div 
                        className="absolute inset-2 pointer-events-none"
                        style={{
                          border: '2px solid rgba(168, 85, 247, 0.3)',
                          borderRadius: '4px',
                        }}
                      />
                      
                      {/* Header Banner with Sycek Branding */}
                      <div className="relative z-10 border-b-4 border-purple-500 bg-gradient-to-r from-purple-900/40 via-purple-800/30 to-purple-900/40 py-4 px-8">
                        <div className="text-center">
                          <div className="text-purple-400 text-4xl font-bold mb-1 tracking-wider font-mono">SYCEK INTELLIGENCE</div>
                          <div className="text-purple-300 text-sm font-mono tracking-widest">OSINT ASSESSMENT REPORT</div>
                          <div className="flex items-center justify-center gap-2 mt-2">
                            <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
                            <div className="text-purple-400 text-xs font-mono">CLASSIFIED</div>
                            <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Report Container with Padding */}
                      <div className="p-8 relative z-10">
                        <div className="markdown-content prose prose-invert max-w-none">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h1: ({node, children, ...props}: any) => {
                                const isHeader = typeof children === 'string' && children.includes('SYCEK INTELLIGENCE');
                                return (
                                  <h1 className={`text-purple-400 text-3xl font-bold ${isHeader ? 'text-center mt-6 mb-2' : 'mt-8 mb-4 border-b-2 border-purple-500/40 pb-2'} tracking-wide`} {...props} />
                                );
                              },
                              h2: ({node, children, ...props}: any) => {
                                const isHeader = typeof children === 'string' && (children.includes('OSINT Assessment') || children.includes('Report'));
                                return (
                                  <h2 className={`text-purple-300 text-lg font-semibold ${isHeader ? 'text-center mb-4' : 'mt-6 mb-3 border-b border-purple-500/30 pb-1'} font-bold`} {...props} />
                                );
                              },
                              h3: ({node, ...props}) => <h3 className="text-purple-300 text-lg font-semibold mt-5 mb-2" {...props} />,
                              h4: ({node, ...props}) => <h4 className="text-purple-300 text-base font-medium mt-4 mb-2" {...props} />,
                              h5: ({node, ...props}) => <h5 className="text-purple-200 text-sm font-medium mt-3 mb-1" {...props} />,
                              h6: ({node, ...props}) => <h6 className="text-purple-200 text-xs font-medium mt-2 mb-1" {...props} />,
                              p: ({node, ...props}) => <p className="text-white mb-3 leading-7 text-sm" {...props} />,
                              strong: ({node, ...props}) => <strong className="text-purple-400 font-bold" {...props} />,
                              em: ({node, ...props}) => <em className="text-purple-300 italic" {...props} />,
                              ul: ({node, ...props}) => <ul className="list-disc list-inside mb-4 space-y-2 text-purple-200 ml-4" {...props} />,
                              ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-4 space-y-2 text-purple-200 ml-4" {...props} />,
                              li: ({node, ...props}) => <li className="text-white leading-6 text-sm" {...props} />,
                              code: ({node, inline, ...props}: any) => 
                                inline ? (
                                  <code className="bg-purple-900/50 text-purple-300 px-1.5 py-0.5 rounded text-xs font-mono border border-purple-500/30" {...props} />
                                ) : (
                                  <code className="block bg-purple-900/50 text-purple-300 p-3 rounded my-3 overflow-x-auto font-mono text-xs border border-purple-500/30" {...props} />
                                ),
                              blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-purple-500 pl-4 my-3 italic text-purple-200 bg-purple-900/20 py-2 rounded-r" {...props} />,
                              a: ({node, ...props}) => <a className="text-purple-400 hover:text-purple-300 underline" target="_blank" rel="noopener noreferrer" {...props} />,
                              hr: ({node, ...props}) => (
                                <hr 
                                  className="my-8" 
                                  style={{ 
                                    borderTop: '3px solid rgb(168, 85, 247)',
                                    borderBottom: '1px solid rgba(168, 85, 247, 0.3)',
                                    height: '4px',
                                    background: 'linear-gradient(to right, transparent, rgba(168, 85, 247, 0.5), transparent)'
                                  }} 
                                  {...props} 
                                />
                              ),
                              table: ({node, ...props}) => (
                                <div className="overflow-x-auto my-6 border-2 border-purple-500 rounded-lg shadow-xl" style={{ borderColor: 'rgb(168, 85, 247)' }}>
                                  <table className="w-full border-collapse" style={{ border: '2px solid rgb(168, 85, 247)' }} {...props} />
                                </div>
                              ),
                              thead: ({node, ...props}) => (
                                <thead 
                                  className="border-b-2" 
                                  style={{ 
                                    background: 'linear-gradient(to right, rgba(88, 28, 135, 0.8), rgba(126, 34, 206, 0.8))',
                                    borderColor: 'rgb(168, 85, 247)'
                                  }} 
                                  {...props} 
                                />
                              ),
                              th: ({node, ...props}) => (
                                <th 
                                  className="px-4 py-3 text-left font-bold text-sm uppercase tracking-wide" 
                                  style={{ 
                                    color: 'rgb(196, 181, 253)',
                                    borderRight: '1px solid rgba(168, 85, 247, 0.5)',
                                    borderBottom: '2px solid rgb(168, 85, 247)'
                                  }} 
                                  {...props} 
                                />
                              ),
                              td: ({node, ...props}) => (
                                <td 
                                  className="px-4 py-2 text-sm transition-colors" 
                                  style={{ 
                                    color: 'white',
                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                    borderRight: '1px solid rgba(168, 85, 247, 0.3)',
                                    borderBottom: '1px solid rgba(168, 85, 247, 0.3)'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(88, 28, 135, 0.3)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
                                  }}
                                  {...props} 
                                />
                              ),
                            }}
                          >
                            {aiInsights.insights}
                          </ReactMarkdown>
                        </div>
                        
                        {/* Footer Banner with Sycek Branding */}
                        <div className="mt-8 pt-6 border-t-4 border-purple-500 bg-gradient-to-r from-purple-900/40 via-purple-800/30 to-purple-900/40 py-4 px-8 -mx-8 -mb-8">
                          <div className="text-center">
                            <div className="text-purple-400 text-lg font-bold mb-2 font-mono">SYCEK INTELLIGENCE</div>
                            <div className="text-purple-300 text-xs font-mono mb-1">Open Source Intelligence Platform</div>
                            <div className="text-purple-500/70 text-xs font-mono italic">OSINT-derived intelligence; requires independent verification</div>
                            <div className="flex items-center justify-center gap-4 mt-3">
                              <div className="h-px flex-1 bg-purple-500/50" />
                              <div className="text-purple-400 text-xs font-mono">CLASSIFIED</div>
                              <div className="h-px flex-1 bg-purple-500/50" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-12"
                  >
                    <div className="text-purple-400 text-sm font-mono mb-2">[SYSTEM STATUS]</div>
                    <div className="text-purple-500/50 text-xs font-mono">
                      AI Intelligence Module Standby
                    </div>
                    <div className="text-purple-600/30 text-xs font-mono mt-1">
                      Click "Analyze All" or select specific items to generate AI analysis
                    </div>
                  </motion.div>
                )}
                </div>
              </motion.div>
            </div>
          </SourceIntelPanel>
          )}

          {/* BTRA INTEL TAB */}
          {activeTab === 'btra' && (
            <BTRAInvestigationPanel query={query} />
          )}

          {/* INVESTIGATE TAB — action-focused: AI brief + pivot targets + platform pivots */}
          {activeTab === 'investigate' && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center gap-3 pb-1">
                <div className="w-1.5 h-8 rounded-full bg-gradient-to-b from-purple-500 to-blue-500" />
                <div>
                  <div className="font-mono text-[10px] text-gray-500 tracking-widest uppercase">Investigation Workspace</div>
                  <div className="font-mono text-sm text-white font-bold">{query}</div>
                </div>
              </div>

              {/* Pivot targets — computed from breach data */}
              {profileData && (() => {
                const phones: Map<string, string[]> = new Map()
                const altEmails: Map<string, string[]> = new Map()
                const profileLinks: Map<string, string[]> = new Map()
                const usernames: Map<string, string[]> = new Map()
                const platformIds: Map<string, string[]> = new Map()
                const ipAddrs: Map<string, string[]> = new Map()

                const queryLower = query.toLowerCase().trim()
                const addToMap = (map: Map<string, string[]>, key: string, source: string) => {
                  if (!map.has(key)) map.set(key, [])
                  const arr = map.get(key)!
                  if (!arr.includes(source)) arr.push(source)
                }
                const SKIP_FIELDS = new Set(['source', 'date', 'records', 'datatypes', 'numofresults'])

                profileData.breachSources.forEach((breach: any) => {
                  const sourceName = breach.source || 'Unknown'
                  const records: any[] = (breach.allRecords && breach.allRecords.length > 0) ? breach.allRecords : [breach]
                  records.forEach((record: any) => {
                    Object.entries(record).forEach(([k, v]) => {
                      if (!v) return
                      const vs = String(v).trim()
                      if (!vs || vs.length < 2) return
                      const kl = k.toLowerCase().replace(/[^a-z]/g, '')
                      if (SKIP_FIELDS.has(kl)) return
                      const vsLower = vs.toLowerCase()
                      if (kl.includes('phone') || kl.includes('mobile') || kl === 'tel') {
                        const digits = vs.replace(/\D/g, '')
                        if (digits.length >= 7 && vsLower !== queryLower) addToMap(phones, vs, sourceName)
                      } else if ((kl.includes('email') || kl.includes('mail')) && vs.includes('@') && vsLower !== queryLower) {
                        addToMap(altEmails, vs, sourceName)
                      } else if (kl.includes('linkedin') || kl === 'profileurl' || (kl.includes('url') && vs.startsWith('http'))) {
                        addToMap(profileLinks, vs, sourceName)
                      } else if (kl.includes('username') || kl.includes('nickname') || kl === 'login' || kl === 'handle') {
                        if (vsLower !== queryLower) addToMap(usernames, vs, sourceName)
                      } else if (kl.includes('instagram') || kl.includes('facebook') || kl.includes('twitter') || kl.includes('vk')) {
                        addToMap(platformIds, `${k}: ${vs}`, sourceName)
                      } else if ((kl === 'ip' || kl === 'ipaddress') && /^\d{1,3}\.\d{1,3}/.test(vs)) {
                        addToMap(ipAddrs, vs, sourceName)
                      }
                    })
                  })
                })

                const totalPivots = phones.size + altEmails.size + profileLinks.size + usernames.size + platformIds.size + ipAddrs.size
                if (totalPivots === 0) return null

                const copyVal = (text: string) => { try { navigator.clipboard?.writeText(text) } catch (_) {} }

                const ACTION_GROUPS = [
                  { label: 'PHONE NUMBERS', sublabel: 'Query: TrueCaller · Pipl · BreachINT', map: phones, color: 'green', action: 'Pivot to phone investigation' },
                  { label: 'ALTERNATE EMAILS', sublabel: 'Query: BreachINT · Gmail OSINT · RocketReach', map: altEmails, color: 'blue', action: 'Run full profile on each email' },
                  { label: 'PROFILE LINKS', sublabel: 'Open · Archive · RocketReach enrich', map: profileLinks, color: 'cyan', action: 'Verify and capture via WebArchive' },
                  { label: 'USERNAMES', sublabel: 'Search: Sherlock · WhatsMyName · BreachINT', map: usernames, color: 'purple', action: 'Cross-platform username trace' },
                  { label: 'PLATFORM IDs', sublabel: 'Platform API · Social Mapper', map: platformIds, color: 'pink', action: 'Stable ID — survives username changes' },
                  { label: 'IP ADDRESSES', sublabel: 'Query: Shodan · Censys · AbuseIPDB', map: ipAddrs, color: 'orange', action: 'Infrastructure attribution' },
                ]

                const COLOR_MAP: Record<string, { text: string; border: string; bg: string; badge: string }> = {
                  green:  { text: 'text-green-300',  border: 'border-green-900/40',  bg: 'bg-green-950/20',  badge: 'bg-green-950/60 text-green-600' },
                  blue:   { text: 'text-blue-300',   border: 'border-blue-900/40',   bg: 'bg-blue-950/20',   badge: 'bg-blue-950/60 text-blue-600' },
                  cyan:   { text: 'text-cyan-300',   border: 'border-cyan-900/40',   bg: 'bg-cyan-950/20',   badge: 'bg-cyan-950/60 text-cyan-600' },
                  purple: { text: 'text-purple-300', border: 'border-purple-900/40', bg: 'bg-purple-950/20', badge: 'bg-purple-950/60 text-purple-600' },
                  pink:   { text: 'text-pink-300',   border: 'border-pink-900/40',   bg: 'bg-pink-950/20',   badge: 'bg-pink-950/60 text-pink-600' },
                  orange: { text: 'text-orange-300', border: 'border-orange-900/40', bg: 'bg-orange-950/20', badge: 'bg-orange-950/60 text-orange-600' },
                }

                return (
                  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-white/[0.05] flex items-center justify-between">
                      <span className="font-mono text-[9px] text-gray-500 tracking-widest uppercase">Pivot Targets — {totalPivots} queryable identifiers</span>
                      <span className="font-mono text-[9px] text-gray-700">click any value to copy</span>
                    </div>
                    <div className="p-4 space-y-4">
                      {ACTION_GROUPS.filter(g => g.map.size > 0).map(({ label, sublabel, map, color, action }) => {
                        const c = COLOR_MAP[color]
                        return (
                          <div key={label}>
                            <div className="flex items-baseline justify-between mb-2">
                              <div>
                                <span className="font-mono text-[10px] text-gray-400 tracking-widest">{label}</span>
                                <span className="font-mono text-[9px] text-gray-700 ml-2">&rarr; {sublabel}</span>
                              </div>
                              <span className={`font-mono text-[9px] ${c.text} opacity-60`}>{action}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {Array.from(map.entries()).map(([v, srcs]) => {
                                const isLink = v.startsWith('http') || v.includes('linkedin.com')
                                const parts = v.includes(': ') ? v.split(': ') : null
                                const displayVal = parts ? parts.slice(1).join(': ') : v
                                return (
                                  <div key={v} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${c.bg} border ${c.border}`}>
                                    {parts && <span className={`font-mono text-[9px] ${c.text} opacity-50`}>{parts[0]}:</span>}
                                    {isLink ? (
                                      <a href={v.startsWith('http') ? v : `https://${v}`} target="_blank" rel="noopener noreferrer"
                                        className={`font-mono text-xs ${c.text} hover:underline truncate max-w-[200px]`}>{displayVal}</a>
                                    ) : (
                                      <span className={`font-mono text-xs ${c.text}`}>{displayVal}</span>
                                    )}
                                    {srcs.length > 1 && (
                                      <span className={`font-mono text-[9px] px-1 rounded ${c.badge}`}>{srcs.length} sources</span>
                                    )}
                                    <button
                                      onClick={() => copyVal(displayVal)}
                                      className="font-mono text-[9px] text-gray-700 hover:text-gray-400 transition-colors px-1 py-0.5 rounded hover:bg-white/[0.05]"
                                    >
                                      copy
                                    </button>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })()}

              {/* Breach Pivot Enrichment */}
              <BreachPivotEnrichment
                query={query}
                phones={enrichTargets.phones}
                emails={enrichTargets.emails}
                usernames={enrichTargets.usernames}
                result={enrichResult}
                loading={enrichLoading}
                error={enrichError}
                onRun={handleRunEnrichment}
              />

              {/* Platform cross-navigation */}
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-white/[0.05]">
                  <span className="font-mono text-[9px] text-gray-500 tracking-widest uppercase">Cross-Module Investigation Pivots</span>
                </div>
                <div className="p-4 grid grid-cols-2 gap-2">
                  {[
                    { label: 'SOCINT Graph', desc: 'Social graph, email/phone/username cross-link', href: `/socint?q=${encodeURIComponent(query)}`, color: 'text-blue-400', border: 'border-blue-900/30', bg: 'bg-blue-950/10' },
                    { label: 'GEOINT Satellite', desc: 'Location attribution, AOI from addresses found', href: '/geoint', color: 'text-green-400', border: 'border-green-900/30', bg: 'bg-green-950/10' },
                    { label: 'Facial Recognition', desc: 'Search photos extracted from breach records', href: '/facial-recognition', color: 'text-pink-400', border: 'border-pink-900/30', bg: 'bg-pink-950/10' },
                    { label: 'Cyber Threat Intel', desc: 'Check IPs/domains against threat feeds', href: '/cyber-threats', color: 'text-orange-400', border: 'border-orange-900/30', bg: 'bg-orange-950/10' },
                    { label: 'Air Traffic', desc: 'Cross-reference against flight records', href: '/air-traffic', color: 'text-cyan-400', border: 'border-cyan-900/30', bg: 'bg-cyan-950/10' },
                    { label: 'Monitor Dashboard', desc: 'Watch for new events linked to this target', href: '/monitor', color: 'text-purple-400', border: 'border-purple-900/30', bg: 'bg-purple-950/10' },
                  ].map(({ label, desc, href, color, border, bg }) => (
                    <a key={label} href={href}
                      className={`flex flex-col gap-1 p-3 rounded-lg border ${border} ${bg} hover:brightness-110 transition-all`}
                    >
                      <span className={`font-mono text-[10px] font-bold ${color}`}>{label}</span>
                      <span className="font-mono text-[9px] text-gray-600 leading-tight">{desc}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* AI Full Investigation Brief — shown in full here */}
              {aiInsights && (
                <div>
                  <div className="font-mono text-[9px] text-gray-600 tracking-widest mb-3 uppercase">Full AI Investigation Brief</div>
                  <div className="bg-white/[0.02] border border-purple-900/30 rounded-xl px-5 py-4 prose prose-invert prose-sm max-w-none
                    [&_h1]:text-purple-400 [&_h1]:font-mono [&_h1]:text-[11px] [&_h1]:tracking-widest [&_h1]:uppercase [&_h1]:border-b [&_h1]:border-purple-900/40 [&_h1]:pb-1.5 [&_h1]:mb-3 [&_h1]:mt-5 [&_h1:first-child]:mt-0
                    [&_h2]:text-purple-400 [&_h2]:font-mono [&_h2]:text-[11px] [&_h2]:tracking-widest [&_h2]:uppercase [&_h2]:border-b [&_h2]:border-purple-900/40 [&_h2]:pb-1.5 [&_h2]:mb-3 [&_h2]:mt-5 [&_h2:first-child]:mt-0
                    [&_h3]:text-purple-300 [&_h3]:font-mono [&_h3]:text-[10px] [&_h3]:tracking-widest [&_h3]:uppercase [&_h3]:mt-4 [&_h3]:mb-2
                    [&_p]:text-gray-300 [&_p]:leading-relaxed [&_p]:text-sm [&_p]:mb-3
                    [&_strong]:text-white [&_strong]:font-semibold
                    [&_code]:text-green-400 [&_code]:bg-green-950/40 [&_code]:border [&_code]:border-green-900/40 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono
                    [&_ul]:space-y-1.5 [&_ul]:my-2
                    [&_li]:text-gray-300 [&_li]:text-sm [&_li]:leading-relaxed
                    [&_li_strong]:text-white [&_li_code]:text-green-400 [&_li_code]:bg-green-950/40 [&_li_code]:border [&_li_code]:border-green-900/40 [&_li_code]:px-1 [&_li_code]:rounded [&_li_code]:text-xs [&_li_code]:font-mono
                    [&_a]:text-blue-400 hover:[&_a]:text-blue-300
                  ">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {DOMPurify.sanitize(aiInsights?.insights || '')}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
              {!aiInsights && !loadingInsights && (
                <div className="bg-white/[0.02] border border-purple-900/20 rounded-xl p-6 text-center">
                  <div className="font-mono text-[9px] text-gray-600 tracking-widest mb-2">AI BRIEF NOT YET GENERATED</div>
                  <div className="font-mono text-xs text-gray-500 mb-3">Generate the AI investigation brief to see cross-source analysis, pivot points, and investigative next actions</div>
                  <button
                    onClick={() => fetchAIInsights(false)}
                    disabled={loadingInsights}
                    className="px-4 py-2 font-mono text-xs text-purple-300 border border-purple-500/30 rounded-lg bg-purple-950/20 hover:bg-purple-950/40 transition-colors"
                  >
                    Generate AI Brief
                  </button>
                </div>
              )}
            </div>
          )}

            </div>{/* end tab content inner */}
          </div>{/* end tab content scroll */}
        </main>{/* end center column */}

        {/* Right Sidebar */}
        <aside
          className={`flex-none h-full border-l border-purple-900/25 bg-[#07070f] flex flex-col transition-all duration-200 ${rightSidebarOpen ? 'w-[240px] lg:w-[300px] xl:w-[340px] overflow-y-auto overflow-x-hidden gap-3 p-3 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-purple-900/60 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-purple-700/80' : 'w-8 overflow-hidden items-center pt-3'}`}
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#7c3aed transparent' }}
        >
          {/* Collapse toggle */}
          <button
            onClick={() => setRightSidebarOpen(v => !v)}
            className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-purple-400 hover:text-white hover:bg-purple-900/40 transition-colors"
            title={rightSidebarOpen ? 'Collapse panel' : 'Expand panel'}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              {rightSidebarOpen
                ? <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                : <path d="M8 2L4 6L8 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              }
            </svg>
          </button>
          {rightSidebarOpen && <>
          {profileData && (
            <BTRAPanel
              piiData={profileData}
              query={query}
            />
          )}
          <AIEnrichmentPanel
            profileData={profileData}
            query={query}
          />
          <EnrichmentActions
            query={query}
            profileData={profileData}
            callerIdData={callerIdData}
            onRerunSearch={onRerunSearch}
          />
          <CorrelationEnginePanel
            query={query}
            onRerunSearch={onRerunSearch}
          />
          {/* Bottom spacer so last panel isn't flush against the edge */}
          <div className="shrink-0 h-2" />
          </>}
        </aside>
      </div>{/* end 3-column body */}

      {/* Node Detail Modal */}
      <NodeDetailModal 
        node={selectedNode} 
        onClose={() => setSelectedNode(null)} 
      />
      
      {/* Recent Searches Modal */}
      <AnimatePresence>
        {showCombineDialog && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCombineDialog(false)}
          >
            <motion.div
              className="bg-black border border-purple-500/30 rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-purple-500 text-2xl">🔗</span>
                  <div>
                    <h3 className="text-white font-mono text-lg">[COMBINE SEARCHES]</h3>
                    <div className="text-purple-400 text-sm font-mono">Merge & Correlate Intelligence</div>
                  </div>
                </div>
                <button
                  onClick={() => setShowCombineDialog(false)}
                  className="text-purple-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4 mb-4">
                <p className="text-gray-300 text-sm font-mono">
                  Select previous searches to combine with current search. Combined data will be analyzed for correlations.
                </p>
                
                <div className="space-y-3">
                  {getRecentSearches().map((search: any, index: number) => (
                    <motion.div
                      key={search.id}
                      className={`bg-black/50 border rounded-lg p-4 cursor-pointer transition-colors ${
                        combinedSearches.some(s => s.id === search.id)
                          ? 'border-purple-500 bg-purple-900/20'
                          : 'border-gray-700 hover:border-purple-500/50'
                      }`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => {
                        if (combinedSearches.some(s => s.id === search.id)) {
                          setCombinedSearches(prev => prev.filter(s => s.id !== search.id))
                        } else {
                          setCombinedSearches(prev => [...prev, search])
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={combinedSearches.some(s => s.id === search.id)}
                            onChange={() => {}}
                            className="w-4 h-4"
                          />
                          <div>
                            <div className="text-white font-mono">{search.query}</div>
                            <div className="text-purple-400 text-xs font-mono">
                              {new Date(search.timestamp).toLocaleString()} • {search.resultsCount} results
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white text-lg font-bold">{search.resultsCount}</div>
                          <div className="text-purple-400 text-xs font-mono">RESULTS</div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setCombinedSearches([])
                    setShowCombineDialog(false)
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-mono text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCombineSearches}
                  disabled={combinedSearches.length === 0}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-mono text-sm"
                >
                  Combine ({combinedSearches.length})
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {showRecentSearches && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowRecentSearches(false)}
          >
            <motion.div
              className="bg-black border border-red-500/30 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-red-500 text-2xl">🕒</span>
                  <div>
                    <h3 className="text-white font-mono text-lg">[RECENT SEARCHES]</h3>
                    <div className="text-red-400 text-sm font-mono">Intelligence History</div>
                  </div>
                </div>
                <button
                  onClick={() => setShowRecentSearches(false)}
                  className="text-red-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-3">
                {getRecentSearches().map((search: any, index: number) => (
                  <motion.div
                    key={search.id}
                    className="bg-black/50 border border-red-500/30 rounded-lg p-4 cursor-pointer hover:bg-red-900/20 transition-colors"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => {
                      loadPreviousSearch(search.query)
                      setShowRecentSearches(false)
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-red-500">🔍</span>
                        <div>
                          <div className="text-white font-mono">{search.query}</div>
                          <div className="text-red-400 text-xs font-mono">
                            {new Date(search.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-white text-lg font-bold">{search.resultsCount}</div>
                        <div className="text-red-400 text-xs font-mono">RESULTS</div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {getRecentSearches().length === 0 && (
                  <div className="text-center py-8">
                    <div className="text-red-500 text-4xl mb-4">🔍</div>
                    <div className="text-red-400 font-mono">[NO RECENT SEARCHES]</div>
                    <div className="text-red-300 text-sm font-mono mt-2">
                      Your search history will appear here
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
